import store from "@/store";
import { ActionCreatorWithOptionalPayload } from "@reduxjs/toolkit";
import {
    ColDef,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    IServerSideDatasource,
    IsRowSelectable,
    RowNode,
    RowDragEndEvent,
    RowClassParams,
} from "ag-grid-community";
import moment from "moment";
import React, { Dispatch, ReactElement, Ref, SetStateAction } from "react";
import {
    agDefaultSidebar,
    buyboxWinnersAndLosersAvailabilityColDefs,
    catOverviewCompareTablesMetricsToSum,
    CUSTOM_DATE_PICKER_INPUT_FIELDS,
    DATE_PICKER_PRESET_PERIODS,
    FREQUENCY_SLICER_OPTIONS,
    oosProductsColDefs,
    PRICING_CHART_FILTERS_INTIAL_STATE,
    topProductsWithEstimatedLossColDefs,
    topProductsWithLossEcommColDefs,
    topSubcatEstimatedLossEcommColDefs,
} from "./constants";
import { Dayjs } from "dayjs";
import { TDate, TFunnelResponse } from "./funnel";
import { FormInstance, MenuProps } from "antd";
import { MenuItemType } from "antd/es/menu/interface";
import { IAdminSettingsState } from "@/features/adminSettingsSlice";

export type LayoutProps = {
    children: React.ReactNode;
    isHeaderRequired?: boolean;
    isFooterRequired?: boolean;
};

export interface IValueNumber {
    value: number;
}

export interface IValueBoolean {
    value: boolean;
}

export interface IValueString {
    value: string;
}
export interface IValue {
    value: Record<string, any>[];
}

export type TCommonRowObj = Record<
    string,
    string | boolean | number | null | string[] | TClientTags[]
>;

export type TCommonRowData = Array<TCommonRowObj>;

export interface ICommonRowDataRedux {
    value: TCommonRowData;
}

export type TAgGridRowData =
    | TCommonRowData
    | Rule[]
    | TKeywordsActionObj[]
    | TBulkKeywords[]
    | Alert[]
    | RevinitiRecommendation[]
    | ITagTypeAggregate[]
    | IDrainersRowData[]
    | TBuyboxWinnerAndLosersData[]
    | TopSubcategoriesWithEstimatedSalesLossEcommRow[];

export type TNumberObj = Record<string, number>;

export type TStringObj = {
    [key: string]: string | number | null | TCommonRowObj;
};

export type TObjOfNumberObj = Record<string, TNumberObj>;

export type TBidLimit = Record<string, TObjOfNumberObj>;

export type BidLimit = {
    1: Record<string, TBidLimit>;
    2: TNumberObj;
};

export interface IActiveTab {
    activeTab: {
        value: string;
    };
}

export interface IRowNodeValues {
    value: Array<RowNode>;
}

export interface ICheckedNode {
    poCheckedNode?: IRowNodeValues;
    pdCheckedNode?: IRowNodeValues;
    caCheckedNode?: IRowNodeValues;
    adCheckedNode?: IRowNodeValues;
    productMappingSelectionData?: IRowNodeValues;
}

export interface IGridData {
    portfoliosData?: ICommonRowDataRedux;
    productsData?: ICommonRowDataRedux;
    keywordsData?: ICommonRowDataRedux;
    placementsData?: ICommonRowDataRedux;
    campaignsData?: ICommonRowDataRedux;
    adgroupsData?: ICommonRowDataRedux;
}

export interface IRecommendationsData {
    budgetRecommendationData?: ICommonRowDataRedux;
    placementRecommendationData?: ICommonRowDataRedux;
    manualRecommendationData?: ICommonRowDataRedux;
    autoRecommendationData?: ICommonRowDataRedux;
    revinitiRecommendationData?: ICommonRowDataRedux;
}

export interface INegativeKeywordsData {
    suggestedNegKwsData?: ICommonRowDataRedux;
    existingNegKwsData?: ICommonRowDataRedux;
}

export type TNegativeKeywordsGridApi = {
    value: GridApi;
};

export interface INegativeKeywordsGridApi {
    suggestedNegKwsGridApi?: TNegativeKeywordsGridApi;
    existingNegKwsGridApi?: TNegativeKeywordsGridApi;
}

interface IConfigsData {
    country?: string;
    channel?: number;
}

export interface IConfigs {
    type?: string;
    headers?: object | any;
    method?: string;
    API_DOMAIN?: string;
    token?: string;
    client?: number;
    clientGroupId?: string;
    requestType?: string;
    data?: IConfigsData | object[] | string | any;
    body?: IConfigsData | object[] | string | any;
    isExcelUpload?: boolean;
    module?: string;
    channel?: number;
    country?: string;
    timezone?: string;
    clientSubGroupId?: number;
    revstackChannel?: number;
    cube_role?: string;
}

export interface IParams {
    selectedData: TCommonRowData;
    isDayParting: boolean;
    activeModule: string;
}

export interface IToast {
    toastData: {
        type: string;
        text: string;
        show: boolean;
    };
}

export interface ITitle {
    title: { value: string };
}

export interface IToggle {
    toggle: { value: string };
}

export interface IPortfolio {
    portfolio_id?: string;
    portfolio_name?: string;
    portfoliosData?: {
        value: TCommonRowData;
    };
}

export type IDaysArr = Array<{ start: string; end: string; dayOfWeek: number }>;

export type TDaypartingData = {
    rules: {
        action: string;
        actionValue: string;
        campaigns: Array<string>;
        slots: object[];
    };
    timezone: string;
    status?: boolean;
};

export interface IAdminToggleBtn {
    enableDayparting?: {
        value: boolean;
    };
    enableBuybox?: {
        value: boolean;
    };
    enableRevstack?: {
        value: boolean;
    };
    enableKeywordRecommendation: {
        value: boolean;
    };
    enableNavigator?: {
        value: boolean;
    };
    enableCategoryAnalysis?: {
        value: boolean;
    };
    enableProductAnalysis?: {
        value: boolean;
    };
}

export interface ICampaignCreationDisplay {
    campaignCreationDisplay: {
        value: boolean;
    };
}

export interface IClientTagsList {
    clientTagsList: {
        value: TClientTags[];
    };
}

export type TTimezone = {
    abbrev: string;
    utc_offset: {
        hours: number;
    };
    is_dst: boolean;
    label: string;
    value: string;
};

export interface IBulkActionDropDown {
    text: string;
    operation?: string;
    module?: string;
    fn: () => void;
    icon: ReactElement;
    type: string;
    checkboxSelection?: boolean;
    status: boolean;
    element?: ReactElement;
}

export type TGridApi = {
    globalGridApi: {
        value: GridApi;
    };
};

export type TApi = { api: GridApi };

export type TFilterObj = {
    filter?: string;
    filterType?: string;
    type?: string;
    values?: Array<string>;
};

export type TFilterModel = Record<string, TFilterObj>;

export type TFilterData = Array<
    Record<string, string | number | Array<string | number>>
>;
export interface IFilters {
    filterKey: string;
    filterData: TFilterData;
}

export interface IFiltersModule {
    module: string;
    campaignFilters: IFilters;
    keywordFilters: IFilters;
    adgroupFilters: IFilters;
    productFilters: IFilters;
}

export type TGridFiltersValue = {
    filterKey: string;
    filterData: Array<TFilterData>;
};

export type TSelectionValue = {
    module?: string;
    campaignFilters?: TGridFiltersValue;
    keywordFilters?: TGridFiltersValue;
    adgroupFilters?: TGridFiltersValue;
    productFilters?: TGridFiltersValue;
    placementsFilters?: TGridFiltersValue;
};

export type TRequestedParams = {
    dateObj?: {
        start?: string;
        end?: string;
        selectedDate?: string;
    };
    channel?: number;
    country?: string;
    keywords?: Array<string>;
    asin?: Array<string> | string;
    product?: Array<string> | string;
    client?: number;
    campaigns?: Array<number>;
    searchTerm?: string;
    offset?: number;
    pageSize?: number;
    sortModel?: Array<Record<any, any>>;
    filterModel?: Record<any, any>;
    xType?: string;
};

export type SelectOptions = {
    value: string;
    label: string;
};

export type TSelectedTargets = {
    tgt_title?: string;
    prod_target: string;
    tgt_class: string;
    tgt_type: string;
    program_type: string;
    match_type?: string | null;
    bsr_rank: number;
    suggest_bid: number;
};

export type TCampaignTargetObject = {
    productAds: Array<{ asin: string; state: string }>;
    campaignName: string;
    campaignType: Array<string>;
    matchType?: Array<string>;
    portfolioId?: string;
    targetingType: Array<string>;
    targetingClass: Array<string>;
    brandName?: string;
    landingPage?: Array<string>;
};

export type TargetArgs = {
    campaignSelected: Array<SelectOptions>;
    portfolioSelected: SelectOptions;
    targetSelected: Array<SelectOptions>;
    targetClassSelected: Array<SelectOptions>;
    matchTypeSelected: Array<SelectOptions>;
    setTargetsRowData: React.Dispatch<
        React.SetStateAction<Array<TSelectedTargets>>
    >;
    campaignTargetsGridApi?: GridApi;
};

export type TUpdateRow = {
    id: string;
    data: { status: string };
    grid: Record<string, GridApi>;
    forceColumns?: Array<string>;
    force?: boolean;
};

export type TOptions = {
    value: number;
    label: number;
};

export type TOptionsString = {
    value: string;
    label: string;
};

export type TOptionsStringWithColors = TOptionsString & {
    color: string;
};

export type TProductsSelectionData = {
    productsSelectionData: ICommonRowDataRedux;
};

export type TNegativeKeywordsSelectionData = {
    negativeKeywordsSelectionData: ICommonRowDataRedux;
};

export enum IDays {
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
}

export interface IGlobalGridApi {
    globalGridApi?: {
        value: GridApi;
    };
}

export type TIsDaypartingEdit = {
    isDaypartingEdit: IValueBoolean;
};

export type TDayPartingDisplay = {
    dayPartingDisplay: IValueBoolean;
};

export type TDaypartingDataObject = {
    daypartingDataObject: {
        value: Record<string, Array<number>>;
    };
};

export type TErrorFallback = { error: Error; resetErrorBoundary: () => void };

export interface IClient {
    logo_url?: string | null;
    timezone: string;
    id: number;
    client_group_id: number;
    country: string;
    currency: string;
    name: string;
    buybox: boolean;
    dayparting: boolean;
    revstack: boolean;
    country_non_iso: string;
    country_iso: string;
    // country_name: string;
    currency_symbol: string;
    client_subgroup_id: number;
}

export type State = "enabled" | "paused" | "archived";

export interface PerformanceMetrics {
    impression?: number;
    click?: number;
    sale?: number;
    spend?: number;
    orders?: number;
    previous_impression?: number;
    previous_click?: number;
    previous_sale?: number;
    previous_spend?: number;
    previous_orders?: number;
    impression_change?: number;
    click_change?: number;
    sale_change?: number;
    spend_change?: number;
    orders_change?: number;
    cr?: number;
    cpc?: number;
    cpa?: number;
    ctr?: number;
    acos?: number;
    roas?: number;
    previous_cr?: number;
    previous_cpc?: number;
    previous_cpa?: number;
    previous_ctr?: number;
    previous_acos?: number;
    previous_roas?: number;
    cr_change?: number;
    cpc_change?: number;
    cpa_change?: number;
    ctr_change?: number;
    acos_change?: number;
    roas_change?: number;
}

export enum ProgramType {
    "SP" = "sp",
    "SB" = "sb",
    "HSA" = "hsa",
    "SD" = "sd",
    "PLA" = "PLA",
    "PCA" = "PCA",
    "Performance" = "p",
    "Reach" = "r",
    "Awareness" = "a",
    "Keyword Item" = "ki",
    "Item Boost" = "ib",
    "SD-VCPM" = "sd-vcpm",
}

export enum PlacementTypeNames {
    "TOSLP" = "Top of Search Listings Page",
    "ROSL" = "Rest of Search Listings Page",
}

export enum CostType {
    VCPM = "vcpm",
}

export enum DeepDiveGridType {
    "Keyword" = "keyword",
    "AdGroup" = "adgroup",
    "Campaign" = "campaign",
}

export enum ProductModalType {
    PRODUCTS_MODAL = "products-modal",
    ADGROUPS_MODAL = "adgroups-modal",
}

export enum KeywordModalType {
    CAMPAIGNS_MODAL = "campaigns-modal",
}

export type PlacementType = "placement_top" | "placement_product_page";

export interface Campaign extends PerformanceMetrics, Record<string, any> {
    client_id: number;
    campaign_id: string;
    profile_id: string;
    program_type: ProgramType;
    campaign_name: string;
    sys_campaign_id: number;
    state: State;
    budget: number;
    previous_budget?: number;
    type: PlacementType;
    placement: string;
    placement_value: number;
    placement_top_perc: number;
    placement_product_page_perc: number;
    record_type: "campaign" | "productad";
    report_date: string;
    portfolio_name?: string;
    portfolio_id?: number;
    premium_placements_bid?: number;
    product_page_bid?: number;
    budget_utilization?: number;
    campaign_uptime?: number;
}

export interface Portfolio extends PerformanceMetrics, Record<string, any> {
    sys_campaign_id: number;
    portfolio_name: string;
    portfolio_state: State;
    program_type: ProgramType;
    report_date: string;
}

export type TTrendSeries = {
    name: string;
    type: string;
    data: Array<number>;
    yAxis: number;
    dataLabels: {
        enabled: boolean;
        inside: boolean;
        align: string;
        format: string;
    };
};

export interface ITrendsShow {
    trendsModalDisp: IValueBoolean;
}

export interface ITrendsData {
    trendsDataOne?: {
        value: TTrendSeries;
    };
    trendsDataTwo?: {
        value: TTrendSeries;
    };
}
export interface Alert {
    date: Date;
    sku: string;
    title: string;
    type: string;
    from: string;
    to: string;
}

export interface AdGroup extends PerformanceMetrics, Record<string, any> {
    client_id: number;
    campaign_id: string;
    profile_id: string;
    program_type: ProgramType;
    sys_campaign_id: number;
    ad_group_id: string;
    ad_group_name?: string;
    sys_adgroup_id?: number;
    state: State;
    record_type: "adgroup" | "productad";
    asin_id?: string;
}

export interface Keyword extends PerformanceMetrics, Record<string, any> {
    keyword_name: string;
    client_id: number;
    campaign_id: string;
    profile_id: string;
    program_type: ProgramType;
    sys_campaign_id?: number;
    sys_adgroup_id?: number;
    ad_group_id: string;
    // keyword_id?: string;
    sys_ams_keyword_id?: number;
    state: State;
    bid: number;
    creation_date?: number | string;
    record_type: "keyword" | "target" | "productad";
    asin_id?: string;
    match_type: "exact" | "broad" | "phrase";
    min_bid_value?: number;
    max_bid_value?: number;
}

export interface ProductAd {
    client_id: number;
    campaign_id: string;
    profile_id: string;
    program_type: ProgramType;
    sys_campaign_id: number;
    sys_adgroup_id: number;
    ad_group_id: string;
    keyword_id: string;
    sys_ams_keyword_id: number;
    ad_id: string;
    state: State;
    sku_title?: string;
}

export interface IFilters {
    filter: string;
    filterType: string;
    type: string;
    [key: string]: any;
}

export interface ICombinedFilters {
    [key: string]: IFilters;
}

export interface Rule {
    rule_id: string;
    client_id: number;
    type: "query" | "rule";
    name: string;
    description: string;
    filter: Record<string, IFilters>;
    action_type: string;
    action_value: string;
    frequency_units: string;
    frequency_value: number;
    exclude_yesterday: boolean;
    threshold_min?: number;
    threshold_max?: number;
    status: "active" | "inactive" | "archive";
    created_at: string;
    created_by: number | string;
    updated_at: string;
    updated_by: number | string;
    module: string;
    cron_string: string;
}

export interface IRulesData {
    client?: number;
    action?: {
        type: string;
        value: number;
    };
    frequency?: {
        value: number;
        unit: string;
    };
    cronString?: string;
    filters: Record<string, IFilters>;
    module: string;
    name?: string;
    threshold?: {
        min?: string | number;
        max?: string | number;
    };
    excludeYesterday?: boolean;
    type?: string;
}

export interface RevinitiRecommendation {
    search_term?: string;
    bid?: string;
    ad_group?: string;
    campaign?: string;
    targeting_class?: string;
    targeting_type?: string;
    campaign_name?: string;
    adgroup_name?: string;
}

export enum IDRange {
    "Today",
    "Yesterday",
    "Last 3 Days",
    "Last 7 Days",
    "Last Week",
    "Last 14 Days",
    "Last 30 Days",
    "Current Month",
    "Last Month",
    "Custom Range",
}

export interface ITimezoneArg {
    abbrev: string;
    utc_offset: {
        hours: number;
    };
    is_dst: true;
    label: string;
    value: string;
}

export interface IDprRange {
    end: string;
    start: string;
}

export type TDprSlots = Record<string, Record<string, any>>;

export type TAllSlots = {
    slots: Array<IDprRange>;
    state: boolean;
    isDprApplied: boolean;
};

export type TAllSlotsOnSubmit = {
    dayOfWeek: number;
    executionTime: Array<IDprRange>;
    status: boolean;
};

export type IDprCheckedData = Record<string, any> & {
    monday: object;
    tuesday: object;
    wednesday: object;
    thursday: object;
    friday: object;
    saturday: object;
    sunday: object;
};

export type TDprRule = {
    id: string;
    client_id: number;
    channel: number;
    campaign_id: string;
    slots: Array<IDprRange>;
    day_of_week: number;
    action: string;
    timezone: string;
    status: boolean;
    created_at: string;
    updated_at: string;
    created_by: string;
    updated_by: string;
    action_value: null;
    is_dpr_applied: boolean;
};

export interface IDateLogs {
    startDate: moment.Moment;
    endDate: moment.Moment;
}

export interface IStrapiResponse {
    id: number;
    attributes: {
        name: string;
        client: number;
        type: string;
        createdAt: string;
        updatedAt: string;
        asset: {
            data: {
                id: number;
                attributes: {
                    name: string;
                    alternativeText: null | string;
                    caption: null | string;
                    width: null | string;
                    height: null | string;
                    formats: null | string;
                    hash: string;
                    ext: string;
                    mime: string;
                    size: number;
                    url: string;
                    previewUrl: null | string;
                    provider: string;
                    provider_metadata: null | string;
                    createdAt: string;
                    updatedAt: string;
                };
            };
        };
    };
}

export interface IStrapiAssetResponse {
    data: Array<IStrapiResponse>;
}

export interface IFixedReviewOptions {
    categories: IValueString[];
    subCategories: IValueString[];
    skus?: IValueString[];
    brands?: IValueString[];
    features?: MenuItemType[];
}

export interface IMainContext {
    dates: number;
    setDates: Dispatch<SetStateAction<number>>;
    rulesApi: GridApi;
    setRulesApi: Dispatch<SetStateAction<GridApi>>;
    alertsData: Array<Alert>;
    setAlertsData: Dispatch<SetStateAction<Array<Alert>>>;
    activeRulesData: Array<Rule> | TCommonRowData;
    setActiveRulesData: Dispatch<SetStateAction<Array<Rule> | TCommonRowData>>;
    portfoliosGridApi: GridApi;
    setPortfoliosGridApi: Dispatch<SetStateAction<GridApi>>;
    campaignsGridApi: GridApi;
    setCampaignsGridApi: Dispatch<SetStateAction<GridApi>>;
    keywordsGridApi: GridApi;
    setKeywordsGridApi: Dispatch<SetStateAction<GridApi>>;
    productsGridApi: GridApi;
    setProductsGridApi: Dispatch<SetStateAction<GridApi>>;
    placementsGridApi: GridApi;
    setPlacementsGridApi: Dispatch<SetStateAction<GridApi>>;
    adgroupsGridApi: GridApi;
    setAdgroupsGridApi: Dispatch<SetStateAction<GridApi>>;
    DateRange: IDRange;
    setDateRange: Dispatch<SetStateAction<IDRange>>;
    targetSelectedNode: Array<RowNode>;
    setTargetSelectedNode: Dispatch<SetStateAction<Array<RowNode>>>;
    adgroupLoading: boolean;
    setAdgroupLoading: Dispatch<SetStateAction<boolean>>;
    campaignsLoading: boolean;
    setCampaignsLoading: Dispatch<SetStateAction<boolean>>;
    keywordsLoading: boolean;
    setKeywordsLoading: Dispatch<SetStateAction<boolean>>;
    portfoliosLoading: boolean;
    setPortfoliosLoading: Dispatch<SetStateAction<boolean>>;
    productsLoading: boolean;
    setProductsLoading: Dispatch<SetStateAction<boolean>>;
    placementsLoading: boolean;
    setPlacementsLoading: Dispatch<SetStateAction<boolean>>;
    keywordsAnalyticsGridApi: GridApi;
    setKeywordsAnalyticsGridApi: Dispatch<SetStateAction<GridApi>>;
    alertsLoading: boolean;
    setAlertsLoading: Dispatch<SetStateAction<boolean>>;
    activeRulesLoading: boolean;
    setActiveRulesLoading: Dispatch<SetStateAction<boolean>>;
    alertsGridApi: GridApi;
    setAlertsGridApi: Dispatch<SetStateAction<GridApi>>;
    goalsGridApi: GridApi;
    setGoalsGridApi: Dispatch<SetStateAction<GridApi>>;
    goalsGridLoading: boolean;
    tagTypeAggregatedGridApi: GridApi;
    setTagTypeAggregatedGridApi: Dispatch<SetStateAction<GridApi>>;
    tagTypeAggregatedGridLoading: boolean;
    setTagTypeAggregatedGridLoading: Dispatch<SetStateAction<boolean>>;
    setGoalsGridLoading: Dispatch<SetStateAction<boolean>>;
    rulesFilterDisp: boolean;
    setRulesFilterDisp: Dispatch<SetStateAction<boolean>>;
    rulesSummaryDisp: boolean;
    setRulesSummaryDisp: Dispatch<SetStateAction<boolean>>;
    rulesFilterData: Rule;
    setRulesFilterData: Dispatch<SetStateAction<Rule>>;
    ruleLogsDate: IDateLogs;
    setRuleLogsDate: Dispatch<SetStateAction<IDateLogs>>;
    nameValue: string;
    requestModalDisp: boolean;
    setRequestModalDisp: Dispatch<SetStateAction<boolean>>;
    setNameValue: Dispatch<SetStateAction<string>>;
    currentTime: number;
    sessionExpiredAlert: boolean;
    setSessionExpiredAlert: Dispatch<SetStateAction<boolean>>;
    activeCheckedNode: Array<RowNode>;
    isAssetUpload: boolean;
    setIsAssetUpload: Dispatch<SetStateAction<boolean>>;
    assetResponse: IStrapiAssetResponse | Array<IStrapiResponse> | [];
    setAssetResponse: Dispatch<
        SetStateAction<IStrapiAssetResponse | Array<IStrapiResponse> | []>
    >;
    campaignCreationConfigParams: ICellRendererParams;
    setCampaignCreationConfigParams: Dispatch<
        SetStateAction<ICellRendererParams>
    >;
    customImageName: string;
    setCustomImageName: Dispatch<SetStateAction<string>>;
    activePage: number;
    setActivePage: Dispatch<SetStateAction<number>>;
    totalPages: number;
    setTotalPages: Dispatch<SetStateAction<number>>;
    pageLimit: number;
    setPageLimit: Dispatch<SetStateAction<number>>;
    selectedImageFilter: string;
    setSelectedImageFilter: Dispatch<SetStateAction<string>>;
    dprTimeSlots: TDprSlots;
    setDprTimeSlots: Dispatch<SetStateAction<TDprSlots>>;
    selectedValue: string;
    setSelectedValue: Dispatch<SetStateAction<string>>;
    daypartingCardCheckedData: IDprCheckedData;
    setDaypartingCardCheckedData: Dispatch<SetStateAction<IDprCheckedData>>;
    selectedSellersData: Array<string>;
    setSelectedSellersData: Dispatch<SetStateAction<Array<string>>>;
    selectedBrandsData: Array<number>;
    setSelectedBrandsData: Dispatch<SetStateAction<Array<number>>>;
    // using any here as facing some issues with this type and the component
    timezoneChangeHandler: (data: SelectOptions | any) => void;
    manualRecommendationsGridApi: GridApi;
    setManualRecommendationsGridApi: Dispatch<SetStateAction<GridApi>>;
    autoRecommendationsGridApi: GridApi;
    setAutoRecommendationsGridApi: Dispatch<SetStateAction<GridApi>>;
    revinitiRecommendationsGridApi: GridApi;
    setRevinitiRecommendationsGridApi: Dispatch<SetStateAction<GridApi>>;
    budgetRecommendationsGridApi: GridApi;
    setBudgetRecommendationsGridApi: Dispatch<SetStateAction<GridApi>>;
    placementRecommendationsGridApi: GridApi;
    setPlacementRecommendationsGridApi: Dispatch<SetStateAction<GridApi>>;
    manualRecommendationsLoading: boolean;
    setManualRecommendationsLoading: Dispatch<SetStateAction<boolean>>;
    autoRecommendationsLoading: boolean;
    setAutoRecommendationsLoading: Dispatch<SetStateAction<boolean>>;
    revinitiRecommendationsLoading: boolean;
    setRevinitiRecommendationsLoading: Dispatch<SetStateAction<boolean>>;
    budgetRecommendationsLoading: boolean;
    setBudgetRecommendationsLoading: Dispatch<SetStateAction<boolean>>;
    placementRecommendationsLoading: boolean;
    setPlacementRecommendationsLoading: Dispatch<SetStateAction<boolean>>;
    isCheckboxSelected: boolean;
    setIsCheckboxSelected: Dispatch<SetStateAction<boolean>>;
    suggestedNegKwsGridApi: GridApi;
    setSuggestedNegKwsGridApi: Dispatch<SetStateAction<GridApi>>;
    existingNegKwsGridApi: GridApi;
    setExistingNegKwsGridApi: Dispatch<SetStateAction<GridApi>>;
    productMappingGridApi: GridApi;
    setProductMappingGridApi: Dispatch<SetStateAction<GridApi>>;
    productMappingGridLoading: boolean;
    setProductMappingGridLoading: Dispatch<SetStateAction<boolean>>;
    reviewsLoader: boolean;
    setReviewsLoader: Dispatch<SetStateAction<boolean>>;
    fixedOptions: IFixedReviewOptions;
    setFixedOptions: Dispatch<SetStateAction<IFixedReviewOptions>>;
    selectedCategory: Array<string>;
    setSelectedCategory: Dispatch<SetStateAction<Array<string>>>;
    selectedSubCategory: Array<string>;
    setSelectedSubCategory: Dispatch<SetStateAction<Array<string>>>;
    skusData: Array<ISkusData>;
    setSkusData: Dispatch<SetStateAction<Array<ISkusData>>>;
    selectedSKU: string;
    setSelectedSKU: Dispatch<SetStateAction<string>>;
    reviewsBrandData: any;
    setReviewsBrandData: Dispatch<SetStateAction<any>>;
    reviewsSkuData: IReviewsSkuData;
    setReviewsSkuData: Dispatch<SetStateAction<IReviewsSkuData>>;
    resetReviewFilters: () => void;
    sidebarToggleItems: Record<string, boolean>;
    setSidebarToggleItems: Dispatch<SetStateAction<Record<string, boolean>>>;
    cerbosPermissions: ICerbosData;
    setCerbosPermissions: Dispatch<SetStateAction<ICerbosData>>;
    selectedDataFrequency: string;
    setSelectedDataFrequency: Dispatch<SetStateAction<string>>;
    productMappingGridParams: Partial<IProductMappingListParams>;
    setProductMappingGridParams: Dispatch<
        SetStateAction<IProductMappingListParams>
    >;
    selectedBrand: Array<string>;
    setSelectedBrand: Dispatch<SetStateAction<Array<string>>>;
    selectedCatAdditionalFilters: ICategoryAdvFilters;
    setSelectedCatAdditionalFilters: Dispatch<
        SetStateAction<ICategoryAdvFilters>
    >;
    directCompetitorFilter: boolean;
    setDirectCompetitorFilter: Dispatch<SetStateAction<boolean>>;
    powerSkusFilters: boolean;
    setPowerSkusFilter: Dispatch<SetStateAction<boolean>>;
    keywordMappingGridApi: GridApi;
    setKeywordMappingGridApi: Dispatch<SetStateAction<GridApi>>;
    keywordMappingGridLoading: boolean;
    setKeywordMappingGridLoading: Dispatch<SetStateAction<boolean>>;
    keywordMappingGridParams: Partial<IProductMappingListParams>;
    setKeywordMappingGridParams: Dispatch<
        SetStateAction<IProductMappingListParams>
    >;
}

export interface ICustomCellRendererParams {
    node: RowNode;
    data: Campaign;
    isModalComponent?: boolean;
    column: { colId: string };
    disableAction?: boolean;
    colDef?: ColDef;
}

export interface IPopoverRendererParams {
    node: RowNode;
    data: Keyword;
    isModalComponent?: boolean;
    column: { colId: string };
}

export type TModalConfig = {
    module: string;
    actions: Array<string>;
    filter: Record<string, IFilters>;
    isEdit: boolean;
    rule_id?: string;
};

export type TTrendIds = {
    keyword_id?: number;
    campaign_id?: number;
    adgroup_id?: number;
};

export type TAddParams = {
    value: {
        asin: string;
        isFocusedAsin: boolean;
    };
};

export type TBaseAndSupport = Array<Array<string | number | null>>;

export type TBaseAndSupportDecompressed = Record<
    string,
    string | number | null
>;

export type TRetrieveData = {
    rest: any;
    type: string;
    hasSupportingRequest: boolean;
    method: string;
};

export type TRetrieveResponse = {
    rows: TBaseAndSupport;
    status: boolean;
};

interface TChart extends PerformanceMetrics {
    date?: "string";
    report_date?: "string";
}

export type TChartsData = {
    id: string;
    name: string;
    type: string;
    data: TChart;
};

export interface IAssetExpandProps {
    fileExt: string;
    fileMime: string;
    fileURL: string;
    fileSrc: string;
    fileName: string;
}

type IAssetExpandHandler = ({
    fileExt,
    fileMime,
    fileURL,
    fileSrc,
    fileName,
}: IAssetExpandProps) => void;
export interface IPopulateAssetGrid {
    assetClickHandler?: () => void;
    assetData?: Array<IStrapiResponse>;
    assetResponse?: IStrapiAssetResponse | Array<IStrapiResponse> | [];
    setAssetResponse?: React.Dispatch<
        React.SetStateAction<IStrapiAssetResponse | Array<IStrapiResponse> | []>
    >;
    key?: number;
    onAssetExpandClickHandler?: IAssetExpandHandler;
}

export interface IStrapiAssetCard {
    assetResponse: IStrapiResponse;
    assetClickHandler?: () => void;
    onAssetExpandClickHandler?: IAssetExpandHandler;
}

export type TBulkKeywords = {
    keywordText: string;
    programType: string;
    keywordType: string;
    campaignId: string;
    channel_campaign_name: string;
    bid: number;
    adGroupId: string;
    ad_group_name: string;
    matchType: string;
    actionType?: string;
};

export type TSellersData = {
    id: number | string;
    seller_name: string;
    isPreferredSeller: boolean;
};

export type TBrandsData = {
    id: number | string;
    tag_name: string;
    is_selected: boolean;
};

export type TRuleActions = { actions: Array<string> };

export type TRuleModuleAction = Array<TRuleActions> | Array<string>;

export interface IUploadExcelFile extends Blob {
    name: string;
}

export type TMerge = {
    baseData: TBaseAndSupportDecompressed | TBaseAndSupport;
    supportingData: TBaseAndSupportDecompressed | TBaseAndSupport;
    type: string;
};

export type TKeywordsActionObj = {
    keywordText?: string;
    programType?: string;
    keywordType?: string;
    campaignId?: string;
    channel_campaign_name?: string;
    adGroupId?: string;
    ad_group_name?: string;
    matchTypeData?: Array<string>;
    matchTypeArray?: Array<string>;
    matchType?: string;
    state?: string;
    actionType?: string;
    action?: string;
    bid?: number;
    keyword_type?: string;
    keyword_name?: string;
    sysCampaignId?: string;
    sysAdgroupId?: string;
    targeting_type?: string;
};

export type AssetFormErrors = {
    file?: string;
    assetName?: string;
    assetType?: string;
};

export type TBuyboxEnabledData = Array<string>;

export type TBuyboxEnabledDataRedux = {
    buyboxEnabledData: {
        value: TBuyboxEnabledData;
    };
};

export type TFocusedAsins = {
    focusedAsins: {
        value: string[];
    };
};

export type TCampaignFilters = Array<{
    prop: string;
    data: Array<string>;
    prefix: string;
}>;

export type TSpecificAdgroup = {
    and:
        | {
              campaign_id: Array<string>;
          }
        | {
              sys_campaign_id: Array<string>;
          }
        | {
              [x: string]: string[];
          };
};

export type TCreateNegativeKw = { targets: Array<TKeywordsActionObj> };

export type TFetchAssets = {
    activePage: number;
    pageLimit: number;
    selectedImageFilter: string;
};

export type TUpdateAdgroups = {
    adgroupId: string;
    adgroupName?: string;
    attribute: Array<string>;
    state: string;
};

export type TUpdatePortfolios = {
    portfolioId: string;
    attribute: Array<string>;
    portfolio_name?: string;
    state: string;
};

export type TProductsData = {
    productads: Array<string>;
    state: string;
    attribute: Array<string>;
};

export type TNegKwsData = {
    keywordId: string;
    state: string;
    attribute: Array<string>;
};

export type TUpdateProductAds = Array<TProductsData>;
export type TUpdateNegativeKeywords = Array<TNegKwsData>;

export type TKeywords = {
    keywordId?: string;
    placementId?: string;
    changeType?: string;
    changeValue?: number;
    attribute: Array<string>;
    state?: string;
};

export type TPlacements = {
    placementId?: string;
    changeType?: string;
    changeValue?: number;
    attribute: Array<string>;
    state?: string;
};

export type TUpdateKeywords = {
    keywords: Array<TKeywords>;
};

export type TNegativeKw = {
    state: string;
    matchType?: string;
    adGroupId: string | number;
    campaignId: string | number;
    programType: string;
    bid?: null | number;
    keywordText: string;
};

export type TCreateNegativeKeyword = Record<
    string,
    Array<TNegativeKw> | Array<TCommonRowObj>
>;

export type TCampaigns = {
    campaignId: string;
    campaign_name?: string;
    changeType?: string;
    changeValue?: number;
    budget?: number;
    attribute: Array<string>;
    state: string;
    isDprActive?: boolean;
    cpm?: number;
    endDate?: number;
    tillBudgetEnds?: boolean;
    equallyDivideBudget?: boolean;
};

export type TAdgroups = {
    adGroupId?: string;
    ad_group_name?: string;
    budget?: number;
    attribute: Array<string>;
    state: string;
    cpm?: number;
};

export enum ToggleOptions {
    TOGGLE = "TOGGLE",
    RESET = "RESET",
    SETALL = "SETALL",
}

export type AdminSettingsAction =
    | { type: "TOGGLE"; key: keyof IAdminSettingsState }
    | { type: "SET_ALL"; payload: IAdminSettingsState }
    | { type: "RESET"; payload: IAdminSettingsState };

export type TUpdateCampaigns = Array<TCampaigns>;

export type TUpdateRecords = Array<TCampaigns> | Array<TAdgroups>;

export type TGetTrendsData = Record<string, TTrendIds>;

export type TOtherAttr = {
    baseProgramType: string;
    costType: string;
    campaignBudget: number;
    minBidValue: number;
    maxBidValue: number;
    recordType?: string;
};

export type ProductAds = {
    asin: string;
    state: "enabled" | "paused" | "archived";
};

export enum MatchType {
    PHRASE = "phrase",
    BROAD = "broad",
    EXACT = "exact",
    NEGATIVE_PHRASE = "negativePhrase",
    NEGATIVE_EXACT = "negativeExact",
    NEGATIVE_BROAD = "negativeBroad",
    ASIN_SAME_AS = "asinSameAs",
}

export type TKwAdgroup = {
    keywordText: string;
    state: string;
    matchType: string;
    bid: number;
};

export type TTargetAdgroup = {
    state: string;
    expressionType?: string;
    bid?: number;
    expression?: [
        {
            value?: string;
            type?: string;
        }
    ];
};

export type TAdGroupObj = {
    name?: string;
    state: string;
    defaultBid?: number;
    keywords?: Array<TKwAdgroup>;
    targets?: Array<TTargetAdgroup>;
    expressionType?: string;
    bid?: number;
    expression?: [
        {
            value?: string;
            type?: string;
        }
    ];
};

export type TCampaignObject = {
    productAds?: string;
    campaignName?: string;
    campaignType?: string;
    matchType?: Array<MatchType[keyof MatchType]>;
    adGroups: Array<TAdGroupObj>;
    targetingType?: Array<string>;
    targetingClass?: Array<string>;
    brandName?: string;
};

export interface ICampaignObj {
    name?: string;
    campaignName?: string;
    portfolioId?: string | number;
    campaignType?: string;
    targetingType?: string;
    targetingClass?: string;
    state?: string;
    startDate?: string;
    productAds?: string;
    dailyBudget?: number;
    brandName?: string;
}

export interface IProcessedCampaigns {
    adGroups: Array<TAdGroupObj>;
    dailyBudget: number;
    totalTargetCount: number;
    placementTop: number;
    placementProductPage: number;
    isAutomateBidChecked: boolean;
    campaignType?: string;
    productAds?: string;
    campaignName?: string;
    brandName?: string;
}

export interface ICreateReviewKeywords {
    keywordId: number;
    campaignId: string;
    adGroupId: string;
    keywordName: string;
    campaignName: string;
    adgroupName: string;
    matchType: string;
    existingBid: number;
    newBid: number;
}

export type TAdminSettings = {
    timezone?: TTimezone;
    buybox?: boolean;
    "day-parting"?: boolean;
    revstack?: boolean;
    "keyword-recommendation"?: boolean;
    navigator?: boolean;
    "category-analysis"?: boolean;
    "product-analysis"?: boolean;
};

export type TReports = {
    id: string;
    name: string;
    type: number;
};

export type TUserCreds = {
    username: string;
    password: string;
};

export interface IReportsData {
    report_id: string;
    embed_url: string;
    report_name: string;
    report_type_id: number;
    report_order_id: number;
}

export interface IReportForm {
    existingReportsData: Array<IReportsData>;
    setGetUpdatedReports: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedModule: React.Dispatch<React.SetStateAction<string>>;
}

export enum EAccountType {
    VENDOR = "vendor",
    SELLER = "seller",
}

export interface IChannels {
    channel_id: number;
    name: string;
    logo_url: string | null;
    domain: string;
    url_format: string;
    wallet_balance?: number;
    // revstack?: boolean;
    dayparting?: boolean;
    buybox?: boolean;
    country_code: string | null;
    selected_country_code: string;
    selected_country_locale: any;
    default_account_type?: EAccountType;
}

export interface IChannel {
    channel?: IValueNumber;
    channelList?: {
        value: Array<IChannels>;
    };
    channelWalletBalance?: IValueNumber;
    channelCurrencySymbol?: IValueString;
    channelDomain?: IValueString;
}

export interface IDailyBudgetUpdateResponse {
    dailyBudgetUpdateResponse: {
        value?: IResponse;
    };
}

export interface IDailyBudgetResponse {
    value?: IResponse;
    isLoading?: boolean;
    error?: string | Error;
}

export interface IResponse {
    status?: boolean;
    message?: string;
}

export type Modules = SubModule | ParentModule;

export type TClientTimezone = {
    clientTimezone: {
        value: TTimezone;
    };
};

export type TModuleDispConfig = {
    [key: string]: {
        isSubheaderVisible: boolean;
    };
};

export interface GridLayoutTypes {
    columnDefs: ColDef[];
    rowData?: TAgGridRowData;
    api?: GridApi;
    setGridApi?: React.Dispatch<SetStateAction<GridApi>>;
    onSelectionChanged?: (event: GridReadyEvent) => void;
    updateModule?: () => void;
    loading?: boolean;
    pagination?: boolean;
    isTotalRowsReq?: boolean;
    isRowSelectable?: IsRowSelectable;
    isSortByStatusReq?: boolean;
    setGridHeight?: React.Dispatch<SetStateAction<number>>;
    rowHeight?: number;
    gridParams?: GridParams;
    paginationPageSize?: number;
    rowModelType?: "clientSide" | "infinite" | "viewport" | "serverSide";
    serverSideDatasource?: IServerSideDatasource;
    serverSideStoreType?: "partial" | "full";
    loadingCellRenderer?: () => null;
    sideBar?: typeof agDefaultSidebar | null;
    planLevel?: string;
    excelStyles?: any;
    rowDragManaged?: boolean;
    onRowDragEnd?: (event: RowDragEndEvent) => void;
    cacheBlockSize?: number;
    maxBlocksInCache?: number;
    isServerSideGrid?: boolean;
    getRowStyle?: (params: RowClassParams) => Record<string, string>;
}

export interface GridParams {
    isBadgeRequired: boolean;
    rowHeight: number;
}

export type TActiveClientId = {
    activeClientId: {
        value: string;
    };
};

export interface IReportData {
    value?: Record<string, Array<IReportsData[]>>;
    isLoading?: boolean;
    error?: string | Error;
}

export type TReportsData = {
    reportsData: {
        value: {
            revstack: IReportsData[];
            common: IReportsData[];
        };
        isLoading?: boolean;
    };
};

export type TReportTypesData = {
    id: number;
    name: string;
};

export interface IReportTypes {
    reportTypesData: {
        value: Array<TReportTypesData>;
    };
}

export type TSelectedReportData = {
    selectedReportData: {
        value: IReportsData;
    };
};

export type TOverviewGrid = {
    componentType?: string;
    setGridApi?: React.Dispatch<SetStateAction<GridApi>>;
    gridApi?: GridApi;
};

export type AppDispatch = typeof store.dispatch;

export interface IChannelList {
    value?: Array<IChannels>;
    isLoading?: boolean;
    error?: string | Error;
}

export interface ICampaignList {
    value?: Array<Campaign[]>;
    isLoading?: boolean;
    error?: string | Error;
}

export interface ILinks {
    title: string;
    name: string;
    id: string;
    path: string;
    activeModule?: string;
    report?: IReportsData;
}

export interface ITargetCellRenderer extends ICellRendererParams {
    pinnedColumnValue: Record<string, string | number>;
    isBadgeRequired: boolean;
    isTypeBadgeRequired: boolean;
    isCampaignSyncStatusRequired?: boolean;
    setGetUpdatedReports?: React.Dispatch<React.SetStateAction<boolean>>;
    disableAction?: boolean;
    existingReportsData?: Array<IReportsData>;
}
export interface IEditNameCellRenderer {
    params: ITargetCellRenderer;
    setInputWidth: Dispatch<SetStateAction<number>>;
    setInputValue: Dispatch<SetStateAction<string>>;
    isCampaignSyncCheckRequired?: boolean;
}

export enum Channels {
    AMAZON = 1,
    AMAZON_FRESH = 32,
    FLIPKART = 2,
    BLINKIT = 27,
    MYNTRA = 28,
    NYKAA = 25,
    BIGBASKET = 26,
    DMART = 71,
    JIO_MART = 68,
    FLIPKART_GROCERY = 39,
    INSTAMART = 65,
    NOON = 87,
    ZEPTO = 109,
    TAKEALOT = 118,
    PURPLLE = 63,
    CARREFOUR = 43,
    TALABAT = 239,
    TALABAT_REVSTACK = 228,
    NOON_MINUTES = 283,
    WALMART = 42,
    SHOPEE = 30,
    LAZADA = 31,
    TIKTOK = 288,
    HEPSIBURADA = 180,
}

export enum ParentModule {
    PERFORMANCE_OVERVIEW = "performance-overview",
    PRODUCT_DEEPDIVE = "product-deepdive",
    RULES = "rules",
    KEYWORD_ANALYTICS = "keyword-analytics",
    PRODUCT_ANALYTICS = "product-analytics",
    NEGATIVE_KEYWORDS = "negative-keywords",
    KEYWORD_RECOMMENDATIONS = "keyword-recommendations",
    CAMPAIGN_RECOMMENDATIONS = "campaign-recommendations",
    REVSTACK = "revstack",
    CHANGE_LOG = "change-log",
    SETTINGS = "settings",
    BUYBOX = "buybox",
    INTEGRATIONS = "integrations",
    COMMON_REPORTS = "common-reports",
    REPORTS_BUILDER = "reports-builder",
    ADMIN_SETTINGS = "admin",
    REVIEW_ANALYTICS = "review-analytics",
    CHATBOT = "chatbot",
    ONEDER = "1der",
}

export enum SubModule {
    OVERVIEW = "overview",
    PORTFOLIO = "portfolios",
    CAMPAIGN = "campaigns",
    ADGROUP = "adgroups",
    KEYWORD = "keywords",
    PRODUCT = "products",
    KEYWORD_MANUAL = "manual",
    KEYWORD_AUTO = "auto",
    REVIEW_BRAND = "brand",
    REVIEW_SKU = "sku",
    KEYWORD_REVINITI = "reviniti",
    CAMPAIGN_BUDGET = "budget",
    CAMPAIGN_PLACEMENT = "placement",
    SUGGESTED_NEG_KWS = "suggested",
    EXISTING_NEG_KWS = "existing",
    MEDIA_PLANNING = "media-planning",
    CATEGORY_INSIGHTS = "category-insights",
    CATEGORY_ANALYSIS = "category-analysis",
    NAVIGATOR = "navigator",
    PRODUCT_ANALYSIS = "product-analysis",
    KEYWORD_MAPPING = "keyword-mapping",
    PLACEMENTS = "placements",
    CAMPAIGN_CREATION = "campaign-creation",
}

export enum SubGrids {
    KA_KEYWORDS = "keywords",
    KA_CAMPAIGNS = "campaigns",
    PA_KEYWORDS = "keywords",
    PA_ADGROUPS = "adgroups",
    ADD_AS_EXACT = "addAsExact",
}

export enum OverviewGrids {
    TAG_TYPE_AGGREGATE = "tagTypeAggregate",
}

export enum Actions {
    BID_CHANGE = "BID_CHANGE",
    STATUS_CHANGE = "STATUS_CHANGE",
    BUDGET_CHANGE = "BUDGET_CHANGE",
    NAME_CHANGE = "NAME_CHANGE",
    ADD_AS_NEGATIVE = "ADD_AS_NEGATIVE",
    REMOVE_NEGATIVE = "REMOVE_NEGATIVE",
    PAUSE = "PAUSE",
    ENABLE = "ENABLE",
    ARCHIVE = "ARCHIVE",
    INCREASE_BUDGET_PERC = "INCREASE_BUDGET_PERC",
    DECREASE_BUDGET_PERC = "DECREASE_BUDGET_PERC",
    INCREASE_BID_PERC = "INCREASE_BID_PERC",
    DECREASE_BID_PERC = "DECREASE_BID_PERC",
    BID_CHANGE_ABSOLUTE = "BID_CHANGE_ABSOLUTE",
    MARK_NEGATIVE = "MARK_NEGATIVE",
    PAUSE_KEYWORDS = "PAUSE_KEYWORDS",
    PAUSE_ADS = "PAUSE_ADS",
    ADD_AS_NEGATIVE_EXACT = "ADD_AS_NEGATIVE_EXACT",
    ADD_KEYWORD = "ADD_KEYWORD",
    PLACEMENT_CHANGE = "PLACEMENT_CHANGE",
    REQUIRED_DAILY_BUDGET_CHANGE = "REQUIRED_DAILY_BUDGET_CHANGE",
    ADD_TO_BUYBOX = "ADD_TO_BUYBOX",
    REMOVE_BUYBOX = "REMOVE_BUYBOX",
    PAUSE_ALL = "PAUSE_ALL",
    ENABLE_ALL = "ENABLE_ALL",
    CREATE_CAMPAIGN = "CREATE_CAMPAIGN",
    MOVE_TO_SUGG_MIN = "MOVE_TO_SUGG_MIN",
    MOVE_TO_SUGG_MAX = "MOVE_TO_SUGG_MAX",
    MOVE_TO_SUGG_AVG = "MOVE_TO_SUGG_AVG",
    BULK_CAMPAIGN_CREATION = "BULK_CAMPAIGN_CREATION",
}

export enum Features {
    BULK_ACTION = "bulk-actions",
    DAY_PARTING = "day-parting",
    RULE = "rule",
    BID = "bid",
    BUDGET = "budget",
    PAUSE = "pause",
    ENABLE = "enable",
    ARCHIVE = "archive",
    STOP = "stop",
    MARK_NEGATIVE = "mark-negative",
    BULK_TGT_ACTION = "bulk-target-actions",
    ADD_TO_BUYBOX = "add-to-buybox",
    REMOVE_BUYBOX = "remove-buy-box",
    PAUSE_ALL = "pause-all",
    ENABLE_ALL = "enable-all",
    CREATE_CAMPAIGN = "create-campaign",
    ADD_NEGATIVE = "add-negatives",
    REMOVE_NEGATIVE = "remove-negatives",
    ADD_AS_EXACT = "add-as-exact",
    REVERT_ACTION = "revert-action",
    DATE_CHANGE = "date-change",
    TAGS_ADDITION = "tags-add",
    BUYBOX = "buybox",
    REVSTACK = "revstack",
    KEYWORD_RECOMMENDATIONS = "keyword-recommendations",
    BULK_PLACEMENT_CHANGE = "bulk-placement-change",
    BULK_PRODUCT_MAPPING = "bulk-mapping",
    ADD_MAPPING = "add-mapping",
    APPLY_1DS_MAPPING = "apply-1ds-mapping",
    CATEGORY_ANALYSIS = "category-analysis",
    NAVIGATOR = "navigator",
    PRODUCT_ANALYSIS = "product-analysis",
}

export interface ILog {
    id: number;
    log_date: string;
    channel_id: number;
    client_id: number;
    country: string | number;
    module: string;
    change_type: string;
    record_id: string;
    record_type: string;
    attribute: string | null;
    old_value: string | number | null;
    new_value: string | number | null;
    source: string;
    nature: "auto" | "manual";
    source_name: string;
    metainfo: string;
}

export interface IRevertAction {
    id: number;
    module: string;
    record_id: string;
    old_value: string | number;
    attribute: string;
    metainfo: string;
}

export interface IUserCountryDetails {
    selected_country_code: string;
    // country_name: string;
    selected_country_locale: Array<string>;
}

export interface ICountryCodePicker {
    fetchData: boolean;
    style?: Record<string, string>;
    countryRef?: Ref<null>;
    selectedCountryDetails: IUserCountryDetails;
    setSelectedCountryDetails: React.Dispatch<
        React.SetStateAction<IUserCountryDetails>
    >;
}

export enum ColorHex {
    SUCCESS = "#28a745",
    WARNING = "#ffc107",
    DANGER = "#dc3545",
    BLUE = "#3652AD",
    PRIMARY_BLUE = "#0255cf",
    MAROON = "#944E63",
    GREY = "#787A91",
    WHITE = "#FFFFFF",
    NAVY = "#316B83",
}

export enum ServingStatus {
    ad_group_name = "adgroup_status",
    campaign_name = "campaign_status",
    keyword_name = "serving_status",
    serving_status = "campaign_status",
    campaign_status = "campaign_status",
    sku_id = "serving_status",
}

export enum BadgeText {
    enabled = "Active",
    live = "Live",
    new = "New",
    serviceable = "Serviceable",
    exhausted = "Exhausted",
    paused = "Paused",
    budget_met = "Budget Met",
    pacing_budget_met = "Budget Met",
    total_budget_met = "Total Budget Met",
    daily_budget_met = "Daily Budget Met",
    daily_budget_exhausted = "Daily Budget Met",
    campaign_archived = "Archived",
    aborted = "Aborted",
    completed = "Completed",
    draft = "Draft",
    non_editable = "Non Editable",
    ended = "Ended",
    stopped = "Stopped",
    active = "Active",
    on_hold = "On Hold",
    archived = "Archived",
    product_listing = "Product Booster",
    product_recommendation = "Recommendation Ads",
    banner_listing = "Listing Spotlight",
    search_suggestion = "Brand Suggestions",
    paused_due_to_end_date_reached = "End Date Reached",
    paused_due_to_max_cap_reached = "Max Cap Reached",
    under_review = "Under Review",
    AUCTION = "Auction",
    OS_ADS_SEARCH = "Search Ads",
    SMART_SHOPPING = "Smart Shopping",
    campaign_status_live = "Live",
    campaign_status_booked = "Booked",
    campaign_status_stopped = "Stopped",
    CAMPAIGN_AD_TYPE_BANNER = "Banner",
    CAMPAIGN_AD_TYPE_SPONSORED_COLLECTION = "Sponsored Collection",
    CAMPAIGN_AD_TYPE_SPONSORED_PRODUCT = "Sponsored Product",
    product = "Product",
    brand = "Brand",
    search = "Search",
    listing = "Listing",
    BANNER = "BANNER",
    PDA = "PDA",
    MANUAL = "MANUAL",
    KEYWORD_TARGETED_DISPLAY = "KEYWORD_DISPLAY",
    PCA = "PCA",
    under_process = "Under Process",
    scheduled = "Scheduled",
    total_budget_cap_reached = "Total Budget Cap Reached",
    insufficient_wallet_balance = "Insufficient Wallet Balance",
    AUTO = "AUTO",
}

export enum BadgeColor {
    live = ColorHex.SUCCESS,
    active = ColorHex.SUCCESS,
    new = ColorHex.PRIMARY_BLUE,
    serviceable = ColorHex.SUCCESS,
    campaign_status_live = ColorHex.SUCCESS,
    enabled = ColorHex.SUCCESS,
    exhausted = ColorHex.WARNING,
    paused = ColorHex.WARNING,
    budget_met = ColorHex.WARNING,
    pacing_budget_met = ColorHex.WARNING,
    total_budget_met = ColorHex.WARNING,
    daily_budget_exhausted = ColorHex.WARNING,
    daily_budget_met = ColorHex.WARNING,
    on_hold = ColorHex.WARNING,
    completed = ColorHex.WARNING,
    draft = ColorHex.WARNING,
    paused_due_to_end_date_reached = ColorHex.WARNING,
    paused_due_to_max_cap_reached = ColorHex.WARNING,
    under_review = ColorHex.WARNING,
    archived = ColorHex.DANGER,
    campaign_archived = ColorHex.DANGER,
    aborted = ColorHex.DANGER,
    stopped = ColorHex.DANGER,
    campaign_status_booked = ColorHex.DANGER,
    campaign_status_stopped = ColorHex.DANGER,
    non_editable = ColorHex.DANGER,
    ended = ColorHex.DANGER,
    banner_listing = ColorHex.BLUE,
    search_suggestion = ColorHex.BLUE,
    AUCTION = ColorHex.BLUE,
    SMART_SHOPPING = ColorHex.BLUE,
    OS_ADS_SEARCH = ColorHex.MAROON,
    product_listing = ColorHex.MAROON,
    product_recommendation = ColorHex.MAROON,
    CAMPAIGN_AD_TYPE_BANNER = ColorHex.BLUE,
    CAMPAIGN_AD_TYPE_SPONSORED_COLLECTION = ColorHex.NAVY,
    CAMPAIGN_AD_TYPE_SPONSORED_PRODUCT = ColorHex.MAROON,
    product = ColorHex.MAROON,
    brand = ColorHex.NAVY,
    search = ColorHex.MAROON,
    listing = ColorHex.NAVY,
    BANNER = ColorHex.MAROON,
    PDA = ColorHex.NAVY,
    MANUAL = ColorHex.MAROON,
    KEYWORD_TARGETED_DISPLAY = ColorHex.BLUE,
    PCA = ColorHex.MAROON,
    under_process = ColorHex.WARNING,
    scheduled = ColorHex.WARNING,
    total_budget_cap_reached = ColorHex.WARNING,
    insufficient_wallet_balance = ColorHex.WARNING,
    AUTO = ColorHex.BLUE,
}

export interface IUpdatedGridData {
    gridData?: TCommonRowData;
    currentRow?: TCommonRowObj;
    selectedRows?: TCommonRowData;
    paramToChange?: string;
    paramToFilter: string;
    newParamValue?: Array<string> | number | string | boolean | TClientTags[];
    isRelatedGrid?: boolean;
    rowIdentifier?: string | number;
    updateType?: string;
    successfulUpdatedRows?: TCommonRowData;
    additionalValuesToUpdate?: Record<string, string | number | boolean>;
    bulkParamsToChange?: TBulkParamToChange[];
    dateUpdateConfig?: {
        selectedDate: number | string;
        addNumDays: number;
    };
}

export interface IGridsToUpdateParams {
    campaign_tags: Record<string, any>;
}
export interface IGridUpdateConfigParams {
    setGridState: any;
    paramToFilter: string;
    gridsToUpdateParams?: Record<string, any>;
}

type TBulkParamToChange = {
    key: string;
    newValue?:
        | number
        | string
        | boolean
        | string[]
        | { tag_id: number; tag_name: any; tag_type: any; tag_type_id: any }[];
    isDateCalcReq?: boolean;
    dateUpdateType?: string;
    shouldArrayPreserve?: boolean;
};

export interface IBulkRequiredConfig {
    selectedRows: TCommonRowData;
    paramToChange?: string;
    paramToFilter: string;
    newParamValue?: string | number;
    updateType?: string;
    successfulUpdatedRows?: TCommonRowData;
    budgetUpdateType?: string;
    bulkParamsToChange?: TBulkParamToChange[];
    dateUpdateConfig?: {
        selectedDate: number | string;
        addNumDays: number;
    };
}
export interface DailyBudgetConfigUpdate {
    campaigns: number[];
    updateType: string;
    updateValue: string;
}

export enum CostTypeText {
    cpm = "CPM",
    cpc = "CPC",
}
export enum ProgramTypeText {
    p = "Performance",
    r = "Reach",
    a = "Awareness",
    product_listing = "Product Booster",
    product_recommendation = "Recommendation Ads",
    banner_listing = "Listing Spotlight",
    search_suggestion = "Brand Suggestions",
    sp = "SP",
    sb = "SB",
    sd = "SD",
    pla = "PLA",
    pca = "PCA",
    ki = "Keyword Item",
    kb = "Keyword Banner",
    ib = "Item Boost",
    ba = "Banner",
    sc = "Speciality Collection",
    search = "Search",
    listing = "Listing",
    banner = "BANNER",
    pda = "PDA",
    MANUAL = "MANUAL",
    auto = "AUTO",
    keyword_targeted_display = "KEYWORD_TARGETED_DISPLAY",
    auction = "Auction",
    os_ads_search = "Search Ads",
    smart_shopping = "Smart Shopping",
    gts = "Guaranteed Top Slot",
}

export type TOperationType = {
    isBulkAction: boolean;
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>;
};

export interface ISetUpdateGridDataParams {
    params?: any;
    value?: string | number | boolean | string[] | TClientTags[];
    additionalValuesToUpdate?: Record<string, string | number | boolean>;
    isReduxState?: boolean;
    bulkRequiredConfig?: IUpdatedGridData;
    modalParamToChange?: string;
}

export interface IUpdatedGridDataResponse {
    rowData?: TCommonRowData;
    rowIdentifier?: string | number;
}

export interface AdditionalBidParams {
    smartBid?: number;
    isSmartMatch?: boolean;
    slots?: Array<number>;
}

export enum RecordType {
    TARGET = "target",
    KEYWORD = "keyword",
}

export type TAddToExactRowData = {
    addToExactRowData: {
        value: Keyword[];
    };
};

export type TKeywordsSelectionData = {
    keywordsSelectionData?: {
        value: Keyword[];
    };
    addAsExactData?: {
        value: Keyword[];
    };
};
export type TBulkActionsFooterBtnValues = {
    isReviewSelectionBtnVisible: boolean;
    isReviewSelectionBtnDisabled: boolean;
    isAddKeywordBtnVisible: boolean;
    isAddKeywordBtnDisabled: boolean;
    isAddNegativeKeywordBtnVisible: boolean;
    isAddNegativeKeywordBtnDisabled: boolean;
    isBackBtnVisible: boolean;
};

export interface IProps {
    options: any;
    placeholder: string;
    setSelectedOptions: React.Dispatch<React.SetStateAction<SelectOptions[]>>;
    selectedOptions: SelectOptions[];
    id?: string;
    onChangeInSelectedOptions?: () => void;
    channel?: number;
    setIsShowMatchType?: React.Dispatch<React.SetStateAction<boolean>>;
    setFooterBtnValues?: React.Dispatch<
        React.SetStateAction<TBulkActionsFooterBtnValues>
    >;
    actionLevel?: string;
    isAdgroupSelectionRequired?: boolean;
    isMatchTypeSelectionRequired?: boolean;
}

export enum ChannelName {
    AMAZON = "amazon",
    AMAZON_FRESH = "amazon_fresh",
    FLIPKART = "flipkart",
    BLINKIT = "blinkit",
    MYNTRA = "myntra",
    ZEPTO = "zepto",
    INSTAMART = "instamart",
    TAKEALOT = "takealot",
    PURPLLE = "purplle",
    NOON = "noon",
    NOON_MINUTES = "noon minutes",
    TALABAT = "talabat",
    WALMART = "walmart",
    DEFAULT = "default",
    NYKAA = "nykaa",
    BIGBASKET = "big_basket",
    DMART = "dmart",
    JIO_MART = "jio_mart",
    FLIPKART_GROCERY = "flipkart_grocery",
    CARREFOUR = "carrefour",
    SHOPEE = "shopee",
    LAZADA = "lazada",
    TIKTOK = "tiktok",
    HEPSIBURADA = "hepsiburada",
}

type DropDownOptions =
    | "targetTypeOptions"
    | "matchTypeOptions"
    | "negativeMatchTypeOptions"
    | "programTypeOptions"
    | "kaCampaignTypeOptions"
    | "chartFilterOptions";

export type ChannelConfig = {
    options: CommonChannelOptions;
    modules: ParentModuleConfig;
    dropdownOptions?: Record<
        DropDownOptions,
        {
            value: string;
            label: string;
        }[]
    >;
};

export type TFormatText = {
    program_type: Record<string, string>;
    targetingtype?: Record<string, string>;
    record_type?: Record<string, string>;
    marketplace_type?: Record<string, string>;
};

export type CommonChannelOptions = {
    decimalValue: number;
    recommendedBid: number;
    isTargetCheckRequired: boolean;
    formatText: TFormatText;
    isSmartInfoRequired?: boolean;
    defaultActionLevel?: string;
    platformFeatures: Features[];
    minCampaignBudget?: number;
    maxCampaignBudget?: number;
    minExactMatchBid?: number;
};
export interface ParentModuleConfig {
    [ParentModule.PERFORMANCE_OVERVIEW]: ModuleConfig<ParentModule.PERFORMANCE_OVERVIEW>;
    [ParentModule.RULES]?: ModuleConfig<ParentModule.RULES>;
    [ParentModule.KEYWORD_ANALYTICS]?: ModuleConfig<ParentModule.KEYWORD_ANALYTICS>;
    [ParentModule.PRODUCT_ANALYTICS]: ModuleConfig<ParentModule.PRODUCT_ANALYTICS>;
    [ParentModule.PRODUCT_DEEPDIVE]: ModuleConfig<ParentModule.PRODUCT_DEEPDIVE>;
    [ParentModule.NEGATIVE_KEYWORDS]?: ModuleConfig<ParentModule.NEGATIVE_KEYWORDS>;
    [ParentModule.KEYWORD_RECOMMENDATIONS]?: ModuleConfig<ParentModule.KEYWORD_RECOMMENDATIONS>;
    [ParentModule.CAMPAIGN_RECOMMENDATIONS]?: ModuleConfig<ParentModule.CAMPAIGN_RECOMMENDATIONS>;
    [ParentModule.REVSTACK]?: ModuleConfig<ParentModule.REVSTACK>;
    [ParentModule.COMMON_REPORTS]?: ModuleConfig<ParentModule.COMMON_REPORTS>;
    [ParentModule.CHANGE_LOG]: ModuleConfig<ParentModule.CHANGE_LOG>;
    [ParentModule.SETTINGS]: ModuleConfig<ParentModule.SETTINGS>;
    [ParentModule.INTEGRATIONS]?: ModuleConfig<ParentModule.INTEGRATIONS>;
    [ParentModule.REPORTS_BUILDER]?: ModuleConfig<ParentModule.REPORTS_BUILDER>;
    [ParentModule.ADMIN_SETTINGS]: ModuleConfig<ParentModule.ADMIN_SETTINGS>;
    [ParentModule.REVIEW_ANALYTICS]?: ModuleConfig<ParentModule.REVIEW_ANALYTICS>;
    [ParentModule.CHATBOT]: ModuleConfig<ParentModule.CHATBOT>;
    [ParentModule.ONEDER]: ModuleConfig<ParentModule.ONEDER>;
    [SubModule.NAVIGATOR]?: ModuleConfig<SubModule.NAVIGATOR>;
    [SubModule.CATEGORY_ANALYSIS]?: ModuleConfig<SubModule.CATEGORY_ANALYSIS>;
    [SubModule.PRODUCT_ANALYSIS]?: ModuleConfig<SubModule.PRODUCT_ANALYSIS>;
}

type ComponentConfig = {
    isVisible: boolean;
    label?: string;
    disabledTypes?: (ProgramType | ProgramTypeText)[];
    showForTypes?: ProgramType[];
    payloadKey?: string;
};

type CustomConfigFields =
    | "till_budget_ends"
    | "end_date"
    | "equally_divide_budget"
    | "cpm"
    | "contextual_multiplier"
    | "premium_placements"
    | "product_listing_page"
    | "home_page"
    | "top_of_search"
    | "auto"
    | "premium_users";

export interface ModuleConfig<
    T extends keyof GridParamsMapping &
        keyof SubModuleMapping &
        keyof SubGridMapping
> {
    name?: string;
    isSubheaderVisible?: boolean;
    subModules?: SubModuleMapping[T];
    actions?: Actions[];
    columns?: string[];
    columnsOverwrite?: Array<Record<string, any>>;
    features?: Partial<
        Record<
            Features,
            {
                name: string;
                actions?: Actions[];
            }
        >
    >;
    gridParams?: GridParamsMapping[T];
    componentConfig?: Record<CustomConfigFields, ComponentConfig>;
    subGrids?: SubGridMapping[T];
    additionalFeatures?: Record<string, boolean>;
    overviewGrids?: OverviewGridMapping[T];
}

interface ExplicitGridParamsMapping {
    [SubModule.ADGROUP]: AdgroupGridParams;
    [SubModule.CAMPAIGN]: CampaignGridParams;
    [SubModule.KEYWORD]: KeywordGridParams;
    [SubModule.PORTFOLIO]: PortfolioGridParams;
    [SubModule.PLACEMENTS]: PlacementGridParams;
    [SubModule.PRODUCT]: CommonGridParams;
    [ParentModule.KEYWORD_ANALYTICS]: KAGridParams;
    [ParentModule.PRODUCT_ANALYTICS]: CommonGridParams;
    [ParentModule.NEGATIVE_KEYWORDS]: NegKwsGridParams;
    [SubModule.SUGGESTED_NEG_KWS]: NegKwsGridParams;
    [SubModule.EXISTING_NEG_KWS]: NegKwsGridParams;
    [SubModule.CAMPAIGN_BUDGET]: CampaignBudgetGridParams;
    [SubModule.CAMPAIGN_PLACEMENT]: CommonGridParams;
    [SubModule.KEYWORD_MANUAL]: CommonGridParams;
    [SubModule.KEYWORD_AUTO]: CommonGridParams;
    [ParentModule.REVSTACK]: undefined;
    [ParentModule.COMMON_REPORTS]: undefined;
    [ParentModule.CHANGE_LOG]: undefined;
    [ParentModule.SETTINGS]: undefined;
    [ParentModule.INTEGRATIONS]: undefined;
    [ParentModule.REPORTS_BUILDER]: undefined;
    [ParentModule.ADMIN_SETTINGS]: undefined;
}

type GridParamsMapping = ExplicitGridParamsMapping & {
    [key in Exclude<string, keyof ExplicitGridParamsMapping>]?: undefined;
};
export interface ExplicitSubModuleMapping {
    [ParentModule.PERFORMANCE_OVERVIEW]: PerformanceOverviewSubModule;
    [ParentModule.KEYWORD_RECOMMENDATIONS]: KeywordRecommendationSubModule;
    [ParentModule.CAMPAIGN_RECOMMENDATIONS]: CampaignRecommendationSubModule;
    [ParentModule.NEGATIVE_KEYWORDS]: NegativeKeywordsSubModule;
    [ParentModule.REVSTACK]: undefined;
    [ParentModule.COMMON_REPORTS]: undefined;
    [ParentModule.CHANGE_LOG]: undefined;
    [ParentModule.SETTINGS]: undefined;
    [ParentModule.INTEGRATIONS]: undefined;
    [ParentModule.REPORTS_BUILDER]: undefined;
    [ParentModule.ADMIN_SETTINGS]: undefined;
}

type SubModuleMapping = ExplicitSubModuleMapping & {
    [key in Exclude<string, keyof ExplicitSubModuleMapping>]?: undefined;
};
interface ExplicitSubGridMapping {
    [ParentModule.KEYWORD_ANALYTICS]: KASubGrid;
    [ParentModule.PRODUCT_ANALYTICS]: PASubGrid;
    [ParentModule.NEGATIVE_KEYWORDS]: undefined;
    [ParentModule.KEYWORD_RECOMMENDATIONS]: undefined;
    [ParentModule.CAMPAIGN_RECOMMENDATIONS]: undefined;
    [ParentModule.REVSTACK]: undefined;
    [ParentModule.COMMON_REPORTS]: undefined;
    [ParentModule.CHANGE_LOG]: undefined;
    [ParentModule.SETTINGS]: undefined;
    [ParentModule.INTEGRATIONS]: undefined;
    [ParentModule.REPORTS_BUILDER]: undefined;
    [ParentModule.ADMIN_SETTINGS]: undefined;
}

type SubGridMapping = ExplicitSubGridMapping & {
    [key in Exclude<string, keyof ExplicitSubGridMapping>]?: undefined;
};

interface ExplicitOverviewGridMapping {
    [SubModule.OVERVIEW]: POOverviewGrid;
}

type OverviewGridMapping = ExplicitOverviewGridMapping & {
    [key in Exclude<string, keyof ExplicitOverviewGridMapping>]?: undefined;
};

interface SubModuleConfig<
    T extends keyof GridParamsMapping & keyof OverviewGridMapping
> {
    name?: string;
    actions?: Actions[];
    columns: string[];
    columnsOverwrite?: Array<Record<string, any>>;
    features?: Partial<
        Record<
            Features,
            {
                name: string;
                actions?: Actions[];
            }
        >
    >;
    gridParams?: GridParamsMapping[T];
    componentConfig?: Partial<Record<CustomConfigFields, ComponentConfig>>;
    slug?: string;
    nameValidationRegex?: RegExp;
    overviewGrids?: OverviewGridMapping[T];
}

interface GridParamConfig {
    cellRenderer?: any;
    minWidth?: number;
    maxWidth?: number;
    headerName?: string;
    filterOptions?: string[];
    maxBidLimitRecordTypes?: Array<string>;
    canBidInputBeDisabled?: boolean;
    isSlotChangeRequired?: boolean;
    bidType?: string;
    minAllowedBudget?: number;
}

export interface CommonGridParams {
    rowHeight?: number;
    isBadgeRequired?: boolean;
    isBidRequired?: boolean;
    isTypeBadgeRequired?: boolean;
    click?: GridParamConfig;
    cpc?: GridParamConfig;
    is_exact?: GridParamConfig;
    bid?: GridParamConfig;
    cpm?: GridParamConfig;
    bidColumn?: string;
    keywordColumn?: string;
    columnsToHide?: string[];
    doesAddToExact?: boolean;
    isRemoveSkuAllowed?: boolean;
    cpmChangeAllowedTypes?: Array<string>;
    isKeywordPauseAllowed?: boolean;
    disabledProgramTypes?: Array<string>;
}

export interface CampaignBudgetGridParams {
    isCpmChangeAllowed: boolean;
    rowHeight?: number;
}

interface CampaignGridParams extends CommonGridParams {
    budget: GridParamConfig;
    program_type: GridParamConfig;
    cost_type?: GridParamConfig;
    campaign_name: GridParamConfig;
    isCpmChangeAllowed: boolean;
    isBudgetReduceAllowed: boolean;
    isDateChangeAllowed: boolean;
    isCampaignRestartAllowed?: boolean;
    isDailyBudgetChangeOnPlatform?: boolean;
    isDateReduceAllowed: boolean;
    isArchivedCampaignsSelectionAllowed: boolean;
    isCampaignSyncStatusRequired?: boolean;
}

interface AdgroupGridParams extends CommonGridParams {
    budget: GridParamConfig;
    isCpmChangeAllowed: boolean;
    isBudgetReduceAllowed: boolean;
    isDateChangeAllowed: boolean;
    isDateReduceAllowed: boolean;
}

interface KeywordGridParams extends CommonGridParams {
    bid: GridParamConfig;
    isKeywordPauseAllowed?: boolean;
    cpmChangeAllowedTypes?: Array<string>;
    excludedBidProgramType?: Array<string>;
    canChangeKeywordStatus?: boolean;
    shouldRemoveTitleCase?: boolean;
}

interface PortfolioGridParams extends CommonGridParams {
    rowHeight?: number;
}

interface PlacementGridParams extends CommonGridParams {
    bid: GridParamConfig;
    isKeywordPauseAllowed?: boolean;
    cpmChangeAllowedTypes?: Array<string>;
    excludedBidProgramType?: Array<string>;
    canChangeKeywordStatus?: boolean;
    shouldRemoveTitleCase?: boolean;
}

interface KAGridParams extends CommonGridParams {
    isKeywordLinkRequired: boolean;
    isAdgroupSelectionRequired: boolean;
    isBidRequired: boolean;
    columnsToHide: string[];
    isMatchTypeReviewRequired: boolean;
    isNegativeAdditionAllowed: boolean;
    isMatchTypeSelectionRequired?: boolean;
    excludedBidProgramType?: string[];
}

interface NegKwsGridParams extends CommonGridParams {
    program_type: GridParamConfig;
}
interface PerformanceOverviewSubModule {
    [SubModule.OVERVIEW]: SubModuleConfig<SubModule.OVERVIEW>;
    [SubModule.PORTFOLIO]?: SubModuleConfig<SubModule.PORTFOLIO>;
    [SubModule.CAMPAIGN]: SubModuleConfig<SubModule.CAMPAIGN>;
    [SubModule.ADGROUP]?: SubModuleConfig<SubModule.ADGROUP>;
    [SubModule.KEYWORD]: SubModuleConfig<SubModule.KEYWORD>;
    [SubModule.PRODUCT]?: SubModuleConfig<SubModule.PRODUCT>;
    [SubModule.PLACEMENTS]?: SubModuleConfig<SubModule.PLACEMENTS>;
}

interface NegativeKeywordsSubModule {
    [SubModule.SUGGESTED_NEG_KWS]: SubModuleConfig<SubModule.SUGGESTED_NEG_KWS>;
    [SubModule.EXISTING_NEG_KWS]: SubModuleConfig<SubModule.EXISTING_NEG_KWS>;
}

interface KeywordRecommendationSubModule {
    [SubModule.KEYWORD_MANUAL]: SubModuleConfig<SubModule.KEYWORD_MANUAL>;
    [SubModule.KEYWORD_AUTO]: SubModuleConfig<SubModule.KEYWORD_AUTO>;
    [SubModule.KEYWORD_REVINITI]: SubModuleConfig<SubModule.KEYWORD_REVINITI>;
}

interface CampaignRecommendationSubModule {
    [SubModule.CAMPAIGN_BUDGET]: SubModuleConfig<SubModule.CAMPAIGN_BUDGET>;
    [SubModule.CAMPAIGN_PLACEMENT]: SubModuleConfig<SubModule.CAMPAIGN_PLACEMENT>;
}

interface KASubGrid {
    [SubGrids.KA_KEYWORDS]: SubModuleConfig<SubModule.KEYWORD>;
    [SubGrids.KA_CAMPAIGNS]?: SubModuleConfig<SubModule.PRODUCT>;
    [SubGrids.ADD_AS_EXACT]?: SubModuleConfig<SubModule.PRODUCT>;
}

interface PASubGrid {
    [SubGrids.PA_KEYWORDS]: SubModuleConfig<SubModule.KEYWORD>;
    [SubGrids.PA_ADGROUPS]?: SubModuleConfig<SubModule.PRODUCT>;
}

interface POOverviewGrid {
    [OverviewGrids.TAG_TYPE_AGGREGATE]: SubModuleConfig<SubModule.OVERVIEW>;
}

export type TSubModules =
    | PerformanceOverviewSubModule
    | KeywordRecommendationSubModule
    | CampaignRecommendationSubModule;

export type TSubGrids = KASubGrid | PASubGrid;
export type TOverviewGrids = POOverviewGrid;

export type TCampaignPortfolioList = {
    campaignPortfolioData: {
        value: Campaign[];
    };
};

export interface IButtonLoader {
    exactLoader?: {
        value: boolean;
    };
    negativeLoader?: {
        value: boolean;
    };
}

export interface IAddToExactConfig {
    attribute: string;
    loader: boolean;
    setLoader: ActionCreatorWithOptionalPayload<boolean, string>;
    addFunc: (data: Record<string, TCommonRowObj[]>) => Promise<any>;
    validateFunc: (data: Record<string, TCommonRowObj[]>) => Promise<any>;
    isBidRequired: boolean;
}

export type THandleAddBtnClick = {
    matchType: string;
    addAsExactData: Keyword[];
    addAsExactConfig: Record<string, IAddToExactConfig>;
    bidField?: string;
    show?: boolean;
    setShow?: (show: boolean) => void;
    handleClose?: () => void;
    setAddLoader?: (show: boolean) => void;
    isIndividualUpdate?: boolean;
};

export interface Log {
    id: number;
    log_date: string;
    channel_id: number;
    client_id: number;
    country: string | number;
    module: string;
    change_type: string;
    record_id: string;
    record_type: string;
    attribute: string | null;
    old_value: string | number | null;
    new_value: string | number | null;
    source: string;
    nature: "auto" | "manual";
    source_name: string;
    metainfo: string;
}
export type TBidCellValue = {
    bid: number;
    suggestedMin: number;
    suggestedMax: number;
};

export type TRuleOptionConfig = {
    value: string;
    text: string;
};

export type TRule = { rule: { value: Rule } };
export type TIsEdit = { isEdit: IValueBoolean };

export enum DateActions {
    NO_END_DATE = "no-end-date",
    ADD_DAYS = "add-days",
    SELECT_DATE = "select-date",
}

export type TCustomFilterOption = {
    displayKey: string;
    displayName: string;
    predicate: (filterValues: number[], cellValue: TBidCellValue) => boolean;
    numberOfInputs?: number;
};

export type TCpmModalDisplay = {
    updateCpmModalDisplay: IValueBoolean;
};

export interface IDailyBudgetModalData {
    dailyBudgetModalData: {
        value: Campaign;
    };
}

export type CampaignParams = {
    endDate?: number;
    tillBudgetEnds?: boolean;
    equally_divide_budget?: boolean;
    pacing_enabled?: boolean;
};

export enum BULK_TGT_ACTION {
    ADD_AS_KEYWORD = "addAsKeyword",
    ADD_AS_NEG_KEYWORD = "addAsNegativeKeyword",
}

export type TTag = {
    id: number;
    tag: string;
};

export type TTagType = {
    id: number;
    tag_type: string;
    is_default: boolean;
};

export type TClientTags = {
    tag_type_id?: number;
    tag_type?: string;
    tag_id?: number;
    tag_name?: string;
    record_type?: string;
    tags?: string[];
    tags_map?: string[];
    is_default?: boolean;
};

export type TValidTagRecordTypes =
    | GlobalTagRecordType.CAMPAIGN
    | GlobalTagRecordType.PRODUCT
    | GlobalTagRecordType.GLOBAL;

type TagsConfig = {
    recordId: string;
    recordName: string;
    isAddTagsAllowed: boolean;
    updatePath?: string;
    updateKey?: string;
    updateMethod?: "PUT" | "POST";
    colName?: string;
    recordType?: "campaign" | "product";
    selectedData?:
        | "campaignsSelectionData"
        | "productsSelectionData"
        | "productMappingData";
    gridApi?: "campaignsGridApi";
    isExternalTagging: boolean;
    validRecordTypes: TValidTagRecordTypes[];
};

export type TagModules =
    | SubModule.CAMPAIGN
    | SubModule.KEYWORD
    | ParentModule.PRODUCT_ANALYTICS
    | ParentModule.SETTINGS
    | SubModule.PLACEMENTS;

export type TagsMap = Record<TagModules, TagsConfig>;
export interface IShowDeltaValues {
    showDeltaValues: {
        value: boolean;
    };
}

export type TBudgetUpdateModules =
    | SubModule.CAMPAIGN
    | SubModule.ADGROUP
    | SubModule.KEYWORD
    | SubModule.PLACEMENTS;

type TBudgetUpdateConfig = {
    updateFunc: string;
    updateRecordId: string;
    updateRecordKey: string;
    recordNameKey: string;
    selectedData?: string;
};

export type TBudgetUpdate = Record<TBudgetUpdateModules, TBudgetUpdateConfig>;

export type UtilsModule = {
    [key: string]: any;
};

export type TSubscribeAction = "dailyBudget" | "state";

export type TSubscribeState = Record<
    TSubscribeAction,
    {
        key: keyof TSubscribeActionResponse;
        message: string;
    }
>;

export type TSubscribeActionResponse = {
    campaignStateActionResponse?: IValue;
    dailyBudgetUpdateResponse?: IValue;
};

export type TRemoveEntityModules =
    | ParentModule.PRODUCT_ANALYTICS
    | SubModule.EXISTING_NEG_KWS;

type TRemoveEntityConfig = {
    removeFunc: string;
    recordId: string;
    recordKey: string;
    recordNameKey: string;
    selectedData: string;
};

export type TRemoveEntity = Record<TRemoveEntityModules, TRemoveEntityConfig>;
export type TGoalsConfig = {
    data_level: string;
    search_option: Record<string, string>[];
    metric_level: Record<string, string>;
    metric_level_condition: Record<string, string>[];
    filter_value: Record<string, string[]>;
    time_selection: Record<string, string>[];
};

export type TGoalDataValues = {
    campaignTagsList: string[];
    productTagsList: string[];
    searchTermsList: string[];
};

export interface ICampaigns {
    campaignsData: {
        value: Campaign[];
    };
}

export interface IAdgroups {
    adgroupsData: {
        value: AdGroup[];
    };
}

export interface IKeywords {
    keywordsData: {
        value: Keyword[];
    };
}

export interface IPlacements {
    placementsData: {
        value: Keyword[];
    };
}

export interface IProducts {
    productsData: {
        value: ProductAd[];
    };
}

export interface IPortfolios {
    portfoliosData: {
        value: Portfolio[];
    };
}

export interface IGoals {
    goalData: {
        value: TGoal[];
    };
}

export type GoalDataLevels =
    | "Campaign Tag"
    | "Product Tag"
    | "Portfolio"
    | "Campaign"
    | "Ad Group"
    | "Keyword"
    | "Product"
    | "Search Term"
    | "Account";

export type TGoal = {
    goal_id: number;
    client_id: number;
    channel_id: number;
    goal_name: string;
    goal_time_period: Record<string, string>;
    data_level: string;
    data_level_filter: string;
    data_level_value: string[];
    metric_level: string;
    metric_level_unit: string;
    metric_level_condition: string;
    metric_level_value: number;
    goal_filter: Record<string, string | string[]>[];
    goal_active_status: boolean;
    goal_archvie_status: boolean;
    goal_creation_time: string;
    goal_priority: string;
};

export type TGoalsData = {
    goalActionType: string;
    goalName: string;
    dataLevel: string;
    dataValueFilter: string;
    dataValueInput: string | string[];
    metric: string;
    metricUnit: string;
    metricValueFilter: string;
    metricValueInput: number;
    conditions: {
        conditionLevel: string;
        conditionValues: string[];
    }[];
    timePeriod: {
        periodType: string;
        periodValue:
            | string
            | {
                  startDate: string;
                  endDate: string;
              };
    };
    dateRange?: Dayjs[];
    priority: string;
};

export interface IGoal {
    goalEditConfig: {
        value: TGoal;
    };
}

export type GoalStatusConfig = {
    text: string;
    color: string;
};

export enum GoalStatus {
    ACHIEVED = "achieved",
    UNACHIEVED = "unachieved",
}

export type TGoalStatus = {
    [key in GoalStatus]: [];
};

interface BrandScoreInfo {
    [key: string]: {
        name: string;
        value: string;
        percChange?: string;
        link?: string;
    };
}

interface Overview {
    sku_title?: string;
    sku?: string;
    skuLink?: string;
    category?: string;
    product_img?: string;
    CompanyName?: string;
    mrp?: string;
    price?: string;
    discount?: string;
    availability?: boolean;
    rating?: number;
    rating_count?: string;
    out_of_stock_since?: string;
    brandScoreInfo?: BrandScoreInfo;
    buyBoxWinner?: string;
    categoryRank?: number | string;
    subCategory?: string;
}

interface ICategoryInfoWidget {
    msContribution: number | string;
}

export interface ProductTrendAnalysis {
    [key: string]: {
        titles: string[];
        chartOptions: {
            [key: string]: {
                title: {
                    text: string;
                };
                series: {
                    name: string;
                    data: (string | number)[];
                }[];
            };
        };
    };
}

export interface IReviews {
    summary?: string;
    rating?: string | number;
    rating_count?: string | number;
    top_pos?: string;
    top_neg?: string;
    review_date?: string;
    review_count?: number;
    themes?: {
        [key: string]: string;
    };
    ratingDistribution?: {
        [key: string]: number;
    };
}

export interface IPaReportWidgetData {
    key?: string;
    productImage?: string;
    productName?: string;
    bsr?: string;
    availability?: number;
    paidSOV?: number;
    organicSOV?: number;
    avgPrice?: number;
    avgDiscount?: number;
    priceRange?: string;
    discountRange?: string;
    rating?: number;
    ratingsCount?: number;
    reviewsCount?: number;
    sku?: string;
    skuLink?: string;
    bsrCategory?: string;
}
export interface IDeepDiveModalConfig {
    overview?: Overview;
    funnel?: TFunnelResponse;
    reviews?: IReviews;
    digitalShelf?: Array<IPaReportWidgetData>;
    productTrendAnalysis?: ProductTrendAnalysis;
    spendRoasData?: {
        chartDataSeries: {
            [key: string]: {
                categories: string[];
                series: {
                    name: string;
                    data: number[];
                    color: string;
                }[];
            };
        };
        delta: {
            [key: string]: {
                roas: number;
                spend: number;
            };
        };
    };
}

export interface ICategoryOverviewConfig {
    categoryInfoWidgetData?: ICategoryInfoWidget;
}

export type TUserPermissionState = {
    [category: string]: {
        [permission: string]: {
            [role: string]: boolean;
        };
    };
};

export interface ICountryData {
    name: string;
    alpha2_code: string;
    country_locale: string[];
    currency_code: string;
    currency_name: string;
}

export interface CustomOptionType {
    key: string;
    value: number;
    children: Record<string, any>;
}

export interface ITagTypeAggregate extends PerformanceMetrics {
    tag: string;
    tag_type: string;
}

export type TAddTagType = {
    recordTypes: string[];
    tagType: string;
};

export type TUpdateTagMapping = {
    tagType?: string;
    tag?: string; // when a single tag is deleted
    recordType?: string;
    recordTypes?: string[];
    tags?: string[]; // when tag type is deleted
    tagTypeId?: number;
    tagId?: number;
};

export type TValidateTagRemoveResponse = {
    message: string;
    status: boolean;
    isTagPresent: boolean;
};

export type ColorConfig = {
    fill: string;
    border: string;
    background: string;
};

export type TProductMappingData = {
    productMappingData?: ICommonRowDataRedux;
    productMappingSelectionData?: ICommonRowDataRedux;
};

export type TProductMappingFormValues = {
    competitors: string[];
    power_sku: boolean;
    sku_identifier: string[];
    tags: string[];
    category: string;
    subcategory: string;
    brand: string;
};

export type TProductMappingUpdateConfig = {
    isBulkRequest: boolean;
    tags?: string[];
    sku_identifier?: string;
    competition?: string[];
    skus?: (string | number)[] | { sku: string | number; channel: number }[];
    skuId?: (string | number)[] | { sku: string | number; channel: number }[];
    power_sku?: boolean;
    compliance_price?: number;
    filterModel?: Record<string, any>;
    isFilteredRequest?: boolean;
    action?: "add" | "remove";
};

export type TUpdateParamsBulkProductMapping = Partial<{
    catId: string | number;
    subcatId: string | number;
    brandId: string | number;
    tags: string[];
    isPowerSku: boolean;
    crossChannelProductId: string;
    compliancePrice: number;
}>;

export type TUpdateConfigBulkProductMapping = {
    sku_channel: {
        channel: number;
        sku: string | number;
    }[];
    update_params: TUpdateParamsBulkProductMapping;
};

export type TSkuCompOption = {
    id: string;
    comp_title: string;
};

export type CustomTagProps = {
    label: React.ReactNode;
    value: any;
    disabled: boolean;
    onClose: (event?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    closable: boolean;
};

export type TOmmitedSelectValue = {
    key?: React.Key;
    value?: string | number;
    label?: React.ReactNode;
    title?: string | number | React.ReactNode;
    disabled?: boolean;
};

export interface IChannelwiseRatingsAndReviewsWidgetData {
    channel_name: string;
    review_percentage: number;
    review_delta: number;
    rating_percentage: number;
    rating_delta: number;
    rating: number;
}

export interface IReviewVerbatimOriginalData {
    review_id?: string;
    title?: string;
    review_title?: string;
    review_content?: string;
    channel?: string;
    name?: string;
    review_date?: string;
    found_helpful?: string;
    rating?: number;
    about_driver?: string;
}

export interface ISentimentOriginalData {
    theme?: string;
    theme_postitive?: string;
    theme_negative?: string;
}

export interface ISentimentDataSubFormat {
    comment: string;
    category: string;
}

export interface ISentimentWidgetData {
    positiveSentiments: ISentimentDataSubFormat[];
    negativeSentiments: ISentimentDataSubFormat[];
}

export type SkuFiltersData = {
    asin: string;
    sku_tags: string[];
    sku_title: string;
    tag_types: string[];
};

export type SkuFiltersOptions = {
    categories: { value: string; label: string }[];
    subCategories: { value: string; label: string }[];
    skus: { value: string; label: string }[];
};

export interface ICustomerSatisfactionOriginalData {
    sentiment?: string;
    satisfied?: string;
    neutral?: string;
    sentiment_count?: string;
    satisfied_count?: string;
    dissatisfied_count?: string;
    neutral_count?: string;
    dissatisfied?: string;
    overall?: string;
    "nps score"?: string;
    sub_category?: string;
}

export interface ICustomerSatisfactionWidgetData {
    chartDataSeries: {
        categories: string[];
        series: {
            name: string;
            data: number[];
            color: string;
        }[];
    };
    npsData: number[];
}

export interface IDriverwiseReviewTrendOriginalData {
    review_date: string;
    review_trend: {
        sentiment: string;
        avg_rating: number;
    }[];
    sub_category?: string;
}

export interface IDriverwiseReviewTrendWidgetData {
    name: string;
    data: [string, number][];
}

export interface IRatingsWidget {
    widgetData: IReviews;
    type?: string;
}

export interface ITrendsComparisionWidgetData {
    chartDataSeries: {
        categories: string[];
        series: {
            name: string;
            data: number[];
        }[];
    };
}

export interface ITrendsComparision {
    widgetData: ITrendsComparisionWidgetData;
    title: string;
    isTitleRequired?: boolean;
    isCardRequired?: boolean;
    maxWidth?: number;
    graphAttributes?: Record<string, any>;
}

export interface IChannelwiseRatingsAndReviewsWidget {
    title: string;
    widgetData: Array<IChannelwiseRatingsAndReviewsWidgetData>;
}

export interface IReviewsDataFormatted {
    ratings?: IReviews;
    trends?: ITrendsComparisionWidgetData;
    channelwiseRatingsAndReviews?: Array<IChannelwiseRatingsAndReviewsWidgetData>;
    reviewsAndVerbatim?: Array<IReviewVerbatimOriginalData>;
    sentimentAnalysis?: ISentimentWidgetData;
    customerSatisfactionDriver?: ICustomerSatisfactionWidgetData;
    driverwiseReviewTrend?: IDriverwiseReviewTrendWidgetData[];
    productAnalysis?: Array<IProductAnalysisWidgetData>;
    csDriversTrendsFeatures?: any[];
}

export interface ICategoryData {
    tag_type: string;
    tags: string[];
}

export interface ICategoriesOptions {
    categories: SelectOptions[];
    subCategories: SelectOptions[];
}

export interface ISkusData {
    sku_tags: string[];
    sku: string;
    sku_title: string;
}

export type ProductAnalysisOriginalData = {
    key: string;
    product_image?: string;
    product_name?: string;
    bsr?: string;
    avg_price?: number;
    price_range?: string;
    discount_range?: string;
    rating?: number;
    ratings_count?: number;
    reviews_count?: number;
    sku?: string;
    sku_link?: string;
    bsr_category?: string;
};

export type IProductAnalysisWidgetData = {
    key?: string;
    productImage?: string;
    productName?: string;
    bsr?: string;
    avgPrice?: number;
    avgDiscount?: number;
    priceRange?: string;
    discountRange?: string;
    rating?: number;
    ratingsCount?: number;
    reviewsCount?: number;
    sku?: string;
    skuLink?: string;
    bsrCategory?: string;
    availability?: number;
    paidSOV?: number;
    organicSOV?: number;
};

export interface ITop10SubCatsByRatingsWidgetData {
    customerSatisfactionDriver: ICustomerSatisfactionOriginalData[];
    driverwiseReviewTrend: IDriverwiseReviewTrendOriginalData[];
    subcategoriesByratings: ITop10SubCatsByRatingsDataOriginalData[];
    subcategoriesByratingsAndDate: ITop10SubCatsByRatingsOriginalData[];
    brandwiseOverallTrend: IBrandwiseOverallTrendsOriginalData[];
    satisfactionDriverwise: ISatisfactionDriverwiseOriginalData[];
}

export interface ITop10SubCatsByRatingsDataOriginalData {
    sub_category?: string | null;
    channel?: string | null;
    star_rating?: number | null;
    "#rating"?: number | null;
    "#reviews"?: number | null;
    "nps score"?: number | null;
    liked_features?: string[] | null;
    disliked_features?: string[] | null;
    date?: string | null;
    subcategoryId?: string | null;
    label?: string | null;
}

export interface ITop10SubCatsByRatingsDataFormattedData {
    label?: string;
    subcategoryId?: string;
    subcategory?: string;
    channel?: string;
    rating?: number;
    rating_count?: number;
    review_count?: number;
    nps?: number;
    features?: {
        liked_features?: string[] | null;
        disliked_features?: string[] | null;
    };
}

export interface ITop10SubCatsByRatingsOriginalData {
    review_date?: string;
    sub_category?: string;
    rating_count?: number;
}

export interface ITop10SubCatsByRatingsSubcatData {
    subcategoryId?: string;
    label?: string;
    ratingScore?: number;
}

export interface ITop10SubCatsByRatingsMasterData {
    date: string;
    values: ITop10SubCatsByRatingsSubcatData[];
}

export interface IAllChartData {
    categories?: string[];
    series?: ITop10SubCatsByRatingsSubcatData[] | any[];
}

export interface IBrandwiseOverallTrendsOriginalData {
    review_date?: string;
    brand?: string;
    rating?: number;
    rating_count?: number;
    sub_category?: string;
}

export interface IBrandwiseData {
    name: string;
    data: number[];
}

export interface IBrandwiseOverallTrendsWidgetData {
    categories: string[];
    ratingsData: IBrandwiseData[];
    numberOfRatingsData: IBrandwiseData[];
}

export interface ISatisfactionDriverwiseOriginalData {
    sub_category: string;
    data: {
        avg_rating: number;
        brand: string;
        theme: string;
    }[];
}

export interface ISatisfactionDriverwiseFormattedData {
    company: string;
    sentiments: {
        sentiment: string;
        rating: number;
        size: number;
    }[];
}

export interface IChannelwiseRatingsAndReviewsWidgetData {
    channel_name: string;
    review_percentage: number;
    review_delta: number;
    rating_percentage: number;
    rating_delta: number;
    rating: number;
}

export interface IReviewVerbatimOriginalData {
    review_id?: string;
    title?: string;
    review_title?: string;
    review_content?: string;
    channel?: string;
    name?: string;
    review_date?: string;
    found_helpful?: string;
    rating?: number;
    about_driver?: string;
}

export interface ISentimentOriginalData {
    theme?: string;
    theme_postitive?: string;
    theme_negative?: string;
}

export interface ISentimentDataSubFormat {
    comment: string;
    category: string;
}

export interface ISentimentWidgetData {
    positiveSentiments: ISentimentDataSubFormat[];
    negativeSentiments: ISentimentDataSubFormat[];
}

export interface ICustomerSatisfactionOriginalData {
    sentiment?: string;
    satisfied?: string;
    neutral?: string;
    dissatisfied?: string;
    overall?: string;
    "nps score"?: string;
    nps_score?: string;
    sub_category?: string;
}

export interface ICustomerSatisfactionWidgetData {
    chartDataSeries: {
        categories: string[];
        series: {
            name: string;
            data: number[];
            color: string;
        }[];
    };
    npsData: number[];
}

export interface IDriverwiseReviewTrendOriginalData {
    review_date: string;
    review_trend: {
        sentiment: string;
        avg_rating: number;
    }[];
    sub_category?: string;
}

export interface IDriverwiseReviewTrendWidgetData {
    name: string;
    data: [string, number][];
}

export interface IRatingsWidget {
    widgetData: IReviews;
}

export interface ITrendsComparisionWidgetData {
    chartDataSeries: {
        categories: string[];
        series: {
            name: string;
            data: number[];
        }[];
    };
}

export interface ITrendsComparision {
    widgetData: ITrendsComparisionWidgetData;
    title: string;
}

export interface IChannelwiseRatingsAndReviewsWidget {
    title: string;
    widgetData: Array<IChannelwiseRatingsAndReviewsWidgetData>;
}

export interface IReviewsDataFormatted {
    ratings?: IReviews;
    trends?: ITrendsComparisionWidgetData;
    channelwiseRatingsAndReviews?: Array<IChannelwiseRatingsAndReviewsWidgetData>;
    reviewsAndVerbatim?: Array<IReviewVerbatimOriginalData>;
    sentimentAnalysis?: ISentimentWidgetData;
    customerSatisfactionDriver?: ICustomerSatisfactionWidgetData;
    driverwiseReviewTrend?: IDriverwiseReviewTrendWidgetData[];
    productAnalysis?: Array<IProductAnalysisWidgetData>;
}

export interface ICategoryData {
    tag_type: string;
    tags: string[];
}

export interface ICategoriesOptions {
    categories: SelectOptions[];
    subCategories: SelectOptions[];
}

export interface ISkusData {
    sku_tags: string[];
    sku: string;
    sku_title: string;
}

export interface ITop10SubCatsByRatingsWidgetData {
    customerSatisfactionDriver: ICustomerSatisfactionOriginalData[];
    driverwiseReviewTrend: IDriverwiseReviewTrendOriginalData[];
    subcategoriesByratings: ITop10SubCatsByRatingsDataOriginalData[];
    subcategoriesByratingsAndDate: ITop10SubCatsByRatingsOriginalData[];
    brandwiseOverallTrend: IBrandwiseOverallTrendsOriginalData[];
    satisfactionDriverwise: ISatisfactionDriverwiseOriginalData[];
}

export interface ITop10SubCatsByRatingsDataOriginalData {
    sub_category?: string | null;
    channel?: string | null;
    star_rating?: number | null;
    "#rating"?: number | null;
    "#reviews"?: number | null;
    "nps score"?: number | null;
    liked_features?: string[] | null;
    disliked_features?: string[] | null;
    date?: string | null;
    subcategoryId?: string | null;
    label?: string | null;
}

export interface ITop10SubCatsByRatingsDataFormattedData {
    label?: string;
    subcategoryId?: string;
    subcategory?: string;
    channel?: string;
    rating?: number;
    rating_count?: number;
    review_count?: number;
    nps?: number;
    features?: {
        liked_features?: string[] | null;
        disliked_features?: string[] | null;
    };
}

export interface ITop10SubCatsByRatingsOriginalData {
    review_date?: string;
    sub_category?: string;
    rating_count?: number;
}

export interface ITop10SubCatsByRatingsSubcatData {
    subcategoryId?: string;
    label?: string;
    ratingScore?: number;
}

export interface ITop10SubCatsByRatingsMasterData {
    date: string;
    values: ITop10SubCatsByRatingsSubcatData[];
}

export interface IAllChartData {
    categories?: string[];
    series?: ITop10SubCatsByRatingsSubcatData[] | any[];
}

export interface IBrandwiseOverallTrendsOriginalData {
    review_date?: string;
    brand?: string;
    rating?: number;
    rating_count?: number;
    sub_category?: string;
}

export interface IBrandwiseData {
    name: string;
    data: number[];
}

export interface IBrandwiseOverallTrendsWidgetData {
    categories: string[];
    ratingsData: IBrandwiseData[];
    numberOfRatingsData: IBrandwiseData[];
}

export interface ISatisfactionDriverwiseOriginalData {
    sub_category: string;
    data: {
        avg_rating: number;
        brand: string;
        theme: string;
    }[];
}

export interface ISatisfactionDriverwiseFormattedData {
    company: string;
    sentiments: {
        sentiment: string;
        rating: number;
        size: number;
    }[];
}

export interface IPlatformAccess {
    platform: string[];
    client_id: string;
}

export enum Platforms {
    PLANNING = "planning",
    ACTIVATION = "activation",
    MEASUREMENT = "measurement",
    CHATBOT = "chatbot",
}
export interface ICerbosResource {
    id: string;
    kind: string;
}
export type TCerbosPrincipal = { id: string; roles: Array<string> };
export type TCerbosResources = Array<{
    resource: ICerbosResource;
    actions: Array<string>;
}>;
export type TCerbosAction = string;

export interface ICerbosData {
    cerbosCallId: string;
    requestId: string;
    results: Array<object>;
    isAllowed?: (params: {
        action: string;
        resource: ICerbosResource;
    }) => boolean;
}
export interface IClientAccess {
    client_id: number;
    client_name: string;
}

export interface IUserChannelAccess {
    logo_url: string;
    client_id: number;
    channel_id: number;
    name: string;
}

export interface IAccess {
    clients: IClientAccess[];
    role_id: number;
    channels: IUserChannelAccess[];
    platforms: string[];
    role_slug: string;
    role_name: string;
    privileges: string[];
    is_internal: boolean;
}

export interface IUserRecord {
    user_id: number;
    uid: string;
    first_name: string;
    last_name: string;
    email: string;
    last_login: string | null;
    created_at: string;
    created_by: string;
    created_by_name?: string;
    updated_at: string | null;
    updated_by: string | null;
    updated_by_name?: string;
    status: boolean;
    country: string;
    timezone: string;
    phone: string | null;
    default_client: number;
    is_archived: boolean;
    is_super_admin: boolean;
    access: IAccess[];
    expiry_date: string | null;
}

export enum Roles {
    READONLY = "read_only",
}
export interface IStatusBadgeCellRenderer extends ICellRendererParams {
    pinnedColumnValue: Record<string, string | number>;
    isBadgeRequired: boolean;
    isTypeBadgeRequired: boolean;
    deepDiveGridType?: string | null;
}

export interface IReviewsOriginalData {
    rating: number;
    rating_count: number;
    star_1: number;
    star_2: number;
    star_3: number;
    star_4: number;
    star_5: number;
}

export interface IReviewTrendsOriginalData {
    channel: string;
    review: IReviews[];
}

export interface IChannelData {
    channel: string;
    curr_review: number;
    prev_review: number;
    delta_review: number;
    curr_rating: number;
    prev_rating: number;
    delta_rating: number;
    star_rating: number;
}

export interface IFunnelStructuredData extends TFunnelResponse {
    chartDataSeries?: any;
    delta?: any;
}

export interface IDeepdiveOverviewFetchParams {
    setLoadingState: any;
    setCampaignsLoading: Dispatch<SetStateAction<boolean>>;
    setSelectedSKU: Dispatch<SetStateAction<string>>;
    selectedSKU: string;
    setRowData: Dispatch<SetStateAction<TCommonRowData>>;
    setModuleData: Dispatch<SetStateAction<IDeepDiveModalConfig>>;
    date: TDate;
}

export type RatingInput =
    | {
          current_rating: number;
          current_rating_count: number;
          current_review_count: number;
          star1: number;
          star2: number;
          star3: number;
          star4: number;
          star5: number;
          prev_rating: number;
          prev_rating_count: number;
          prev_review_count: number;
      }
    | null
    | undefined;

export interface IRatingSummaryData {
    rating: {
        value: number;
        change: number;
    };
    numRatings: {
        value: number;
        change: number;
        velocity: number;
    };
    reviews: {
        total: number;
        change: number;
        averageRating: number;
        ratingBars: { label: string; percentage: number }[];
    };
    ratings: {
        rating: number;
        rating_count: number;
        ratingDistribution: Record<string, number>;
    };
}

export interface ISummaryWidgetConfig {
    key: string;
    title: string;
    valueKey: string;
    changeKey: string;
    additionalInfo?: (data: any) => React.ReactNode;
    popoverContent?: (data: any) => React.ReactNode;
    onClickMore?: () => void;
    onClickMoreTitle?: string;
}

export interface ISummaryWidgetProps {
    widgetData: Partial<IRatingSummaryData>;
    onClickMoreHandler?: (type: string) => void;
    activeTab: string;
    moduleType?: string;
}

export enum SentimentType {
    POSITIVE = "positive",
    NEGATIVE = "negative",
    NEUTRAL = "neutral",
}

export type TSentimentData =
    | {
          sentiment: string;
          sentiment_type: SentimentType;
          curr_nps_score: number | null;
          prev_nps_score: number | null;
          curr_count: number | null;
          prev_count: number | null;
      }
    | null
    | undefined;

export interface ISatisfactionDriver {
    name: string;
    score: number;
    change: number;
}

export interface IWordcloudReviewData {
    sentiment: string;
    sentiment_type: SentimentType;
}

export interface IWordcloudReviewElementsData {
    name: string;
    weight?: number;
    size?: number;
}

export interface IWordcloudReviewWidgetData {
    positiveReviewData: IWordcloudReviewElementsData[];
    negativeReviewData: IWordcloudReviewElementsData[];
}

export type TTrendsWithComparisionInputData = {
    review_date: string | null;
    review_count: number | null;
    rating: number | null;
    date: string | null;
    price: number | null;
    deal: number | null;
    per_day_rating: number | null;
    crawl_date: string | null;
    current_organic_visibility: number | null;
    current_paid_visibility: number | null;
    overall_visibility: number | null;
};

export type TTrendsWithComparisionData =
    | Partial<TTrendsWithComparisionInputData>
    | null
    | undefined;

export interface ITrendsWithComparisionWidgetFormatterKeys {
    categoryKey: "review_date" | "date" | "crawl_date";
    yAxisKey: "review_count" | "price" | "current_paid_visibility";
    yAxisOppositeKey: "rating" | "deal" | "current_organic_visibility";
    yAxisOppositeKeyOptional?: "per_day_rating" | "overall_visibility";
}

export interface ITrendsWithCompareModalWidgetData {
    categories: string[];
    yAxisData: number[];
    yAxisOppositeData?: number[];
    yAxisOppositeDataOptional?: number[];
    statusData?: string[];
}

export interface TReviewDrawerTitleState {
    "all-review": string;
    "satisfaction-driver": string;
    "cross-channel": string;
}

export type TReviewDrawerTitleAction =
    | { type: "all-review"; payload: string }
    | { type: "satisfaction-driver"; payload: string }
    | { type: "cross-channel"; payload: string };

export type TProductReviewData = {
    review_id: string;
    review_title?: string;
    content?: string;
    channel_id: number;
    sentiment_type?: string;
    sentiment_score?: number | string;
    review_date?: string;
    rating?: number;
    found_useful?: boolean | number;
    about_driver?: string;
    phrase_value?: string;
};

export type TProductReviewTableDataEntry = {
    review_id: string;
    review_title: string;
    content: string;
    channel: string;
    sentiment_type: string;
    sentiment_score?: number | string;
    review_date: string;
    rating: number;
    found_useful: boolean;
    about_driver: string[];
};

export type TProductReviewExpandedTableDataEntry = {
    review_id: string;
    sentiment_type: string;
    sentiment_score: number | string;
    review_date: string;
    theme: string;
    verbatim: string;
};

export type IReviewsAndVerbatimTableEntry = TProductReviewTableDataEntry &
    TProductReviewExpandedTableDataEntry & { key?: React.Key };

export type TProductReviewTableExpandedRow =
    | {
          details: TProductReviewExpandedTableDataEntry[];
      }
    | Record<string, any>;

export interface IReviewsAndVerbatimTableWidget {
    widgetData: IAntTableData | any;
}

export type TProductReviewFormattedTableData = {
    tableData: TProductReviewTableDataEntry[];
    expandedTableData: Record<string, TProductReviewExpandedTableDataEntry[]>;
};

export interface ISatisfactionDriversOriginalData {
    review_id: string;
    review_date: string;
    sentiment_score: number;
    channel_id: number;
    sentiment: string;
    phrase_value: string;
    rating: number;
    sentiment_type: string;
}

export interface ISatisfactionDriversFormattedData {
    key: string;
    review_id: string;
    phrase: string;
    channel: string;
    sentiment: string;
    date_published: string;
    rating: number;
    about_driver: string;
}

export interface TProductRatingVsCompetitorsData {
    categories: string[];
    series: {
        name: string;
        data: { y: number }[];
    }[];
}

export type TProductRatingVsCompetitorsOriginalData = (
    data: {
        rating: number;
        rating_count: number;
        sku: string;
    }[],
    selectedSku: string
) => TProductRatingVsCompetitorsData;

export interface IProductRatingVsCompetitorsData {
    category: string;
    sku: string;
    theme: string;
    rating?: number;
}

export interface IProductRatingVsCompetitorsSpiderChartData {
    Features: string[];
    "My SKU Score"?: number[];
    "Category Average"?: number[];
    [sku: string]: string[] | number[] | undefined;
}

export interface TPaModuleLoadingState {
    [ProductDeepdiveModules.RATINGS_REVIEWS]: boolean;
    [ProductDeepdiveModules.OVERVIEW]: boolean;
    [ProductDeepdiveModules.PRICING]: boolean;
    [ProductDeepdiveModules.VISIBILITY]: boolean;
    [ProductDeepdiveModules.AVAILABILITY]: boolean;
}

export type TPaModuleReducerActions =
    | { type: "RESET"; payload: TPaModuleLoadingState }
    | { type: ProductDeepdiveModules.RATINGS_REVIEWS; payload: boolean }
    | { type: ProductDeepdiveModules.OVERVIEW; payload: boolean }
    | { type: ProductDeepdiveModules.PRICING; payload: boolean }
    | { type: ProductDeepdiveModules.VISIBILITY; payload: boolean };

export interface IProductAnalysisModule {
    selectedSKU: string;
    setSelectedSKU: React.Dispatch<React.SetStateAction<string>>;
    defaultActiveModule?: string;
    categoryFiltersLoading?: boolean;
}

export interface IAntTableData {
    headers: string[];
    rows: Array<{
        [key: string]: string | number;
    }>;
}

export interface IAllReviewsWidget {
    title: string;
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    widgetData: {
        chart: ITrendsComparisionWidgetData;
        table: IAntTableData;
    };
    activeReviewTab: string;
    setActiveReviewTab: React.Dispatch<React.SetStateAction<string>>;
}

export type WordCloudData = {
    name: string;
    weight: number;
}[];

export interface WordCloudsWidgetProps {
    title?: string;
    type: SentimentType.POSITIVE | SentimentType.NEGATIVE;
    widgetData: WordCloudData;
    onClickMoreHandler: (
        type: SentimentType.POSITIVE | SentimentType.NEGATIVE
    ) => void;
}

export type TCrossChannelTableData = {
    [key: string]: string | number;
    metrics?: string;
};

export type TCrossChannelWidgetData = {
    tableData: TCrossChannelTableData[];
    expandedTableData: TCrossChannelTableData[];
    charts?: any;
};

export interface ICrossChannelRatingAndReviewTableWidget {
    widgetData: TCrossChannelWidgetData;
    expandLabel?: string;
    showMoreButtonRequired?: boolean;
}

export interface ICrossChannelRatingAndReviewWidget {
    title: string;
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    widgetData: TCrossChannelWidgetData;
    selectedComparisionChannel: string[];
    setSelectedComparisionChannel?: React.Dispatch<
        React.SetStateAction<string[]>
    >;
    comparisionSelectOptions: Array<{
        label: string;
        value: string;
    }>;
    onChannelComparisionChange: (value: string[]) => void;
    comparisionSelectDataType: string;
    setComparisionSelectDataType: React.Dispatch<React.SetStateAction<string>>;
}

export interface IDriverDataItem {
    name: string;
    score: number;
    change: number;
}

export interface ISatisfactionDriversTableWidget {
    chart: ITrendsComparisionWidgetData;
    table: any;
}

export interface ISatisfactionDriverData {
    positives?: IDriverDataItem[];
    negatives?: IDriverDataItem[];
    [key: string]: IDriverDataItem[];
}

export interface ISatisfactionDriversWidgetProps {
    title: string;
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    driverData: ISatisfactionDriverData;
    widgetData: ISatisfactionDriversTableWidget;
    driverFilterOptions: {
        label: string;
        value: string;
    }[];
    activeDriversTab: string;
    setActiveDriversTab: React.Dispatch<React.SetStateAction<string>>;
    selectedDriver: string[];
    onDriversFilterChange: (value: string[]) => void;
}

export interface ISatisfactionDriversTableWidgetData {
    widgetData: IAntTableData[];
}

export interface IProductInfoWidgetProps {
    widgetData: IDeepDiveModalConfig;
}

export interface ICategoryInfoWidgetProps {
    widgetData: ICategoryInfoWidget;
    selectedCategory: string[];
    selectedSubCategory: string[];
    selectedBrand: string[];
}

export interface SatisfactionDriver {
    name: string;
    score: number;
    change: number;
}

export interface AISummary {
    review: string;
    rating: string;
    ranking: string;
}

export interface SatisfactionDriversSummaryWidgetProps {
    moduleType: string;
    widgetData: {
        positives: SatisfactionDriver[];
        negatives: SatisfactionDriver[];
        aiSummary: AISummary;
    };
    onDriverClickHandler?: (type: string, driver: string) => void;
}

export interface ISatisfactionDriversSummaryProps {
    columns: {
        title: string;
        dataIndex: string;
        key: string;
        width: string;
        onHeaderCell?: () => { style: React.CSSProperties };
        render?: (text: any, record?: any) => React.ReactNode;
    }[];
    dataSource: {
        name: string;
        score: number;
        change: number;
    }[];
}

export interface IProductRatingVsCompetitorsWidgetProps {
    title?: string;
    widgetData:
        | {
              trendChart: {
                  categories: string[];
                  series: { name: string; data: number[] }[];
              };
              spiderChart: {
                  Features: number[];
                  "Category Average": number[];
                  [sku: string]: number[];
              };
          }
        | Record<string, any>;
    selectedRatingFilter: string[];
    onProductRatingVsCompetitorsFilterChange: (value: string[]) => void;
    productRatingVsCompetitorsFilterOptions: { label: string; value: string }[];
}

export interface ISatisfactionDriversPopup {
    review_id: string;
    review_date: string;
    sentiment_score: number;
    channel_id: number;
    phrase_value: string;
    rating: number;
    sentiment: string;
    sentiment_type: "positive" | "negative";
}

export interface ICustomerSatisfactionDriver {
    sentiment: string;
    satisfied: number;
    neutral: number;
    dissatisfied: number;
    overall: number;
    satisfied_count: number;
    neutral_count: number;
    dissatisfied_count: number;
    nps_score: number;
}

export interface IDriverwiseReviewTrend {
    theme: string;
    review_date: string;
    rating: number;
}

export interface IProductRatings {
    rating: number;
    rating_count: number;
    sku: string;
}

export interface IProductFeatureRatings {
    theme: string;
    category: string;
    sku: string;
    rating: number;
}

export interface ICrossChannelDataWithFeatures {
    sentiment: string;
    channel_id?: number;
    channel?: string;
    curr_nps_score: number;
    pre_nps_score: number;
}

export interface ICrossChannelData {
    current_rating: number;
    current_reviews: number;
    current_ratings: number;
    previous_rating: number;
    previous_reviews: number;
    previous_ratings: number;
    current_nps: number;
    previous_nps: number;
    channel_id?: number;
    channel?: string;
}

export interface ICrossChannelTrendsRatingAndReviews {
    review_date: string;
    "#reviews"?: number;
    "#ratings"?: number;
    rating: number;
    channel_id: number;
}

export interface IReviewsSkuData {
    ratingSummaryData: IRatingSummaryData;
    positiveReviewData: IWordcloudReviewWidgetData;
    negativeReviewData: IWordcloudReviewWidgetData;
    ratingsReviewsTrend: ITrendsWithCompareModalWidgetData;
    satisfactionDriversSummaryData: {
        positives: ISatisfactionDriver[];
        negatives: ISatisfactionDriver[];
        aiSummary: AISummary;
    };
    satisfactionDriversPopup: ISatisfactionDriversPopup[];
    customerSatisfactionDriver: ICustomerSatisfactionDriver[];
    driverwiseReviewTrend: IDriverwiseReviewTrend[];
    csDriversTrendsFeatures: ICustomerSatisfactionDriver[];
    allReviewData: {
        chart: ITrendsComparisionWidgetData;
        table: TProductReviewFormattedTableData;
    };
    crossChannelTrends: {
        reviews: ICrossChannelTrendsRatingAndReviews[];
        ratings: ICrossChannelTrendsRatingAndReviews[];
    };
    crossChannelData: ICrossChannelData[];
    crossChannelDataWithFeatures: ICrossChannelDataWithFeatures[];
    productRatingVsCompetitors: {
        productRatings: IProductRatings[];
        productFeatureRatings: IProductFeatureRatings[];
    };
}

export type BuyboxDataItem = {
    crawl_date: string;
    buybox_win: boolean;
    merchant_name: string[];
};

export type BuyboxData = BuyboxDataItem[];

export interface ICompiledRoasSpendDelta {
    roas?: number;
    spend?: number;
}

export interface ICompiledRoasSpendDataItem extends ICompiledRoasSpendDelta {
    type?: string;
    sale?: number;
    spends?: number;
}

export interface ICompiledRoasSpendData {
    currentData: ICompiledRoasSpendDataItem[];
    previousData: ICompiledRoasSpendDataItem[];
}

export type ProductTrendDataItem = ICompiledRoasSpendDataItem & {
    crawl_date?: string;
    availability?: boolean;
    price?: number;
    org_sku_wt_crawl_sv?: number;
    org_cat_wt_crawl_sv?: number;
    paid_sku_wt_crawl_sv?: number;
    paid_cat_wt_crawl_sv?: number;
    date?: string;
    daily_trend_sale?: number;
    program_type?: string;
    spend_change?: number;
    roas_change?: number;
    match_type?: "broad" | "phrase" | "exact";
    revenue?: number;
    cat_revenue?: number;
    bsr_rank_2?: number;
    bsr_category_2?: string;
    percentage?: number | null;
    [key: string]: any;
};

export type ProductTrendData = ProductTrendDataItem[];

export interface CompiledDataDigitalShelf {
    [key: string]: {
        sku?: string;
        bsr?: number;
        paid_sov?: number[];
        organic_sov?: number[];
        prices?: number[];
        discounts?: number[];
        ratings?: number[];
        rating_counts?: number[];
        availabilities?: number[];
        productName?: string;
        dates?: string[];
        key?: string;
        productImage?: string;
        avgPrice?: string;
        avgDiscount?: string;
        priceRange?: string;
        discountRange?: string;
        rating?: string;
        ratingsCount?: number;
        availability?: number;
        paidSOV?: number;
        organicSOV?: number;
        skuLink?: string;
        bsrCategory?: string;
    };
}

export interface IDigitalShelfData {
    digitalShelfData: CompiledDataDigitalShelf[];
    maxRatingCount: number;
}

export type TOverviewStructuredDataObj =
    | BuyboxData
    | ProductTrendData
    | RatingInput
    | IDigitalShelfData
    | ICompiledRoasSpendData;

export interface ISelectedPeriodAttr {
    totalSpend: string;
    totalROAS: string;
    averageSpendChange: string;
    averageROASChange: string;
}

export interface ISelectedPeriodData {
    Broad: ISelectedPeriodAttr;
    Phrase: ISelectedPeriodAttr;
    Exact: ISelectedPeriodAttr;
}

export interface ICompiledRoasSpendDataFromKeywords {
    chartDataSeries: any;
    delta: Record<string, { roas: number; spend: string }>;
}

export interface IDriverwiseReviewTrendData {
    review_date: string;
    theme: string;
    rating: number;
}

export interface IGroupedDriverwiseReviewTrendData {
    review_date: string;
    review_trend: {
        sentiment: string;
        avg_rating: number;
    }[];
}

export interface ICommonWidgetModules {
    loadingState: any;
    selectedSKU: string;
    pricingData: any;
    channel: number;
    currencySymbol: string;
}

export interface IProductAnalysisRatingsAndReviewsModule
    extends Partial<ICommonWidgetModules> {
    reviewsData: any;
}

export interface IProductAnalysisPricingModule
    extends Partial<ICommonWidgetModules> {
    pricingData: any;
}

export interface IProductAnalysisVisibilityModule
    extends Partial<ICommonWidgetModules> {
    visibilityData: any;
}

export interface IProductAnalysisAvailabilityModule {
    loadingState: any;
    selectedSKU: string;
    availabilityData: any;
    channel: number;
    currencySymbol: string;
}

export enum ReportFilters {
    CATEGORY = "category",
    SUB_CATEGORY = "subCategory",
    SKU = "sku",
    BRAND = "brand",
}

export enum ReportFiltersType {
    CATEGORIES = "categories",
    SKUS = "skus",
}

export enum OptionValues {
    ALL = "all",
    UNSELECT_ALL = "unselect-all",
    OOS_SKUS = "OOS SKUs",
    DRAINERS = "drainers",
    PRODUCTS_PINCODE_LEVEL_AVAILABILITY = "Products Pincode Level Availabilitiy",
    OOS_DARK_STORES = "OOS Dark Stores",
    SUBCATEGORIES_WITH_MAX_LOSS = "Subcategories with max Loss",
    SELLERS_ECOMM = "Sellers_ECOMM",
}

export type THashConfig = {
    fileName: string;
    fileSize: number;
    fileExt: string;
    clientId: number;
};

export type CellRendererAction = {
    resource: ICerbosResource;
    action: string;
    key?: string;
};

export enum ActionLevel {
    Keyword = "keyword",
    AdGroup = "adgroup",
    Campaign = "campaign",
}
export type TExportGridBtnProps = {
    onClickFunction: () => void;
    isSubModule?: boolean;
    buttonText?: string;
    className?: string;
};

export interface CompetitiveProductsDaywiseTrendsMetric {
    type: string;
    [key: string]: any;
}

export interface ICompetitiveProductsDaywiseTrendsProps {
    title?: string;
    widgetData: {
        name: string;
        metrics: CompetitiveProductsDaywiseTrendsMetric[];
    }[];
}

export interface CompetitiveProductsDaywiseTrendsMetric {
    type: string;
    // [key: string]: any;
}

export interface ICompetitiveProductsDaywiseTrendsProps {
    title?: string;
    widgetData: {
        name: string;
        metrics: CompetitiveProductsDaywiseTrendsMetric[];
    }[];
}

export interface IMsVsRpiTrendsData {
    widgetData: {
        categories: string[];
        series: { name: string; data: number[] }[];
    };
    title: string;
    additionalTitleComponent?: (
        type: keyof IPricingChartsToggleFiltersState,
        moduleType?: string
    ) => JSX.Element;
    currencySymbol: string;
}

export interface IPriceVsSalesWidgetData {
    price: number;
    sales: number;
}

export interface IPriceVsSalesData {
    title?: string;
    widgetData: IPriceVsSalesWidgetData[];
    additionalTitleComponent?: (
        type: keyof IPricingChartsToggleFiltersState,
        moduleType?: string
    ) => JSX.Element;
    currencySymbol: string;
}

export interface IPriceAndDealsCompetitionWidgetData {
    title?: string;
    widgetData: {
        products: string[];
        prices: number[];
        dealPercentages: number[];
        categoryAveragePrice: number[];
        skuTitles: string[];
    };
    currencySymbol: string;
}

export interface IGraphAttributes {
    xAxisTitle?: string;
    xAxisType?: string;
    yAxisTitle?: string;
    hideXAxisTitle?: boolean;
    yAxisOppositeTitle?: string;
    yAxisOppositeTitleOptional?: string;
    yAxisOppositeDashStyleReq?: boolean;
    yAxisOppositeDashLineColor?: string;
}

export enum PricingCharts {
    PRICE_SALES_PPU = "price-sales-ppu",
    MS_RPI_PPU = "ms-rpi-ppu",
    MS_RPI = "ms-rpi",
    PRODUCT_COMPETITION = "product-competition",
    PRODUCT_COMPETITION_PPU = "product-competition-ppu",
    PRICING_DEALS_PPU = "pricing-deals-ppu",
    PRICING_DEALS_TRENDS = "pricing-deals-trends",
    PRICE_DEALS = "price-deals",
    PRICE_DEALS_SKUS = "price-deals-skus",
    PRICE_DEALS_COMPETITION = "price-deals-competition",
    PRICE_DEALS_CHANNEL = "price-deals-channel",
    KPI_SLECTION = "kpi-selection",
}

export enum AvailabilityCharts {
    BB_WIN_IMPACT = "bb-win-impact",
    AVAILABILITY = "availability",
    BUYBOX = "buybox",
}

export interface IPricingChartsToggleFiltersState {
    [PricingCharts.PRICE_SALES_PPU]: boolean;
    [PricingCharts.MS_RPI_PPU]: boolean;
    [PricingCharts.PRODUCT_COMPETITION_PPU]: boolean;
    [PricingCharts.PRICING_DEALS_PPU]: boolean;
}

export interface IPricingChartsFiltersState {
    [PricingCharts.MS_RPI]: string;
    [PricingCharts.PRICE_DEALS]: string;
    [PricingCharts.PRICE_DEALS_SKUS]: string[];
    [PricingCharts.PRICE_DEALS_COMPETITION]: string[];
    [PricingCharts.PRICE_DEALS_CHANNEL]: string;
    [PricingCharts.KPI_SLECTION]: AggregationField.MS_PERCENT;
}

export interface IAvailabilityChartsFiltersState {
    [AvailabilityCharts.BB_WIN_IMPACT]: string;
}

export interface ITrendsWithCompareModalWidget {
    title?: string;
    widgetData: ITrendsWithCompareModalWidgetData;
    widgetLinkName?: string;
    additionalTitleComponent?: (
        type: keyof IPricingChartsToggleFiltersState,
        moduleType?: string
    ) => JSX.Element;
    onWidgetLinkHandler?: () => void;
    graphAttributes?: IGraphAttributes;
    filterPillsComponent?: () => JSX.Element;
    compare?: boolean;
    selectedSku?: string;
    maxSelectableSkus?: number;
    maxDates?: number;
    chart?: string;
    showRadioFilter?: boolean;
    showFilter?: boolean;
    selectedRadioFilters?: CATEGORY_OVERVIEW_SKU_FILTERS;
    setSelectedRadioFilters?: (value: CATEGORY_OVERVIEW_SKU_FILTERS) => void;
    selectedMSTrendsOvertimeRadio?: CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO;
    setSelectedMSTrendsOvertimeRadio?: (
        value: CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO
    ) => void;
    showDataLabels?: boolean;
    yAxisGraphType?: string;
}

export type IPricingChartsToggleFiltersActions =
    | { type: "RESET"; payload: IPricingChartsToggleFiltersState }
    | { type: keyof IPricingChartsToggleFiltersState; payload: boolean };

export type IPricingChartsFiltersActions =
    | { type: "RESET"; payload: IPricingChartsFiltersState }
    | { type: keyof IPricingChartsFiltersState; payload: string | string[] };

export type IAvailabilityChartsFiltersActions =
    | { type: "RESET"; payload: IAvailabilityChartsFiltersState }
    | {
          type: keyof IAvailabilityChartsFiltersState;
          payload: string | string[];
      };

export interface IRpiData {
    price: number;
    revenue: number;
    upd: number;
}

export interface IPriceVsSalesDataOriginalData {
    sales_units: number;
    price: number;
}

export interface IPriceVsSalesFormattedData {
    price: number;
    sales: number;
}

export interface IMsVsRpiDataOriginalData {
    crawl_date: string;
    market_share: number;
    price: number;
    revenue: number;
    upd: number;
    avg_price_on_date: number;
}

export interface IMsVsRpiDataFormattedData {
    categories: string[];
    series: {
        name: string;
        data: number[];
    }[];
}

export interface IPriceDealsDataOriginalData {
    crawl_date: string;
    price: number;
    deal: number;
}

export interface IPriceDealsDataFormattedData {
    date: string;
    price: number;
    deal: number;
}

export interface IPricingSummaryOriginalData {
    current_price?: number;
    previous_price?: number;
    per_unit?: number;
    current_rpi?: number;
    previous_rpi?: number;
    best_sku_rpi?: number;
    current_deal?: number;
    previous_deal?: number;
}

export interface IVisibilitySummaryOriginalData {
    [AggregationField.CURRENT_ORGANIC_VISIBILITY]?: number;
    [AggregationField.CURRENT_PAID_VISIBILITY]?: number;
    [AggregationField.PREVIOUS_ORGANIC_VISIBILITY]?: number;
    [AggregationField.PREVIOUS_PAID_VISIBILITY]?: number;
}

export interface IPricingSummaryFormattedData {
    price: {
        value: string;
        change: number;
        unitPrice: string;
    };
    rpi: {
        value: number;
        change: number;
        percentage: number;
        status: "above" | "below";
    };
    averageDealPercentage: {
        value: number;
        change: number;
    };
}

export interface IVisibilitySummaryFormattedData {
    organicVisibility: {
        value: number;
        change: number;
    };
    paidVisibility: {
        value: number;
        change: number;
        percentage: number;
        status: "above" | "below";
    };
    GV: {
        value: number;
        change: number;
    };
}

export interface ICompetitiveProductsDaywiseOriginalData {
    crawl_date: string;
    sku: string;
    sku_title: string;
    price: number | null;
    market_share: number | null;
}

export interface ICompetitiveProductsDaywiseMetrics {
    type: string;
    [key: string]: string | number;
}

export interface ICompetitiveProductsDaywiseFormattedData {
    name: string;
    metrics: ICompetitiveProductsDaywiseMetrics[];
}

export interface IPricingCompetitiveAnalysisOriginalData {
    sku?: string | null;
    price?: number | null;
    sales?: number | null;
    sku_title?: string | null;
    unit_per_day?: number | null;
}

export interface IPricingCompetitiveAnalysisFormattedData {
    price: number;
    sales: number;
    volume: number;
    productName: string;
    skuTitle: string;
}

export interface IPricingSatisfactionSummaryOriginalData {
    sku?: string | number;
    curr_price?: number | null;
    prev_price?: number | null;
    curr_revenue?: number;
    curr_upd?: number;
}

export interface IVisibilitySatisfactionSummaryOriginalData {
    [AggregationField.KEYWORD]?: string | number;
    [AggregationField.CURRENT_WT_CRAWL_SV]?: number | null;
    [AggregationField.PREVIOUS_WT_CRAWL_SV]?: number | null;
}

export interface IPricingSatisfactionSummaryFormattedData {
    positives: any;
    negatives:
        | {
              product_name: string | number;
              price_drop: number;
              rpi: number;
          }[]
        | [];
}

export interface IMyProductTrendsOriginalData {
    name: string;
    price: number;
    crawl_date: string;
    revenue: number;
    unit_per_day: number;
    sku_title: string;
    sku: string;
    keyword: string;
    tag_subcategory: string;
    brand: string;
    brand_title: string;
}

export interface IMyProductTrendsFormatterData {
    chartDataSeries: {
        categories: string[];
        series: {
            name: string;
            data: number[];
            color?: string;
        }[];
    };
    skuTitles: Record<string, string>;
}

export interface IPricingCompetitiveAnalysisTrendsOriginalData {
    sku: string | null;
    crawl_date: string | null;
    price: number | null;
    bsr_category: string | null;
    current_deal_percentage: number | null;
    sku_title: string | null;
}

export interface IPricingCompetitiveAnalysisTrendsFormattedData {
    products: string[];
    prices: number[];
    dealPercentages: number[];
    categoryAveragePrice: number[];
    skuTitles: string[];
}

export interface IPricingAndDealCompetitionAnalysisWidget {
    title: string;
    widgetData: any;
    show: boolean;
    setShow: (show: boolean) => void;
    chartFilters: typeof PRICING_CHART_FILTERS_INTIAL_STATE;
    setChartFilters: React.Dispatch<IPricingChartsFiltersActions>;
    currencySymbol: string;
    hideDrawerChartSwitchOptions?: boolean;
    radioOptions?: Array<Record<string, string>>;
    isComparisonRequired?: boolean;
    isSubCategoryDropDownOptionsRequired?: boolean;
    subCategories?: string[];
    brands?: string[];
    isKpiSelectionRequired?: boolean;
    selectedTrendsSubCategory?: string;
    setSelectedTrendsSubCategory?: React.Dispatch<React.SetStateAction<string>>;
    isGoToDrainersRequired?: boolean;
    setShowDrainers?: React.Dispatch<React.SetStateAction<boolean>>;
    isCrossRegionRequired?: boolean;
    kpiOptions?: Array<{
        label: string;
        value: string;
    }>;
    showMoreButtonRequired?: boolean;
    compareWithOptions?: { value: any; label: string }[];
}

export interface ICompareWith
    extends Partial<IPricingAndDealCompetitionAnalysisWidget> {
    selectedCompareOption: MyProductTrendsModules;
    setSelectedCompareOption: React.Dispatch<
        React.SetStateAction<MyProductTrendsModules>
    >;
    chartFilters: typeof PRICING_CHART_FILTERS_INTIAL_STATE;
    setChartFilters: React.Dispatch<IPricingChartsFiltersActions>;
    isComparisonRequired: boolean;
    selectedKpi: string;
    setSelectedKpi: React.Dispatch<React.SetStateAction<string>>;
    activeModule: string;
    isCrossRegionRequired: boolean;
}

export enum ProductDeepdiveModules {
    OVERVIEW = "overview",
    RATINGS_REVIEWS = "rating-reviews",
    PRICING = "pricing",
    VISIBILITY = "visibility",
    AVAILABILITY = "availability",
    CONTENT_SCORE = "content-score",
}

export enum CrossChannelTableDataModules {
    REVIEW = "review",
    PRICING = "pricing",
    VISIBILITY = "visibility",
    AVAILABILITY = "availability",
    OVERVIEW = "overview",
}

export enum CrossChannelTableDataModuleSections {
    KEYWORD_VISIBILITY = "keyword visibility",
    CATEGORY_OVERVIEW_CROSS_CHANNEL = "category overview cross-channel",
    CATEGORY_OVERVIEW_COMPETITION = "category overview competition",
    CATEGORY_OVERVIEW_COMPARISON = "category overview comparison",
    CATEGORY_AVAILABILITY_CROSS_CHANNEL_ECOMM = "category availability cross-channel",
    CATEGORY_AVAILABILITY_COMPETITION_ECOMM = "category availability competition",
    CATEGORY_AVAILABILITY_COMPARISON_ECOMM = "category availability comparison",
}

export enum MyProductTrendsModules {
    CROSS_CHANNEL = "cross-channel",
    COMPETITION = "competition",
    COMPARISON = "comparison",
    CROSS_REGION = "cross-region",
}

export enum AggregationField {
    RATING = "rating",
    MARKET_SHARE = "market_share",
    PRICE = "price",
    AVG_PRICE_ON_DATE = "avg_price_on_date",
    CURRENT_DEAL_PERCENTAGE = "current_deal_percentage",
    DEAL_PERCENTAGE = "deal_percentage",
    DEAL = "deal",
    BSR_RANK_2 = "bsr_rank_2",
    CAT_UPD = "cat_upd",
    CONTENT_SCORE = "content_score",
    MRP = "mrp",
    ORG_CAT_WT_CRAWL_SV = "org_cat_wt_crawl_sv",
    ORG_SKU_WT_CRAWL_SV = "org_sku_wt_crawl_sv",
    PAID_CAT_WT_CRAWL_SV = "paid_cat_wt_crawl_sv",
    PAID_SKU_WT_CRAWL_SV = "paid_sku_wt_crawl_sv",
    UNIT_PER_DAY = "unit_per_day",
    PER_DAY_RATING = "per_day_rating",
    DAILY_TREND_SALE = "daily_trend_sale",
    ACOS = "acos",
    AOV = "aov",
    CPA = "cpa",
    CPC = "cpc",
    CPM = "cpm",
    CR = "cr",
    CTR = "ctr",
    PERCENT_SPEND = "percent_spend",
    ROAS = "roas",
    SPEND = "spend",
    SALE = "sale",
    AVAILABILITY = "availability",
    CLICK = "click",
    ORDERS = "orders",
    IMPRESSION = "impression",
    ORGANIC_RANK = "organic_rank",
    OVERALL_RANK = "overall_rank",
    SPONSORED_RANK = "sponsored_rank",
    CONVERSION_RATE = "conversionRate",
    CURRENT_ORGANIC_VISIBILITY = "current_organic_visibility",
    CURRENT_PAID_VISIBILITY = "current_paid_visibility",
    PREVIOUS_ORGANIC_VISIBILITY = "previous_organic_visibility",
    PREVIOUS_PAID_VISIBILITY = "previous_paid_visibility",
    CURRENT_WT_CRAWL_SV = "current_wt_crawl_sv",
    PREVIOUS_WT_CRAWL_SV = "previous_wt_crawl_sv",
    KEYWORD = "keyword",
    OVERALL_VISIBILITY = "overall_visibility",
    BUYBOX = "buybox",
    BUYBOX_WIN = "buybox_win",
    TOTAL_SALES = "total_sales",
    MS_PERCENT = "MS%",
    OFFTAKE = "offtake",
    REVENUE = "revenue",
    UNITS = "units",
    UPD = "upd",
    CAT_REVENUE = "cat_revenue",
    CUR_PRICE = "cur_price",
    UPD_MS = "upd_ms",
    REVENUE_MS = "revenue_ms",
    CURR_AVAILABILITY = "curr_availability",
    CURR_PRICE = "curr_price",
    CURR_UPD = "curr_upd",
    CURR_DRR_UPD = "curr_drr_upd",
    CURR_DRR_PRICE = "curr_drr_price",
    CURR_DRR_REVENUE = "curr_drr_revenue",
    CURR_REVENUE = "curr_revenue",
    CITY = "city",
    PINCODE = "pincode",
    STORE_TYPE = "store_type",
    CURR_INVENTORY = "curr_inventory",
    STORE_ID = "store_id",
}

export enum MyProductTrendsTypes {
    RPI = "rpi",
    PRICE = "price",
    DEALS = "deals",
    OVERALL_RANK = "overall_rank",
    ORGANIC_RANK = "organic_rank",
    PAID_RANK = "paid_rank",
    AVAILABILITY = "availability",
    BUYBOX = "buybox",
    ORGANIC_VISIBILITY = "organic_visibility",
    PAID_VISIBILITY = "paid_visibility",
    OVERALL_VISIBILITY = "overall_visbility",
    MS_PERCENT = AggregationField.MS_PERCENT,
    SALE = AggregationField.SALE,
    ROAS = AggregationField.ROAS,
    AD_SPEND = AggregationField.SPEND,
    TOTAL_SALES = AggregationField.TOTAL_SALES,
}

export enum CommonChartAttributes {
    SKU = "sku",
    PPU_DATA = "ppu-data",
    DEFAULT_DATA = "default-data",
    SKU_OPTIONS = "sku-options",
    BUBBLE_CHART = "bubble-chart",
    TREND = "trend",
}

export enum CommonWidgetMetrics {
    NAME = "name",
    CHANNEL_ID = "channel_id",
    CHANNEL = "channel",
    SKU = "sku",
    SKU_FILTER = "sku_filter",
    REVIEW_DATE = "review_date",
    DATE = "date",
    CRAWL_DATE = "crawl_date",
    REVIEW_COUNT = "review_count",
    PRICE = "price",
    RATING = "rating",
    DEAL = "deal",
    CHANNEL_NAME = "name",
    POSTITIVE = "positive",
    NEGATIVE = "negative",
    SCORE = "score",
    CHANGE = "change",
    PRODUCT_NAME = "product_name",
    PRICE_DROP = "price_drop",
    RPI = "rpi",
    CURR_RPI = "curr_rpi",
    VALUE = "value",
    ABOVE = "above",
    BELOW = "below",
    INCREASE = "Increased",
    DECREASE = "Decreased",
    CONSISTENT = "Consistent",
    KEYWORD_NAME = "keyword",
    KEYWORD_TYPE = "intent",
    INDEXED_SEARCH_VOLUME = "indexed_search_volume",
    SV_CHANGE = "sv_change",
    GREATER_THAN = "Greater than",
    LESS_THAN = "Less than",
    BRAND = "brand",
    BRAND_ID = "brand_id",
    BRAND_FILTER = "brand_filter",
    CATEGORY = "category",
    SUB_CATEGORY = "subcategory",
    TAG_SUBCATEGORY = "tag_subcategory",
    MS = "ms",
    MARKET_SIZE = "market_size",
    AD_SALES = "ad_sales",
    AD_SPENDS = "ad_spends",
    ROAS = "roas",
    SOV = "sov",
    RPI_NEW = "rpi",
    RATING_NEW = "rating",
    REVIEWS = "reviews",
    AVAILABILITY = "availability",
    BUYBOX = "buybox",
    STOCK = "stock",
    COMPARE = "compare",
    TITLE = "title",
    RANK = "rank",
    TOTAL_SALES = "total_sales",
    CTR = "ctr",
    CTC = "ctc",
    CLICKS = "clicks",
    ORDERS = "orders",
    IMPRESSIONS = "impressions",
    SALES = "sales",
    SPENDS = "spends",
    SEARCH_VOLUME = "search_volume",
    PRICING_RPI = "pricingRpi",
    CATEGORY_CONTRIBUTION = "categoryContribution",
    All = "all",
    MARKET_SHARE = "market_share",
    OVERALL_SOV = "overall_sov",
    OVERALL_IMPRESSIONS = "overall_impressions",
    WEIGHTED_AVAILABILITY = "weighted_availability",
    ORGANIC_VISIBILITY = "weighted_visibility",
    PAID_VISIBILITY = "weighted_visibility",
    DISCOUNT = "weighted_availability", // TODO: Change to weighted_discount
    WEIGHTED_DISCOUNT = "weighted_discount",
    AVERAGE_DISCOUNT = "weighted_availability", // TODO: Change to average_discount
    ORGANIC_SOV = "organic_sov",
    PAID_SOV = "paid_sov",
    PAID_IMPRESSION = "total_market_sponsored_impressions",
    BRANDED_SEARCH = "branded_search",
    RELATIVE_PRICE_INDEX = "relative_price_index",
    STOCK_IN_HAND = "weighted_avg_stock_in_hand",
    AVERAGE_RATING = "avg_rating",
    AVERAGE_PRICE = "average_price", // TODO: Change to avg_pricing
    AVERAGE_MRP = "average_mrp",
    FILL_RATE = "fill_rate",
    LISTING_STATUS = "listing_status",
    CONTENT_SCORE = "content_score_weighted",
    AVERAGE_CONTENT_SCORE = "average_content_score",

    CUR_MS_UPD = "cur_ms_upd",
    CUR_MS_REVENUE = "cur_ms_revenue",
    PREV_MS_UPD = "prev_ms_upd",
    PREV_MS_REVENUE = "prev_ms_revenue",
    CUR_SALE = "cur_sale",
    CUR_SPEND = "cur_spend",
    CUR_ROAS = "cur_roas",
    CUR_TOTAL_SALES = "cur_total_sales",
    PREV_TOTAL_SALES = "prev_total_sales",
    PREV_SALE = "prev_sale",
    PREV_SPEND = "prev_spend",
    PREV_ROAS = "prev_roas",

    CUR_UPD_MS = "cur_upd_ms",
    CUR_REVENUE_MS = "cur_revenue_ms",
    PREV_UPD_MS = "prev_upd_ms",
    PREV_REVENUE_MS = "prev_revenue_ms",
    CATEGORY_SHARE = "category_share",

    KEYWORD_COUNT = "keyword_count",

    KEYWORD_OVERALL_SOV = "keyword_overall_sov",
    KEYWORD_ORGANIC_SOV = "keyword_organic_sov",
    KEYWORD_PAID_SOV = "keyword_paid_sov",

    KEYWORD_OVERALL_SOV_SKU = "keyword_overall_sov_sku",
    KEYWORD_ORGANIC_SOV_SKU = "keyword_organic_sov_sku",
    KEYWORD_PAID_SOV_SKU = "keyword_paid_sov_sku",

    SEARCH_INDEX = "search_index",
    KEYWORD_INTENT = "intent",

    GENERALISED_ORGANIC_SOV = "generalised_organic_sov",
    GENERALISED_PAID_SOV = "generalised_paid_sov",
    GENERALISED_OVERALL_SOV = "generalised_overall_sov",

    LINE_AVL_PCT = "line_availability_pct",
    LINE_AVG_7D_REVENUE = "line_avg_7day_revenue",

    TOTAL_ORGANIC_IMPRESSIONS = "total_market_organic_impressions",

    BUYBOX_PCT = "buybox_pct",

    REGION = "region",
    CITY = "city",
    SKU_TITLE = "sku_title",
    CROSS_CHANNEL_SKU_ID = "cross_channel_sku_id",

    VARIANTS = "variants",
    PRIORITY = "priority",
    ACTION = "action",
    ORGANIC_RANK = "organic_rank",
    PAID_RANK = "paid_rank",
    SPONSORED_SOV = "sponsored_sov",

    PRODUCT_URL = "product_url",
    THUMBNAIL = "thumbnail",

    CATEGORY_ID = "category_id",
    MRP = "mrp",
    SP = "sp",
    LP = "lp",
    IN = "IN",
    BY_VALUE = "by_value",
    BY_VOLUME = "by_volume",
}

export enum SummaryWidgetConfigKeys {
    PRICE = "price",
    PRICE_VALUE = "price.value",
    PRICE_CHANGE = "price.change",
    RPI = "rpi",
    RPI_VALUE = "rpi.value",
    RPI_CHANGE = "rpi.change",
    AVG_DEAL_PERC = "averageDealPercentage",
    AVG_DEAL_VALUE = "averageDealPercentage.value",
    AVG_DEAL_CHANGE = "averageDealPercentage.change",
    RATING = "rating",
    RATING_VALUE = "rating.value",
    RATING_KEY = "rating.change",
    NUM_RATINGS = "numRatings",
    NUM_RATINGS_VALUE = "numRatings.value",
    NUM_RATINGS_CHANGE = "numRatings.change",
    REVIEWS = "reviews",
    REVIEWS_VALUE = "reviews.total",
    REVIEWS_CHANGE = "reviews.change",
    ORGANIC_VISIBILITY = "organicVisibility",
    ORGANIC_VISIBILITY_VALUE = "organicVisibility.value",
    ORGANIC_VISIBILITY_CHANGE = "organicVisibility.change",
    PAID_VISIBILITY = "paidVisibility",
    PAID_VISIBILITY_VALUE = "paidVisibility.value",
    PAID_VISIBILITY_CHANGE = "paidVisibility.change",
    AVAILABILITY_PERCENTAGE = "availabilityPercentage",
    AVAILABILITY_PERCENTAGE_VALUE = "availabilityPercentage.value",
    AVAILABILITY_PERCENTAGE_CHANGE = "availabilityPercentage.change",
    BUYBOX_PERCENTAGE = "buyboxPercentage",
    BUYBOX_PERCENTAGE_VALUE = "buyboxPercentage.value",
    BUYBOX_PERCENTAGE_CHANGE = "buyboxPercentage.change",
    ESTIMATED_SALES_LOSS = "estimatedSalesLoss",
    ESTIMATED_SALES_LOSS_VALUE = "estimatedSalesLoss.value",
    ESTIMATED_SALES_LOSS_CHANGE = "estimatedSalesLoss.change",
    GV = "GV",
    GV_VALUE = "GV.value",
    GV_CHANGE = "GV.change",
    WT_AVAILIBILITY = "wtAvailability",
    WT_AVAILIBILITY_VALUE = "wtAvailability.value",
    WT_AVAILIBILITY_CHANGE = "wtAvailability.change",
    ASSORTMENT = "assortment",
    ASSORTMENT_VALUE = "assortment.value",
    ASSORTMENT_CHANGE = "assortment.change",
    LISTING_STATUS = "listingStatus",
    LISTING_STATUS_VALUE = "listingStatus.value",
    LISTING_STATUS_CHANGE = "listingStatus.change",
}

export enum DeepDiveBrandScoreInfo {
    MS_PERC_BY_VALUE = "MS% (By Value)",
    LATEST_BSR = "Latest BSR",
    OUT_OF_STOCK_PERC = "Out Of Stock%",
    BUYBOX_WIN = "Buybox win",
    BUYBOX_LOST_SINCE = "Buybox Lost Since",
    ORGANIC_VISIBILITY = "Organic Visibility",
    PAID_VISIBILITY = "Paid Visibility",
    CONTENT_SCORE = "Content Score",
}

export interface User {
    user_id: number;
    uid: string;
    first_name: string;
    last_name: string;
    email: string;
    last_login: string;
    created_at: string;
    created_by: string;
    updated_at: string;
    updated_by: string;
    status: boolean;
    country: string;
    timezone: string;
    phone: string;
    default_client: number;
    is_archived: boolean;
    is_super_admin: boolean;
    access: IAccess[];
}

export type TUserdata = {
    userData: {
        value: User;
    };
};

export interface IPermissionConfigItem {
    resource: string;
    action: string;
    key: string;
}

export type TPermissionConfig = {
    commonPermissions?: IPermissionConfigItem[];
    [key: string]: IPermissionConfigItem[] | undefined;
};

export type TUsersFormInitialState = {
    channelsLoading: number | null;
    formLoading: boolean;
    reactivateUserLoading: boolean;
    formSubmissionLoading: boolean;
};

export type TUsersFormActions =
    | { type: "SET_CHANNELS_LOADING"; payload: number | null }
    | { type: "SET_FORM_LOADING"; payload: boolean }
    | { type: "SET_REACTIVATE_LOADING"; payload: boolean }
    | { type: "SET_FORM_SUBMISSION_LOADING"; payload: boolean };

export type TChannels = Array<IChannels>;

export interface ClientGroup {
    clients: number[];
    platforms: string[];
    role_id: number;
    privileges: string[];
    channels: Array<number>;
}

export interface IUserPrivilegesState {
    [index: string]: string[];
}

export interface ISavedPermissionsData {
    role_id: number;
    permission_id: number;
}

export interface IUserRolesComponentProps {
    privilegesModalDisp?: boolean;
    setPrivilegesModalDisp?: (value: boolean) => void;
    clientId?: number[] | number;
    userPrivileges?: IUserPrivilegesState; // Changed from TUserPermissionState
    setUserPrivileges?: (
        updater: (prev: IUserPrivilegesState) => IUserPrivilegesState
    ) => void;
    selectedRole?: number;
    availableRoles?: Array<{
        role_id: number;
        role_name: string;
        role_slug?: string;
    }>;
    form?: FormInstance;
    enableNavigator?: boolean;
    enableCategoryAnalysis?: boolean;
    enableProductAnalysis?: boolean;
}

export interface IReportUserRecord {
    id: number | null;
    username: string;
}

export interface IUserSettingModalTable {
    updateRecords: boolean;
    setUpdateRecords: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IAddOrEditUsersProps {
    addUsersDisplay?: boolean;
    editUserDisplay?: boolean;
    setEditUserDisplay?: React.Dispatch<React.SetStateAction<boolean>>;
    setUpdateRecords: React.Dispatch<React.SetStateAction<boolean>>;
    isEdit: boolean;
    record?: IReportUserRecord;
}

export enum AddEditReportUserFormFields {
    USER_NAME = "userName",
}
export interface IReusableDrawerProps {
    title: string;
    width: number;
    onClose: () => void;
    open: boolean;
    children: React.ReactNode;
    footer: boolean;
}

export type TSelectOptionsChildrenProps = Array<
    string | { props?: { children?: string } }
>;

export type TChannelSelectOptions = {
    children: {
        props: {
            children: [
                string,
                {
                    props: {
                        children: TSelectOptionsChildrenProps;
                    };
                }
            ];
        };
    };
};

export type TClientSelectOptions = {
    children: {
        props: {
            children: Array<{
                props: { children: TSelectOptionsChildrenProps };
            }>;
        };
    };
};

export enum AppRoutes {
    PAGENOTFOUND = "pagenotfound",
    UNAUTHORIZED = "unauthorized",
}

export interface IEntityGroup {
    id?: number;
    name?: string;
}

interface IRegistrationInfo {
    user: string;
    date: Date;
    job_platform: string;
}

interface IModificationInfo {
    date: Date;
    user: string;
    job_platform: string;
}
// export interface IClientListItem {
//     id: number;
//     name: string;
//     client_group: IEntityGroup;
//     client_subgroup: IEntityGroup;
//     status: boolean;
//     is_archived: boolean;
//     subscription: {
//         plan_name: string;
//         tier: string;
//         status: string;
//         start_date: Date;
//         end_date: Date;
//         trial_start_date: Date;
//         trial_end_date: Date;
//     };
//     channels: string[];
//     timezones: string[];
//     country: string;
//     contact: IClientContact;
//     domain: string;
//     sys_registered_by_user: string;
//     registered_on: Date;
//     registrations: IRegistrationInfo[];
//     modifications: IModificationInfo[];
// }

export interface ChannelDetail {
    channel_id: number;
    name: string;
    logo_url: string;
}
export interface IClientListItem {
    id: number;
    name: string;
    client_group: IEntityGroup;
    client_subgroup: IEntityGroup;
    status: boolean;
    is_archived: boolean;
    subscription: {
        plan_name: string;
        tier: string;
        status: string;
        start_date: Date;
        end_date: Date;
        trial_start_date: Date;
        trial_end_date: Date;
    };
    timezones: string[];
    country: string;
    contact: IClientContact;
    domain: string;
    sys_registered_by_user: string;
    registered_on: Date;
    registrations: IRegistrationInfo[];
    modifications: IModificationInfo[];
    channel_details?: ChannelDetail[];
    logo_url: string;
}
export interface IClientGroup {
    id: number;
    name: string;
}

export interface IClientSubgroup {
    id: number;
    name: string;
}

export interface IClientContact {
    name?: string;
    email?: string;
    phone?: string;
}

export interface ICreateClientPayload {
    name: string;
    plan_id?: number;
    country: string;
    contact?: IClientContact;
    status?: boolean;
    timezone: string;
    client_group: IEntityGroup;
    client_subgroup: IEntityGroup;
    domain?: string;
    domain_alias?: string;
    evidence_retention_days?: number;
    data_retention_days?: number;
    user_account_dormancy_period_in_days?: number;
    logo?: File | null;
}

export type IUpdateClientPayload = Partial<ICreateClientPayload>;

export interface IPlan {
    id: number;
    name: string;
    tier: string;
    price: number;
    duration: string;
    discount_rate: number;
}

export interface IClientIntegration {
    client_id: number;
    channel_id: number;
    channel_name: string;
    platform_status: string;
    default_account_name: string | null;
    job_platform_name: string | null;
    registered_on: Date;
    unq_account_id: string | null;
    fk_ba_account_id: string | null;
    fk_aa_account_id: string | null;
    account_username: string | null;
    sys_registered_by_user: string | null;
    [key: string]: string | number | Date | null;
}
export interface IIntegrationChannel {
    channel_id: number;
    name: string;
    logo: React.ReactElement;
    description: string;
    platform_status: boolean;
    oauthEnabled?: boolean;
}

export enum ClientIntegrationFormFields {
    PlatformStatus = "platform_status",
    DefaultAccountName = "default_account_name",
    JobPlatformName = "job_platform_name",
    UnqAccountId = "unq_account_id",
    AccountUsername = "account_username",
    FkBaAccountId = "fk_ba_account_id",
    FkAaAccountId = "fk_aa_account_id",
    Password = "password",
}

export enum EditClientUserFormFields {
    FirstName = "first_name",
    LastName = "last_name",
    Email = "email",
    RoleId = "role_id",
    Status = "status",
    Country = "country",
    Timezone = "timezone",
    Phone = "phone",
}

export enum ClientFormFields {
    Name = "name",
    Country = "country",
    Timezone = "timezone",
    Domain = "domain",
    Status = "status",
    Contact = "contact",
    ClientGroupId = "client_group_id",
    ClientGroupName = "client_group_name",
    ClientSubgroupId = "client_subgroup_id",
    ClientSubgroupName = "client_subgroup_name",
    SelectedChannels = "channels",
    PlanName = "plan_name",
    PlanId = "plan_id",
    IsNewGroup = "is_new_group",
    Logo = "logo_url",
}

export enum ClientMemberColumns {
    Name = "name",
    Email = "email",
    Role = "role",
    Status = "status",
    Country = "country",
    Timezone = "timezone",
    LastLogin = "last_login",
    Action = "action",
    FullName = "full_name",
    ExpiryDate = "expiry_date",
}

export enum ClientDetailsLabels {
    ClientName = "CLIENT NAME",
    Status = "STATUS",
    Group = "GROUP",
    Subgroup = "SUBGROUP",
    Country = "COUNTRY",
    Domain = "DOMAIN",
    ContactName = "CONTACT NAME",
    ContactEmail = "CONTACT EMAIL",
    ContactPhone = "CONTACT PHONE",
    Subscription = "SUBSCRIPTION",
    Plan = "PLAN",
    Tier = "TIER",
    SubscriptionStatus = "STATUS",
    StartDate = "START DATE",
    EndDate = "END DATE",
    RegisteredBy = "REGISTERED BY",
    RegisteredOn = "REGISTERED ON",
    Logo = "LOGO",
}

export enum ClientTableColumns {
    ID = "ID",
    Name = "Name",
    ClientGroup = "Client Group",
    ClientSubGroup = "Client Sub Group",
    Status = "Status",
    Subscription = "Subscription",
    ChannelList = "Channels",
    ContactName = "Contact Name",
    ActionButtons = "Actions",
}

export enum ChannelIntegrationAccountType {
    Seller = "seller",
    Vendor = "vendor",
    Advertising = "advertising",
}

export enum EnvironmentType {
    Local = "local",
    Beta = "beta",
}

export interface IClientResponse {
    status: boolean;
    message?: string;
    data?: {
        id: number;
        [key: string]: any;
    };
}

export enum GroupingKey {
    CHANNEL_ID = "channel_id",
    CLIENT_SUBGROUP_ID = "client_subgroup_id",
    SKU_LINK = "sku_link",
    SKU = "sku",
    SKU_ID = "sku_id",
    ASIN_ID = "asin_id",
    PROGRAM_TYPE = "program_type",
    SKU_TITLE = "sku_title",
    CLIENT_ID = "client_id",
    CHANNEL = "channel",
    KEYWORD = "keyword",
    NAME = "name",
    BRAND = "brand",
    TAG_SUBCATEGORY = "tag_subcategory",
    STORE_ID = "store_id",
    CATEGORY = "category",
    SUB_CATEGORY = "sub_category",
    CITY = "city",
    PINCODE = "pincode",
    REGION = "region",
}

export interface IChartOptions {
    [key: string]: any;
}

export interface IModuleData {
    titles: string[];
    chartOptions: IChartOptions;
}

export interface CustomGroupingFunctionType {
    [key: string]: (item: Record<string, string>, groupKey: string) => string;
}

export enum AggregationType {
    DATEWISE_TRENDS_OF_COMPETITIVE_PRODUCTS = "datewise_trends_of_competitive_products",
    MS_VS_RPI = "ms_vs_rpi",
    MY_PRODUCT_PRICE_AND_DEAL = "my_product_price_and_deal",
    MY_PRODUCT_TRENDS_GRAPH_COMPETITION = "my_product_trends_graph_competition",
    MY_PRODUCT_TRENDS_GRAPH_CROSS_CHANNEL = "my_product_trends_graph_cross_channel",
    PRICING_AND_DEAL_TRENDS = "pricing_and_deal_trends",
    OVERVIEW = "overview",
    ALL_REVIEW_TRENDS = "all_review_trends",
    CROSS_CHANNEL_COUNT_RATINGS = "cross_channel_count_ratings",
    CROSS_CHANNEL_COUNT_REVIEWS = "cross_channel_count_reviews",
    CROSS_CHANNEL_RATINGS = "cross_channel_ratings",
    CROSS_CHANNEL_REVIEWS = "cross_channel_reviews",
    CROSS_CHANNEL_SCORE_RATINGS = "cross_channel_score_ratings",
    CROSS_CHANNEL_SCORE_REVIEWS_RATINGS = "cross_channel_score_reviews_ratings",
    RATING_AND_REVIEWS_TRENDS = "rating_and_reviews_trends",
    CUSTOMER_SATISFACTION_AND_FEATURE = "customer_satisfaction_and_feature",
    SALES_TREND = "salesTrend",
    TOTAL_ROWS = "totalRows",
    ROWS = "rows",
    COMBINED_RESPONSE = "combinedResponse",
    VISIBILITY_TRENDS = "visibility_trends",
    MY_PRODUCT_TREND_CROSS_CHANNEL = "my_product_trend_cross_channel",
    MY_PRODUCT_TREND_COMPETITION = "my_product_trend_competition",
    PERFORMANCE_COMPARISON_CTR_TREND = "performance_comparision_ctr_trend",
    KEYWORD_PERFORMANCE_GRAPH = "keyword_performance_graph",
    KEYWORD_PERFORMANCE_GRAPH_COMPETITION = "keyword_performance_graph_competition",
    ORG_SKU_WT_CRAWL_SV = "org_sku_wt_crawl_sv",
    PAID_SKU_WT_CRAWL_SV = "paid_sku_wt_crawl_sv",
    ORG_CAT_WT_CRAWL_SV = "org_cat_wt_crawl_sv",
    PAID_CAT_WT_CRAWL_SV = "paid_cat_wt_crawl_sv",
    MY_SKU_DATA = "my_sku_data",
    COMPETITION_DATA = "competition_data",
    CROSS_CHANNEL_AND_DIRECT_COMPETITOR_DATA = "cross_channel_and_direct_competitor_data",
    MS_TREND_OVER_TIME = "ms_trend_over_time",
    MY_BRAND_CROSS_CHANNEL_GRAPH = "my_brand_cross_channel_graph",
    MY_BRAND_COMPETITION_GRAPH = "my_brand_competition_graph",
    COMPARISON_GRAPH = "comparison_graph",
    CATEGORY_DATA = "category_data",
}

export enum FrequencySlicerOptions {
    Daily = "Daily",
    Weekly = "Weekly",
    Monthly = "Monthly",
}

export type PERIOD_OPTIONS = {
    label: string;
    value: DATE_PICKER_PRESET_PERIODS;
};

export interface ISpendRoasData {
    spendRoasData?: {
        chartDataSeries: {
            byAdType: {
                categories: string[];
                series: {
                    name: string;
                    data: number[];
                    color: string;
                    yAxis: number;
                    dataLabels?: {
                        enabled: boolean;
                        formatter: () => string;
                    };
                    tooltip?: {
                        valueSuffix: string;
                    };
                }[];
            };
        };
        delta: {
            SP: {
                roas: number;
                spend: number;
            };
            SD: {
                roas: number;
                spend: number;
            };
        };
    };
}

export enum ProductDeepdiveOverviewSections {
    OVERVIEW = "overview",
    SALES_TREND = "salesTrend",
    FUNNEL = "funnel",
    SPEND_ROAS_DATA = "spendRoasData",
    REVIEWS = "reviews",
    DIGITAL_SHELF = "digitalShelf",
}

export enum StateTypeValues {
    ENABLED = "enabled",
    PAUSED = "paused",
    ARCHIVED = "archived",
}

export type TMediaPlanConfig = {
    plan_id: number;
    plan_name: string;
    plan_budget: string;
    plan_month: string;
    plan_level: string[];
    plan_version: number;
};

export enum DefaultMediaPlanLevel {
    ACCOUNT = "Account",
    CATEGORY = "Category",
    AD_TYPE = "Ad Type",
    PRODUCT = "Product",
}

export type TGeneratePlanConfig = Record<
    string,
    {
        fieldColumn: string;
        gridHeight?: number;
    }
>;

export type TMonthOption = {
    label: string;
    value: string;
};

export type TMediaPlanList = {
    plan_id: number;
    plan_name: string;
    plan_year: number;
    plan_month: string;
    plan_budget: number;
    plan_levels: string[];
    created_at: string;
    created_by: string;
    plan_details: TMediaPlanRowData;
    latest_plan_version: number;
    user_id: string;
};

export type TMediaPlanEditConfig = Record<string, Record<string, string[]>>;

export type TPlanList = {
    rowData: TMediaPlanList[];
};

export interface MediaPlanPerformanceMetrices {
    impression?: number;
    achieved_impression?: number;
    achieved_impression_perc?: number;
    click?: number;
    achieved_click?: number;
    achieved_click_perc?: number;
    sale?: number;
    achieved_sale?: number;
    achieved_sale_perc?: number;
    spend?: number;
    achieved_spend?: number;
    achieved_spend_perc?: number;
    orders?: number;
    achieved_orders?: number;
    achieved_orders_perc?: number;
    cr?: number;
    achieved_cr?: number;
    ctr?: number;
    achieved_ctr?: number;
    cpc?: number;
    achieved_cpc?: number;
    roas?: number;
    achieved_roas?: number;
    troas?: number;
    achieved_troas?: number;
    aov?: number;
    achieved_aov?: number;
}

export interface IMediaPlanRowObj extends MediaPlanPerformanceMetrices {
    recordKey?: string;
    isRowModified?: boolean;
    [x: string]: number | string | boolean;
}

export type TMediaPlanRowData = Record<string, IMediaPlanRowObj[]>;

export type TMappedPlanData = Map<string, Map<string, IMediaPlanRowObj>>;

export type TMediaPlanChangeLogs = Record<string, TPlanLevelChanges>;

export type TPlanLevelChanges = Record<string, TPlanRecordChanges>;

export type TPlanRecordChanges = Record<
    string,
    {
        from: number;
        to: number;
    }
>;

export interface ICreateMediaPlan {
    planConfig: {
        mediaPlanBudget: number;
        planMonth: number;
        planName: string;
        planVersion?: number;
        planId?: number;
    };
    planData: TMediaPlanRowData;
}

export interface IEditMediaPlan extends ICreateMediaPlan {
    changeLogs: TMediaPlanChangeLogs;
}

export type TPlanTrackingConfig = {
    plan_id: number;
    plan_month: string;
    plan_year: number;
    plan_levels: string[];
};

export enum PlanMetrics {
    SPEND = "spend",
    SALE = "sale",
    CLICK = "click",
    ORDERS = "orders",
    IMPRESSION = "impression",
    CTR = "ctr",
    CPC = "cpc",
    CPM = "cpm",
    CR = "cr",
    AOV = "aov",
    ROAS = "roas",
    TROAS = "troas",
}

export interface IClientJob {
    id: number;
    client_id: number;
    record_type: string | null;
    status: boolean;
    source: string;
    channel_id: number;
    cron_string: string;
    period: string;
    job_class: string;
    job_priority: string;
    schedule_timezone: string | null;
    created_on: string;
    modified_on: string;
    report_name: string | null;
    client_name: string;
    channel_name: string;
    last_execution_status: string | null;
    last_stage_status: string | null;
    last_execution_date: string | null;
    last_execution_timestamp: string | null;
    last_execution_start: string | null;
    last_execution_end: string | null;
    last_execution_duration: string | null;
    tracker_record_type: string | null;
    task_uid: string | null;
    group_uid: string | null;
    execution_details: string | null;
    program_type: string | null;
    last_report_date: string | null;
}

export enum ClientJobTableColumns {
    ID = "ID",
    STATUS = "Status",
    SOURCE = "Source",
    RECORD_TYPE = "Record Type",
    SCHEDULE = "Schedule",
    SCHEDULE_TIMEZONE = "Schedule Timezone",
    PERIOD = "Period (days)",
    PRIORITY = "Priority",
    CREATED_ON = "Created On",
    MODIFIED_ON = "Modified On",
    REPORT_NAME = "Report Name",
    HISTORY = "History",
    JOB_TYPE = "Job Type",
    CHANNEL = "Channel",
    JOB_CLASS = "Job Class",
    CLIENT = "Client",
    LAST_EXECUTION_STATUS = "Last Execution Status",
    LAST_STAGE_STATUS = "Last Stage Status",
    LAST_EXECUTION_DATE = "Last Execution Date",
    LAST_REPORT_DATE = "Last Report Date",
    LAST_UPDATE = "Last Update",
    EXECUTION_DURATION = "Execution Duration",
    TASK_UID = "Task UID",
    GROUP_UID = "Group UID",
    EXECUTION_DETAILS = "Execution Details",
    PROGRAM_TYPE = "Program Type",
}

export enum ClientIntegrationColumns {
    CHANNEL = "Channel",
    PLATFORM_NAME = "Platform Name",
    PARENT_ACCOUNT_ID = "Parent Account ID",
    ACCOUNT_ID = "Account ID",
    USERNAME = "Username",
    STATUS = "Status",
    REGISTERED_ON = "Registered On",
    ACTIONS = "Actions",
}

export interface IClientJobForm {
    report_name: string;
    job_type: string;
    period: string;
    priority: string;
    channel: string;
    schedule: string;
    status: boolean;
}

export interface IChannelReport {
    sys_report_id: string;
    report_name: string;
    report_module_name: string;
    platform_name: string;
    record_type: string;
    is_valid: boolean;
    common_name: string;
    report_priority: number;
}

export interface IBrand {
    id: number;
    name: string;
    sys_registered_by: string;
    sys_last_updated: string;
}

export interface ICreateBrandPayload {
    name: string[];
}

export enum CardTitles {
    spend = "Spend",
    sale = "Sales",
    roas = "RoAS",
}

export interface IKeywordVisibilityInputData {
    keyword: string;
    channel_id: number;
    indexed_organic: string;
    page_number: number;
    organic_rank: number | null;
    overall_rank: number;
    sponsored_rank: number | null;
    paid_kwd_wt_crawl_sv: number | null;
    org_kwd_wt_crawl_sv: number | null;
    paid_cat_wt_crawl_sv: number | null;
    org_cat_wt_crawl_sv: number | null;
}

export interface IKeywordVisibilityFormattedData {
    key: string;
    title: string;
    indexed: string;
    delta_organic_impr: string;
    delta_paid_impr: string;
    delta_overall_impr: string;
    organic_time_page1: string;
    sponsored_time_page1: string;
    organic_top5_rank: string;
    sponsored_top5_rank: string;
}

export interface ISovDataItem {
    sku: string;
    bcg_class: string;
    org_kwd_wt_crawl_sv?: number;
    paid_kwd_wt_crawl_sv?: number;
    org_cat_wt_crawl_sv?: number;
    paid_cat_wt_crawl_sv?: number;
}

export interface ISovFormattedData {
    categories: string[];
    series: ICTRSeriesItem[];
}

export interface ICTRGroupedDataItem {
    impression: number;
    click: number;
    sale: number;
    spend: number;
    orders: number;
}

export interface ICTRSeriesItem {
    name: string;
    type?: string;
    data: number[];
    color?: string;
    yAxis?: number;
}

export interface ICTRFormattedData {
    categories: string[];
    series: ICTRSeriesItem[];
}

export interface IPerformanceComparisionRawDataItem {
    date: string;
    impression: number;
    click: number;
    sale: number;
    spend: number;
    orders: number;
    program_type: string;
}

export interface IPerformanceComparisionAggregatedDataItem {
    impression: number;
    clicks?: number;
    sales: number;
    spend?: number;
    orders?: number;
    program_type: string;
    crawl_date?: string;
    roas?: number;
    revenue?: number;
    availability?: boolean;
    buybox_win?: boolean;
    sku?: string;
}

export interface IPerformanceComparisonData {
    date: string;
    impression: number;
    ctr: number;
    roas: number;
    conversionRate: number;
    programType: string;
}

export enum VisibilityTableColumnKeys {
    Title = "title",
    Indexed = "indexed",
    DeltaOrganicImpr = "delta_organic_impr",
    DeltaPaidImpr = "delta_paid_impr",
    DeltaOverallImpr = "delta_overall_impr",
    OrganicTimePage1 = "organic_time_page1",
    OrganicTop5Rank = "organic_top5_rank",
    SponsoredTimePage1 = "sponsored_time_page1",
    SponsoredTop5Rank = "sponsored_top5_rank",
    Action = "action",
}

export interface ICtrTrendOverTimeProps {
    widgetData: ICTRFormattedData;
    additionalTitleComponent?: () => JSX.Element;
    filterPillsComponent?: () => JSX.Element;
}

export enum VisibilityFilterOptionKeys {
    SOV_TYPE = "SOV Type",
    KEYWORD_TYPE = "Keyword Type",
    AD_TYPE = "AD Type",
    PAGE_RANK = "Page Rank",
}

export enum SOVTypeValues {
    OVERALL = "overall",
    PAID = "paid",
    ORGANIC = "organic",
}

export enum KeywordTypeValues {
    BRANDED = "branded",
    GENERIC = "generic",
    COMPETITION = "competition",
    CUSTOM = "custom",
}

export enum PageRankValues {
    TOP_5 = "top_5",
    TOP_10 = "top_10",
    PAGE_1 = "page_1",
}

export interface IAddBrandModalProps {
    clientId: number;
    visible: boolean;
    onSuccess: () => void;
    onCancel: () => void;
}

export interface TIntegrationPayload {
    platform_status: boolean;
    account_username?: string;
    password?: string;
    unq_account_id?: string;
    fk_ba_account_id?: string;
    fk_aa_account_id?: string;
    [key: string]: string | boolean | undefined;
}
export interface PaginationState {
    current: number;
    pageSize: number;
    total: number;
}

export enum BrandTableColumns {
    ID = "id",
    NAME = "name",
    REGISTERED_BY = "sys_registered_by",
    LAST_UPDATED = "sys_last_updated",
}

export enum ClientIntegrationFields {
    CHANNEL_NAME = "channel_name",
    JOB_PLATFORM_NAME = "job_platform_name",
    ACCOUNT_ID = "account_id",
    PARENT_ACCOUNT_ID = "parent_account_id",
    ACCOUNT_USERNAME = "account_username",
    PLATFORM_STATUS = "platform_status",
    REGISTERED_ON = "registered_on",
    ACTIONS = "actions",
}

export enum ClientJobFormFields {
    CHANNEL = "channel",
    REPORT_NAME = "report_name",
    PERIOD = "period",
    JOB_PRIORITY = "job_priority",
    CRON_STRING = "cron_string",
    ID = "id",
    STATUS = "status",
    SOURCE = "source",
    RECORD_TYPE = "record_type",
    CREATED_ON = "created_on",
    MODIFIED_ON = "modified_on",
    HISTORY = "history",
    SCHEDULE_TIMEZONE = "schedule_timezone",
}

export interface JobsData {
    loading: boolean;
    jobs: IClientJob[];
    pagination: PaginationState;
    searchQuery: string;
}

export interface BrandsData {
    loading: boolean;
    brands: IBrand[];
    pagination: PaginationState;
    searchQuery: string;
}

export enum ClientTabs {
    DETAILS = "details",
    BRANDS = "brands",
    INTEGRATIONS = "integrations",
    MEMBERS = "members",
    JOBS = "jobs",
}

export enum AdminSettingsTabs {
    GENERAL = "general",
    USERS = "users",
    ROLES = "roles",
    CLIENTS = "clients",
    ACTIVITY_LOGS = "activity-logs",
}

export interface ChannelIntegrationConfig {
    id: number;
    name: string;
    logo: React.ReactElement;
    color?: string;
    accountTypes: {
        type: string;
        enabled: boolean;
    }[];
    authFields: {
        id: string;
        required: boolean;
        label: string;
        placeholder?: string;
    }[];
    oauthEnabled: boolean;
}
export interface IBBWinnersDistributionOriginalData {
    merchant_name: string;
    avg_price: number;
}

export interface IBBWinnersDistributionWidgetData {
    name: string;
    value: number;
    color: string;
    subCat?: string;
}

export interface IAvailabilityMySkuData {
    sku: string;
    crawl_date: string;
    price: number;
    unit_per_day: number;
    cat_upd: number;
    revenue: number;
    cat_revenue: number;
    availability: boolean;
    merchant_name: string[];
    sub_category: string | null;
    buybox_win: boolean;
    ad_spends: number | null;
    ad_sales: number | null;
}

export interface IAvailabilityCrossChannelAndDirectCompetitorData {
    crawl_date: string;
    sku: string;
    channel_id: number;
    price: number;
    unit_per_day: number | null;
    cat_upd: number | null;
    revenue: number | null;
    cat_revenue: number | null;
    availability: boolean;
    merchant_name: string[];
    sub_category: string | null;
    buybox_win: boolean;
}

export interface IAvailabilityCompetitionData {
    crawl_date: string;
    sku: string;
    channel_id: number;
    price: number;
    unit_per_day: number | null;
    cat_upd: number | null;
    revenue: number | null;
    cat_revenue: number | null;
    availability: boolean;
    merchant_name: string | null;
    sub_category: string | null;
    buybox_win: boolean | null;
}

export interface IAvailabilityBuyboxSummaryData {
    sku: string;
    buybox_win_rate: number;
    buybox_win_rate_change: number;
}

export interface IAvailabilitySummaryData {
    sku: string;
    availability_percent: number;
    availability_change: number;
    out_of_stock_days: number;
}

export interface IAvailabilityEstimatedLossSummaryData {
    sku: string;
    current_total_sales_loss: number;
    percentage_change: number;
}

export interface IOtherSellerData {
    sku: string;
    total_sellers: number | null;
    [key: `seller_${number}`]: string;
    [key: `seller_${number}_rating`]: number;
    [key: `seller_${number}_review`]: number;
    [key: `seller_${number}_positiveFeedback`]: number;
    mrp: number;
    [key: `seller_${number}_deliveryDate`]: string;
    [key: `avg_seller_${number}_price`]: number;
}

export type TOtherSellerFormattedData = {
    [key: string]: string | number;
    seller: string;
    rating: number;
    review: number;
    positiveFeedback: number;
    discountedPrice: number;
    mrp: number;
    deliveryDate: string;
};

export interface IOtherSellersDataModalWidgetProps {
    title: string;
    show: boolean;
    setShow: (show: boolean) => void;
    widgetData: TOtherSellerFormattedData[];
}

export interface OutOfStockMarketShareWidgetProps {
    title?: string;
    widgetData: {
        categories: string[];
        series: {
            name: string;
            data: number[];
            color: string;
        }[];
        skuLists: string[];
    };
    maxDate?: number;
}

export enum PriceImpactBuyboxWinChartType {
    Price = "Price",
    BBWin = "Buybox Win %",
}

export enum AVAILABILITY_CHARTS {
    AVAILABILITY_TRENDS_OVERTIME = "availability_trends_over_time",
    MY_PRODUCT_VS_COMPETITION = "my_product_vs_competition",
    OUT_OF_STOCK_MARKET_SHARE_DYNAMICS = "out_of_stock_market_share_dynamics",
}

export interface IAvailabilityData {
    my_sku_data: IAvailabilityMySkuData[];
    competition_data: IAvailabilityCompetitionData[];
    widget_buybox_data_resp: IAvailabilityBuyboxSummaryData[];
    widget_avail_data_resp: IAvailabilitySummaryData[];
    widget_estimated_loss_resp: IAvailabilityEstimatedLossSummaryData[];
    widget_dailog_buybox_data_resp: IOtherSellerData[];
    cross_channel_and_direct_competitor_data: IAvailabilityCrossChannelAndDirectCompetitorData[];
    performance_comparision_ctr_trend: IPerformanceComparisionRawDataItem[];
    buy_box_winner_distribution: IBBWinnersDistributionOriginalData[];
}

export type TResponseWithStatusCode = { response?: any; statusCode: number };

export enum RuleFilterType {
    DATE = "date",
    SET = "set",
    IN_RANGE = "inRange",
    RELATIVE = "relative",
}

export enum RuleFilterTextProps {
    DATE_FROM = "dateFrom",
    DATE_TO = "dateTo",
    FILTER = "filter",
    FILTER_TO = "filterTo",
    NOT_IN = "not in",
    IN = "in",
    CONDITION_1 = "condition1",
    CONDITION_2 = "condition2",
    VALUES = "values",
}

export enum Nature {
    AUTO = "auto",
    MANUAL = "manual",
}

export enum ActionType {
    PERC_INC = "percentIncrease",
    PERC_DEC = "percentDecrease",
    ABSOLUTE = "absolute",
}

export type TCheckboxValueType = string | number | boolean;

export type TSelectedReportRowDataForAccessManagement = {
    report_name: string;
    report_id: string;
    embed_url: string;
    report_type_id: number;
    access_type: string;
    visibility?: string;
    report_order_id: number;
};

export interface IManageReportAccessModalProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    title: string;
    selectedReportData: TSelectedReportRowDataForAccessManagement;
    setGetUpdatedReports: React.Dispatch<React.SetStateAction<boolean>>;
    userData: IUserRecord;
}

export interface IAddReportAccessData {
    userId: number;
    reportId: string;
    status: boolean;
}

export interface IUserWithReportAccessData {
    user_id: number;
    first_name: string;
    last_name: string;
    email: string;
    report_id: string;
    client_id: number;
    visibility?: string;
}

export type TManageReportAccessInitialState = {
    usersWithAccessLoading: boolean;
    removeUserAccessLoading: boolean;
    addOrChangeAccessLoading: boolean;
};

export type TManageReportAccessActions =
    | { type: "SET_USERS_WITH_ACCESS_LOADING"; payload: boolean }
    | { type: "SET_REMOVE_USERS_ACCESS_LOADING"; payload: boolean }
    | { type: "SET_ADD_OR_CHANGE_ACCESS_LOADING"; payload: boolean };
export interface IBulkPlacementModalDisplay {
    bulkPlacementModalDisplay: {
        value: boolean;
    };
}

export interface IReportAccessUserSelectionOptions {
    label: string | React.ReactNode;
    value: number | string;
    disabled?: boolean;
}

export type TReportAccessTypeMenuItem = Required<MenuProps>["items"][number];

export enum ReportsAccessTypes {
    GENERAL = "general",
    RESTRICTED = "restricted",
}

export enum ReportVisibilityTypes {
    SHOW = "show",
    HIDE = "hide",
}
export interface IClientAccessUpdationBase {
    platforms?: string[];
    role_id?: number;
    privileges?: number[];
    channels?: number[];
}

export interface IClientAccessAddition extends IClientAccessUpdationBase {
    clients: number[];
    platforms: string[];
    role_id: number;
    privileges: number[];
    channels: number[];
}

export interface IClientAccessModification extends IClientAccessUpdationBase {
    client_id: number;
}

export interface IClientChanges {
    additions: IClientAccessAddition[];
    deletions: number[];
    modifications: IClientAccessModification[];
}

export interface IProductMappingListParams {
    startRow: number;
    endRow: number;
    sortModel: any[];
    filterModel: any;
}

export interface IPfmSkuFilterParams {
    filterModel: any;
    recordField?: string;
}

export enum VcpmColumns {
    CATEGORY = "Category",
    BRAND = "Brand",
    SUBCATEGORY = "Subcategory",
    SKU_ID = "sku_identifier",
    TAGS = "tags",
    CATEGORY_1DS = "category_1ds",
    SUBCATEGORY_1DS = "subcategory_1ds",
    BRAND_1DS = "brand_1ds",
    PACK_SIZE_BAND = "pack_size_band",
    PRICE_BAND = "price_band",
}

export enum KwdColumns {
    KEYWORD = "keyword",
    SEARCH_VOLUME = "aggregated_search_volume",
    CATEGORY = "category_list",
    SUBCATEGORY = "subcategory_list",
    BRAND = "brand_list",
    CATEGORY_1DS = "category_list_1ds",
    SUBCATEGORY_1DS = "subcategory_list_1ds",
    BRAND_1DS = "brand_list_1ds",
}

export enum GlobalTagRecordType {
    CAMPAIGN = "campaign",
    PRODUCT = "product",
    GLOBAL = "global",
}

export enum RevinitiEnv {
    PROD = "production",
    DEV = "development",
    STAGING = "staging",
}
export interface VisibilityMyProductTrendData {
    sku: string;
    crawl_date: string;
    sku_title: string;
    channel: string;
    price: number;
    org_sku_wt_crawl_sv: number | null;
    paid_sku_wt_crawl_sv: number | null;
    org_cat_wt_crawl_sv: number | null;
    paid_cat_wt_crawl_sv: number | null;
}
export enum CATEGORY_OVERVIEW_CHARTS {
    BEST_SELLER_SKUS = "best_seller_skus",
    SUB_CATEGORY_PERFORMANCE = "sub_category_performance",
}

export enum CATEGORY_OVERVIEW_SKU_FILTERS {
    REVENUE = "revenue",
    UNITS_SOLD = "units_sold",
}

export enum SUB_CAT_PERFORMANCE_FILTERS {
    REVENUE = CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE,
    UNITS_SOLD = CATEGORY_OVERVIEW_SKU_FILTERS.UNITS_SOLD,
    TOP_5 = "top_5",
    TOP_10 = "top_10",
    TOP_20 = "top_20",
    PAGE_1 = "page_1",
}

export enum SUB_CAT_PERFORMANCE_FILTERS_RADIO_GROUPS {
    MS_BY = "ms_by",
    TOP_N_FOR_SOV = "top_n_for_sov",
}

export interface ISubcategoryPerformanceTableCols {
    key: string;
    subcategory: string;
    msPercentage: string;
    marketSize: number;
    adSales: string;
    adSpends: string;
    roas: number;
    organicSOV: string;
    paidSOV: string;
    shareOfVoice: string;
    pricing: number;
    rating: number;
    reviews: number;
    availability: string;
    buybox: string;
    stock: number;
}

export interface ICategoryInsightFilters {
    tag_category: string;
    tag_subcategory: string;
    brand: string;
    feature: string;
}

export interface ICategoryMarketShareAndSizeData {
    prev_brand_upd: number;
    prev_market_size_by_upd: number;
    prev_upd_share: number;
    prev_brand_revenue: number;
    prev_market_size_by_revenue: number;
    prev_revenue_share: number;
    cur_brand_upd: number;
    cur_market_size_by_upd: number;
    cur_upd_share: number;
    cur_brand_revenue: number;
    cur_market_size_by_revenue: number;
    cur_revenue_share: number;
}

export enum CATEGORY_OVERVIEW_MS_OFFTAKE_RADIO {
    MS_PERCENT = "ms_percent",
    OFFTAKE = "offtake",
}

export interface ICategoryOverviewMarketShareAndSizeFormattedData {
    marketShare: {
        revenueValue: number | string;
        revenueDelta: number;
        unitsValue: number;
        unitsDelta: number;
        chartPoints: {
            revenue: number[];
            units: number[];
        };
    };
    offtake: {
        revenueValue: number | string;
        revenueDelta: number;
        unitsValue: number | string;
        unitsDelta: number;
        chartPoints: {
            revenue: number[];
            units: number[];
        };
    };
    marketSize: {
        revenueValue: number | string;
        revenueDelta: number;
        unitsValue: number | string;
        unitsDelta: number;
        chartPoints: {
            revenue: number[];
            units: number[];
        };
    };
}
export interface ISovRatingBuyboxReview {
    cur_rating: number;
    prev_rating: number;
    cur_buybox_win: number;
    prev_buybox_win: number;
    cur_org_sov: number;
    cur_paid_sov: number;
    prev_org_sov: number;
    prev_paid_sov: number;
    current_review_count: number;
    previous_review_count: number;
    cur_availability: number;
    prev_availability: number;
    cur_search_volume: number;
    prev_search_volume: number;
    cur_rpi: number;
    prev_rpi: number;
    category_share: number;
}

export interface ISubCategoryPerformance {
    tag_subcategory: string;
    upd: number;
    revenue: number;
    upd_size: number;
    revenue_size: number;
    org_sov: number;
    paid_sov: number;
    overall_sov: number;
    rating: number;
    buybox_win: number;
    review_count: number;
    availability: number;
    rpi: number;
    sale: number;
    spend: number;
    roas: number;
    ctr: number;
    cpc: number;
    orders: number;
    click: number;
    impression: number;
    stock_in_hand: number | null;

    prev_upd: number | null;
    prev_revenue: number | null;
    prev_upd_size: number | null;
    prev_revenue_size: number | null;
    prev_org_sov: number | null;
    prev_paid_sov: number | null;
    prev_overall_sov: number | null;
    prev_rating: number | null;
    prev_buybox_win: number | null;
    prev_review_count: number | null;
    prev_availability: number | null;
    prev_rpi: number | null;
    prev_sale: number | null;
    prev_spend: number | null;
    prev_roas: number | null;
    prev_ctr: number | null;
    prev_cpc: number | null;
    prev_orders: number | null;
    prev_click: number | null;
    prev_impression: number | null;
    prev_stock_in_hand: number | null;
}

export interface ISaleSpendRoas {
    cur_sale: number;
    cur_spend: number;
    cur_roas: number;
    prev_sale: number;
    prev_spend: number;
    prev_roas: number;
}

export interface ICategoryOverviewSerpData {
    sov_rating_buybox_review: ISovRatingBuyboxReview[];
    sub_category_performance_rating_buybox_review_rpi: ISubCategoryPerformance[];
    sale_spend_roas: ISaleSpendRoas[];
}

export type ValuesAndDeltaMetric = { value: number | string; delta: number };

export interface IFormattedCategoryOverviewSummaryWidgetData {
    [CommonWidgetMetrics.SALES]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.SPENDS]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.ROAS]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.ORGANIC_SOV]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.PAID_SOV]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.SEARCH_VOLUME]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.PRICING_RPI]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.RATING]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.REVIEW_COUNT]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.AVAILABILITY]: ValuesAndDeltaMetric;
    [CommonWidgetMetrics.BUYBOX]: ValuesAndDeltaMetric;
}

export interface ICategoryOverviewSummaryMetrics
    extends IFormattedCategoryOverviewSummaryWidgetData {
    categoryContribution?: number | string;
}

export interface ICategoryOverviewSummaryData {
    marketShare: {
        value: number;
        delta: number;
        chartPoints: number[];
    };
    marketSize: {
        value: number;
        delta: number;
        chartPoints: number[];
    };
    summaryWidget: ICategoryOverviewSummaryMetrics;
}

export enum CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO {
    MS_PERCENT = "ms_percent",
    ROAS = "roas",
    SALES = "sales",
    AD_SPENDS = "ad_spends",
    TOTAL_SALES = "total_sales",
}

export interface IFormattedCategoryOverviewMsTrendsOverTimeData {
    [key: string]: {
        [subKey: string]:
            | {
                  categories: string[];
                  yAxisData: number[];
                  yAxisOppositeData: number[];
              }
            | object;
    };
}

export interface ICategoryOverviewMSTrendsOverTimeData {
    crawl_date: string;
    upd: number;
    cat_upd: number;
    revenue: number;
    cat_revenue: number;
    roas: number;
    sale: number;
    spend: number;
}

export interface MetricWithDelta {
    value: number | string;
    delta: number;
}

export interface IFormattedSubCategoryPerformance {
    [CommonWidgetMetrics.SUB_CATEGORY]: string;
    [CommonWidgetMetrics.MS]: {
        revenue: MetricWithDelta;
        units: MetricWithDelta;
    };
    [CommonWidgetMetrics.MARKET_SIZE]: {
        revenue: MetricWithDelta;
        units: MetricWithDelta;
    };
    [CommonWidgetMetrics.AD_SALES]: MetricWithDelta;
    [CommonWidgetMetrics.AD_SPENDS]: MetricWithDelta;
    [CommonWidgetMetrics.ROAS]: MetricWithDelta;
    [CommonWidgetMetrics.ORGANIC_SOV]: MetricWithDelta;
    [CommonWidgetMetrics.PAID_SOV]: MetricWithDelta;
    [CommonWidgetMetrics.SOV]: MetricWithDelta;
    [CommonWidgetMetrics.RPI]: MetricWithDelta;
    [CommonWidgetMetrics.RATING]: MetricWithDelta;
    [CommonWidgetMetrics.REVIEWS]: MetricWithDelta;
    [CommonWidgetMetrics.AVAILABILITY]: MetricWithDelta;
    [CommonWidgetMetrics.BUYBOX]: MetricWithDelta;
    [CommonWidgetMetrics.STOCK]: MetricWithDelta;
    [CommonWidgetMetrics.COMPARE]: string;
}

export interface ICategoryOverviewDrainers {
    tag_subcategory: string;
    cur_upd_ms: number;
    cur_revenue_ms: number;
    prev_upd_ms: number;
    prev_revenue_ms: number;
    cur_sale: number;
    cur_spend: number;
    cur_roas: number;
    cur_ctr: number;
    cur_cpc: number;
    cur_orders: number;
    cur_click: number;
    cur_impression: number;
    prev_sale: number;
    prev_spend: number;
    prev_roas: number;
    prev_ctr: number;
    prev_cpc: number;
    prev_orders: number;
    prev_click: number;
    prev_impression: number;
    prev_total_sales: number;
    cur_total_sales: number;
}

export interface IFormattedDrainerData {
    [CommonWidgetMetrics.SUB_CATEGORY]: string;
    [CommonWidgetMetrics.MS]: MetricWithDelta;
    [CommonWidgetMetrics.TOTAL_SALES]: MetricWithDelta;
    [CommonWidgetMetrics.AD_SALES]: MetricWithDelta;
    [CommonWidgetMetrics.AD_SPENDS]: MetricWithDelta;
    [CommonWidgetMetrics.ROAS]: MetricWithDelta;
    [CommonWidgetMetrics.CTR]: MetricWithDelta;
    [CommonWidgetMetrics.CTC]: MetricWithDelta;
    [CommonWidgetMetrics.CLICKS]: MetricWithDelta;
    [CommonWidgetMetrics.ORDERS]: MetricWithDelta;
    [CommonWidgetMetrics.IMPRESSIONS]: MetricWithDelta;
}

export type IDrainersRowData = {
    [key in CommonWidgetMetrics]?:
        | string
        | {
              value: string;
              deltaText?: string;
              delta?: number;
          };
};

export interface IDrawerWithTabeWidget {
    title: string;
    show: boolean;
    setShow: (value: boolean) => void;
    setShowMyBrandTrends?: (value: boolean) => void;
    colDefs: ColDef[];
    rowData: TAgGridRowData;
    showTitleActionBtns?: boolean;
    showDeltaValues?: boolean;
    setShowDeltaValues?: (value: boolean) => void;
}

export interface ICategoryOverviewCrossChannelGraphData {
    crawl_date: string;
    tag_subcategory: string;
    name: string;
    total_sales: number;
    cur_price: number;
    upd: number;
    cat_upd: number;
    revenue: number;
    cat_revenue: number;
    sale: number;
    spend: number;
    roas: number;
}

export interface ICategoryOverviewCompetitionTableData {
    brand: string;
    tag_subcategory: string;
    channel_id: number;
    cur_ms_upd: number;
    cur_ms_revenue: number;
    prev_ms_upd: number;
    prev_ms_revenue: number;
    cur_total_sales: number;
    prev_total_sales: number;
    cur_sale: number;
    cur_spend: number;
    cur_roas: number;
    prev_sale: number;
    prev_spend: number;
    prev_roas: number;
}

export interface ICategoryOverviewCompetitionGraphData {
    crawl_date: string;
    tag_subcategory: string;
    brand: string;
    channel_id: number;
    total_sales: number;
    cur_price: number;
    upd: number;
    cat_upd: number;
    revenue: number;
    cat_revenue: number;
    sale: number;
    spend: number;
    roas: number;
}

export interface ICategoryOverviewComparisonGraphData {
    tag_subcategory: string;
    crawl_date: string;
    upd_ms: number;
    revenue_ms: number;
    sale: number;
    spend: number;
    total_sales: number;
    roas: number;
}

export interface ICategoryOverviewComparisonGraphFormattedData {
    [CommonWidgetMetrics.TAG_SUBCATEGORY]: string;
    [CommonWidgetMetrics.CRAWL_DATE]: string;
    [AggregationField.MS_PERCENT]: number;
    [AggregationField.ROAS]: number;
    [AggregationField.SALE]: number;
    [AggregationField.SPEND]: number;
    [AggregationField.TOTAL_SALES]: number;
}

export interface IcategoryOverviewChartDataSeries {
    chartDataSeries: {
        categories: string[];
        series: {
            name: string;
            data: number[];
        }[];
    };
    skuTitles: {
        [AggregationField.MS_PERCENT]: string;
        [AggregationField.ROAS]: string;
        [AggregationField.SALE]: string;
        [AggregationField.SPEND]: string;
        [AggregationField.TOTAL_SALES]: string;
    };
}

export type TCategoryOverviewComparisonGraphFormattedDataForXAxisOpposite = {
    [AggregationField.MS_PERCENT]?: IcategoryOverviewChartDataSeries;
    [AggregationField.ROAS]?: IcategoryOverviewChartDataSeries;
    [AggregationField.SALE]?: IcategoryOverviewChartDataSeries;
    [AggregationField.SPEND]?: IcategoryOverviewChartDataSeries;
    [AggregationField.TOTAL_SALES]?: IcategoryOverviewChartDataSeries;
};

export interface IWidgetWithTableParams {
    title: string;
    columns: any[];
    data: any[];
    chart?: CATEGORY_OVERVIEW_CHARTS;
    selectedRadioFilter?: CATEGORY_OVERVIEW_SKU_FILTERS | Record<string, any>;
    setSelectedRadioFilters?: (value: CATEGORY_OVERVIEW_SKU_FILTERS) => void;
    showRadioButtons?: boolean;
    showDeltaValueCheck?: boolean;
    showDeltaValues?: boolean;
    setShowDeltaValues?: (value: boolean) => void;
    showTopNSovFilters?: boolean;
    showFilterMenu?: boolean;
    customGridHeight?: number;
}

export interface IBestSellerSKUData {
    sku: string;
    sku_title: string;
    tag_subcategory: string;
    unit_per_day: number | null;
    revenue: number | null;
    price: number | null;
    org_sku_wt_crawl_sv: number | null;
    paid_sku_wt_crawl_sv: number | null;
    org_cat_wt_crawl_sv: number | null;
    paid_cat_wt_crawl_sv: number | null;
}

export type TTrendDefaultData = {
    categories: string[];
    yAxisData: number[];
    yAxisOppositeData?: number[];
};

export interface ICategoryAdvFilters {
    [feature: string]: string[];
}

export interface ICategoryOverviewBrandContributionData {
    brand: string;
    tag_subcategory: string;
    unit_per_day: number;
    revenue: number;
    sub_cat_unit_per_day: number;
    sub_cat_revenue: number;
}

export type TBrandContributionFormattedData = {
    [CATEGORY_OVERVIEW_SKU_FILTERS.UNITS_SOLD]: IBBWinnersDistributionWidgetData[];
    [CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE]: IBBWinnersDistributionWidgetData[];
};

export interface IChartDataCategoriesAndSeries {
    categories: string[];
    series: {
        name: string;
        data: number[];
        color?: string;
    }[];
}

export type TCategoryOverviewSubCategoriesData =
    | {
          [CATEGORY_OVERVIEW_SKU_FILTERS.REVENUE]: IChartDataCategoriesAndSeries;
          [CATEGORY_OVERVIEW_SKU_FILTERS.UNITS_SOLD]: IChartDataCategoriesAndSeries;
      }
    | object;

export interface ICategoryOverviewCrossChannelCompetitionComparisonGraphItem {
    [CommonWidgetMetrics.TAG_SUBCATEGORY]: string;
    [CommonWidgetMetrics.CRAWL_DATE]: string;
    [AggregationField.MS_PERCENT]: number;
    [AggregationField.ROAS]: number;
    [AggregationField.SALE]: number;
    [AggregationField.SPEND]: number;
    [AggregationField.TOTAL_SALES]: number;
    [AggregationField.REVENUE]?: number;
    [AggregationField.CAT_REVENUE]?: number;
    [CommonWidgetMetrics.NAME]?: string;
    [CommonWidgetMetrics.BRAND]?: string;
}

export interface ICategoryOverviewBaseTableItem {
    tag_subcategory: string;
    cur_ms_upd: number | null;
    cur_ms_revenue: number | null;
    prev_ms_upd: number | null;
    prev_ms_revenue: number | null;
    cur_sale: number | null;
    cur_spend: number | null;
    cur_roas: number | null;
    cur_total_sales: number | null;
    prev_total_sales: number | null;
    prev_sale: number | null;
    prev_spend: number | null;
    prev_roas: number | null;
}
export interface ICategoryOverviewCrossChannelTableItem
    extends ICategoryOverviewBaseTableItem {
    name: string;
}

export interface ICategoryOverviewCompetitionTableItem
    extends ICategoryOverviewBaseTableItem {
    brand: string;
    channel_id: number;
}
export interface ICategoryOverviewComparisonTableItem
    extends ICategoryOverviewBaseTableItem {
    cur_upd_ms: number | null;
    cur_revenue_ms: number | null;
    prev_upd_ms: number | null;
    prev_revenue_ms: number | null;
}
export interface ICategoryOverviewBestSellerSKUTableRow {
    sku: string;
    title: string;
    rank: number | string;
    price: number | string;
}
export interface ICategoryOverviewFormattedData {
    brandContribution: TBrandContributionFormattedData;
    msTrendsOverTime: IFormattedCategoryOverviewMsTrendsOverTimeData;
    subCategoriesContribution: TCategoryOverviewSubCategoriesData;
    marketShareAndSize: ICategoryOverviewMarketShareAndSizeFormattedData[];
    crossChannelGraph: ICategoryOverviewCrossChannelCompetitionComparisonGraphItem[];
    crossChannelTable: ICategoryOverviewCrossChannelTableItem[];
    competitionGraph: ICategoryOverviewCrossChannelCompetitionComparisonGraphItem[];
    competitionTable: ICategoryOverviewCompetitionTableItem[];
    summaryWidget: ICategoryOverviewSummaryMetrics;
    subCategoryPerformance: IFormattedSubCategoryPerformance[];
    drainers: IFormattedDrainerData[];
    comparisonTable: ICategoryOverviewComparisonTableItem[];
    comparisonGraph: ICategoryOverviewCrossChannelCompetitionComparisonGraphItem[];
    bestSellerSku: ICategoryOverviewBestSellerSKUTableRow[];
}

export interface ICategoryOverviewData {
    [FREQUENCY_SLICER_OPTIONS.DAILY_DATA]: ICategoryOverviewFormattedData;
    [FREQUENCY_SLICER_OPTIONS.WEEKLY_DATA]: ICategoryOverviewFormattedData;
    [FREQUENCY_SLICER_OPTIONS.MONTHLY_DATA]: ICategoryOverviewFormattedData;
}
export interface ICategoryAnalysisOverviewModule {
    loadingState: TPaModuleLoadingState;
    overviewData: ICategoryOverviewFormattedData;
    channel: number;
    currencySymbol: string;
    selectedBrands: string[];
    selectedCategory?: string[];
    selectedSubCategory: string[];
}

export type TCategoryOverviewRadioFiltersInitialState = {
    msTrendsOverTimeRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS;
    brandContributionRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS;
    subCategoriesContributionRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS;
    bestSellerSkuRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS;
    subCatPerformanceRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS;
    msOfftakeRadioFilter: CATEGORY_OVERVIEW_MS_OFFTAKE_RADIO;
    revenueUnitsRadioFilter: CATEGORY_OVERVIEW_SKU_FILTERS;
    msTrendsOvertimeMetricsRadioFilter: CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO;
};

export type TCategoryOverviewRadioFiltersActions =
    | { type: "SET_MS_TRENDS_OVERTIME"; payload: CATEGORY_OVERVIEW_SKU_FILTERS }
    | { type: "SET_BRAND_CONTRIBUTION"; payload: CATEGORY_OVERVIEW_SKU_FILTERS }
    | {
          type: "SET_SUB_CATEGORY_CONTRIBUTION";
          payload: CATEGORY_OVERVIEW_SKU_FILTERS;
      }
    | { type: "SET_BEST_SELLER_SKU"; payload: CATEGORY_OVERVIEW_SKU_FILTERS }
    | {
          type: "SET_SUB_CAT_PERFORMANCE";
          payload: CATEGORY_OVERVIEW_SKU_FILTERS;
      }
    | { type: "SET_MS_OFFTAKE"; payload: CATEGORY_OVERVIEW_MS_OFFTAKE_RADIO }
    | { type: "SET_SUMMARY_WIDGET"; payload: CATEGORY_OVERVIEW_SKU_FILTERS }
    | {
          type: "SET_MS_TRENDS_OVERTIME_METRICS";
          payload: CATEGORY_OVERVIEW_MS_TRENDS_OVERTIME_RADIO;
      };

export type TCategoryOverviewShowHideStatesInitialState = {
    showDrainers: boolean;
    showDrainersDeltaValues: boolean;
    showSubCatPerformanceDeltaValues: boolean;
    showMyBrandTrends: boolean;
};

export type TCategoryOverviewShowHideStatesActions =
    | { type: "SET_SHOW_DRAINERS"; payload: boolean }
    | { type: "SET_SHOW_DRAINERS_DELTA_VALUES"; payload: boolean }
    | { type: "SET_SHOW_SUBCAT_PERFORMANCE_DELTA_VALUES"; payload: boolean }
    | { type: "SET_SHOW_MY_BRANDS_TRENDS"; payload: boolean };

export interface ICategoryOverviewSummaryWidgetParams {
    showFilter?: boolean;
    selectedMSOfftakeRadio: CATEGORY_OVERVIEW_MS_OFFTAKE_RADIO;
    setSelectedMSOfftakeRadio: (
        value: CATEGORY_OVERVIEW_MS_OFFTAKE_RADIO
    ) => void;
    selectedRevenueUnitsRadio: CATEGORY_OVERVIEW_SKU_FILTERS;
    setSelectedRevenueUnitsRadio: (
        value: CATEGORY_OVERVIEW_SKU_FILTERS
    ) => void;
    selectedBrands: string[];
    widgetData: ICategoryOverviewSummaryData;
    setShowDrainers: (value: boolean) => void;
    sovFilterRequired?: boolean;
    setShowMyBrandTrends: (value: boolean) => void;
}

export interface IDeltaValuesTextRendererParams {
    changeValue: number | null | undefined;
    addBrackets?: boolean;
    fontSize?: string;
    textBold?: boolean;
}

export interface IAxisWithOpposite extends Highcharts.Axis {
    opposite?: boolean;
}

export type ProductAndCategoryAnalyticsModuleType =
    | ProductDeepdiveModules.OVERVIEW
    | ProductDeepdiveModules.RATINGS_REVIEWS
    | ProductDeepdiveModules.PRICING
    | ProductDeepdiveModules.VISIBILITY;

export type TApply1DSMappingConfig = {
    skuChannel: {
        channel: number;
        sku: number | string;
    }[];
    filterModel?: Record<string, any>;
    updateParams: string[];
};

export enum Fixed1DSMappingCols {
    CATEGORY_1DS = "category_1ds",
    SUBCATEGORY_1DS = "subcategory_1ds",
    BRAND_1DS = "brand_1ds",
}

export enum Tag1DSMappingCols {
    PACK_SIZE_BAND = "pack_size_band",
    PRICE_BAND = "price_band",
}

export interface ITagMappingTableStats {
    tag_type: string;
    channel_id: number;
    channel_name: string;
    tag_perc: number;
}

export interface ITagMappingStats {
    top_line: number;
    table_data_visibility: Array<ITagMappingTableStats>;
    table_data_upd: Array<ITagMappingTableStats>;
}

export interface KeywordMappingList {
    sys_keyword_id: number;
    keyword: string;
    category_list: string[];
    subcategory_list: string[];
    brand_list: string[];
    aggregated_search_volume: number;
    category_1ds?: string[];
    subcategory_1ds?: string[];
    brand_1ds?: string[];
    category?: string;
    subcategory?: string;
    brand?: string;
}

export enum TagStatusFilters {
    VISIBILITY_SCORE = "visibility_score",
    SALES_INDEX = "upd",
}

export enum TagWorkerType {
    FILTER_TAGS = "FILTER_TAGS",
    PROCESS_TAGS = "PROCESS_TAGS",
}

export enum TagWorkerResult {
    FILTER_TAGS_RESULT = "FILTER_TAGS_RESULT",
    PROCESS_TAGS_RESULT = "PROCESS_TAGS_RESULT",
}

export enum TableWorkerType {
    FILTER_DATA = "FILTER_DATA",
}

export enum TableWorkerResult {
    FILTER_DATA_RESULT = "FILTER_DATA_RESULT",
}

export interface ITagWorkerPayload {
    tagTypeList?: any[];
    recordType?: string;
    validRecordTypes?: string[];
    tags?: string[];
    inputValue?: string;
    type?: string;
    payload?: any;
}

export interface IcategoryOverviewCrossChannelCompetitonComparisonData {
    [CommonWidgetMetrics.NAME]?: string;
    [CommonWidgetMetrics.BRAND]?: string;
    [CommonWidgetMetrics.TAG_SUBCATEGORY]: string;
    [CommonWidgetMetrics.CUR_MS_UPD]: number;
    [CommonWidgetMetrics.CUR_UPD_MS]?: number;
    [CommonWidgetMetrics.CUR_MS_REVENUE]: number;
    [CommonWidgetMetrics.CUR_REVENUE_MS]?: number;
    [CommonWidgetMetrics.PREV_MS_UPD]: number;
    [CommonWidgetMetrics.PREV_MS_REVENUE]: number;
    [CommonWidgetMetrics.CUR_SALE]: number;
    [CommonWidgetMetrics.CUR_SPEND]: number;
    [CommonWidgetMetrics.CUR_ROAS]: number;
    [CommonWidgetMetrics.CUR_TOTAL_SALES]: number;
    [CommonWidgetMetrics.PREV_TOTAL_SALES]: number;
    [CommonWidgetMetrics.PREV_SALE]: number;
    [CommonWidgetMetrics.PREV_SPEND]: number;
    [CommonWidgetMetrics.PREV_ROAS]: number;

    // for ComparisonTableData
    [CommonWidgetMetrics.PREV_UPD_MS]?: number;
}

export type TFormattedCategoryOverviewCrossChannelCompetitonComparisonData =
    Required<
        Omit<
            IcategoryOverviewCrossChannelCompetitonComparisonData,
            "name" | "brand"
        >
    > & { name?: string; brand?: string };

export type TCatOverviewCrossChannelCompetitionComparisonTablesMetricKey =
    (typeof catOverviewCompareTablesMetricsToSum)[number];

export type TCatOverviewCrossChannelCompetitionComparisonAggEntry = {
    [K in "name" | "brand"]?: string;
} & {
    tag_subcategory: string;
} & Record<
        TCatOverviewCrossChannelCompetitionComparisonTablesMetricKey,
        number
    >;

export interface IMainSeriesItem {
    name: string;
    y: number;
    drilldown: string;
    color: string;
}

export interface IDrilldownDataPoint {
    [category: string]: string | number;
    0: string;
    1: number;
}

export interface IDrilldownSeriesItem {
    id: string;
    name: string;
    type: "column";
    data: [string, number][];
    color: string;
}

export interface IAvailabilityChartData {
    mainSeries: IMainSeriesItem[];
    drilldownSeries: IDrilldownSeriesItem[];
}

export interface IAvailabilityDrilldownChartProps {
    widgetData: IAvailabilityChartData;
    onBarClickHandler: (
        option: string,
        supportedOption: { subcategory: string; city: string }
    ) => void;
    isQcommChannel: boolean;
}

export interface IAvailabilitySalesLossItem {
    name: string;
    availability: number;
    salesLoss: number;
    color: string;
}

export interface ISalesLossWidgetProps {
    widgetData: {
        byRegion: IAvailabilitySalesLossItem[];
        bySubCategory: IAvailabilitySalesLossItem[];
    };
    onBarClickHandler: (
        option: string,
        supportedOption: { city: string }
    ) => void;
}

export interface ICityWiseAvailabilityData {
    city: string;
    assortment: number;
    listingStatus: string;
    availability: string;
    salesLoss: string;
}

export interface IAvailabilityByPincode {
    product: string;
    region: string;
    pincode: string;
    listingStatus: string;
    availability: string;
    stockInHand: number;
    asp: string;
}

export interface IMyBrandVsCompetitionData {
    brand: string;
    availabilityPercentage: number;
    buyboxPercentage: number;
}

export interface ICategoryAvailabilitySubcategoryWidgetProps {
    citywiseAvailabilityAndSalesLossWidgetData: ICityWiseAvailabilityData[];
    availabilityByPincodeWidgetData: IAvailabilityByPincode[];
    myBrandVsCompetitionData: {
        [key: string]: IMyBrandVsCompetitionData[];
    };
    isQcommChannel: boolean;
    selectedSubCategories: string[];
    oosMsBySubCategoryData: {
        [key: string]: OutOfStockMarketShareWidgetProps["widgetData"];
    };
    productsTableData: {
        [key: string]: any;
    };
    onWidgetLinkHandler: (
        event: React.MouseEvent<HTMLSpanElement, MouseEvent>
    ) => void;
}

export type TAvailabilityIssuesByCityData = {
    city: string;
    data: number[];
    color: string;
};

export type TAvailabilityIssuesByCityDataResult = {
    below50: {
        categories: string[];
        data: TAvailabilityIssuesByCityData[];
    };
    fiftyToEighty: {
        categories: string[];
        data: TAvailabilityIssuesByCityData[];
    };
    above80: {
        categories: string[];
        data: TAvailabilityIssuesByCityData[];
    };
};

export type TAvailabilityEcommData = {
    crawl_date: string;
    sku: string;
    sku_title: string;
    channel_id: number;
    channel_name: string;
    curr_availability: boolean | number;
    curr_price: number | null;
    merchant_name: string | null;
    curr_upd: string | null;
    curr_revenue: number | null;
    category: string;
    brand: string;
    sub_category: string;
    buybox_win: boolean | null | number;
    sale: number | null;
    spend: number | null;
    click: number | null;
    impression: number | null;
    curr_drr_upd: string | null;
    curr_drr_revenue: number | null;
    sales_rank: number;
    curr_mrp: number | null;
};

export enum DeepDiveModuleType {
    PRODUCT = "product",
    CATEGORY = "category",
}

export type AvailabilityCompetitionData = {
    crawl_date?: string;
    brand?: string;
    sku_title?: string;
    ms_percent: number;
    availability: number;
    buybox_win?: number;
    buybox?: number;
    roas: number;
    sale: number;
    spend: number;
};

export enum AvailabilityDrawerRadioOptions {
    WINNERS = "winners",
    LOSERS = "losers",
}

export type TBuyboxWinnerAndLosersData = {
    product: string;
    subCategory: string;
    seller: string;
    sellerType: string | null;
    sellerRating: string | null;
    discountedPrice: string;
    mrp: string;
};

export type TopSubcategoriesWithEstimatedSalesLossEcommRow = {
    subCategory: string;
    availability: string;
    estimatedSalesLoss: string;
    totalProducts: number;
    oosProducts: number;
};
export type TopProductsWithEstimatedSalesLossEcommRow = {
    title: string;
    subCategory: string;
    latestSr: number | string;
    availabilityStatus: "Available" | "Not Available";
    estimatedSalesLoss: number;
    latestBBwin: string;
};

export enum DeepDiveModuleTypes {
    CATEGORY_QCOMM = "category_qcomm",
    CATEGORY_ECOMM = "category_ecomm",
}

export enum REPORTS_TYPES {
    THREE_P_REPORT = "3p-report",
    COMMON = "common",
}

export enum REPORTS_TYPES_IDS {
    THREE_P_REPORT = 1,
    COMMON = 2,
}

export enum categoryOverviewTrendsFilters {
    SUB_CATEGORY = "sub_category",
    brand = "brand",
}

export type TSubCategoryAvailabilityData = {
    subCategory: string;
    value: number;
};

export enum AvailabilityDrilldownTypes {
    BELOW50 = "below50",
    FIFTY_TO_EIGHTY = "fiftyToEighty",
    ABOVE80 = "above80",
}

export interface IAvailabilityDrawerState {
    isVisible: boolean;
    title: {
        primaryTable: string;
        secondaryTable: string;
    };
    filtersRequired: {
        subCat: boolean;
        city: boolean;
        radioOptions: boolean;
    };
    tableData: {
        rowData: {
            primaryTable: TAgGridRowData;
            secondaryTable: TAgGridRowData;
        };
        colDefs: {
            primaryTable:
                | typeof topSubcatEstimatedLossEcommColDefs
                | typeof buyboxWinnersAndLosersAvailabilityColDefs
                | typeof oosProductsColDefs;
            secondaryTable:
                | typeof topProductsWithLossEcommColDefs
                | typeof topProductsWithEstimatedLossColDefs;
        };
    };
}

export interface ISparklineChartProps {
    data: any;
    height?: number;
    width?: number;
    tooltipTitle?: string;
    tooltipValueFormatter?: (value: number) => string;
    key?: string;
    color?: string;
    fillColor?: string;
}

export enum GraphTypes {
    TREND = "trend",
    BAR = "bar",
    PIE = "pie",
}

// Revstack Types

export interface IMainFilterState {
    isOpen: boolean;
    appliedFilters: { [key: string]: Set<string> };
    draftFilters: { [key: string]: Set<string> };
    activeIndex: number;
    searchText: string;
}

export interface IFilterData {
    label: string;
    value: number;
    options: TOptionsString[];
}

export interface IFilterTag {
    filterLabel: string;
    value: string;
    displayLabel: string;
}

export interface IAdditionalFiltersHandlers {
    onOpenModal: () => void;
    onCloseModal: () => void;
    onSetActiveIndex: (index: number) => void;
    onSetSearchText: (text: string) => void;
    onUpdateDraftFilter: (payload: {
        label: string;
        option: string;
        checked: boolean;
    }) => void;
    onApplyFilters: () => void;
    onClearAllFilters: () => void;
    onRemoveTag: (payload: { label: string; value: string }) => void;
}

export interface DateConfig {
    currentRange: string[];
    previousRange: string[];
    granularity: string;
}

export interface CubeTemplate {
    queryType?: string;
    sectionId?: string;
    payload?: {
        timeDimensions: Array<{
            dimension: string;
            compareDateRange?: string[][];
            dateRange?: string[];
            granularity?: string;
        }>;
        filters: any;
        measures: CommonWidgetMetrics[];
        dimensions: string[];
    };
}

export interface IDatePickerInputValidationObject {
    invalid: boolean;
    message: string;
}

export interface IDatePickerInputValidationInitialState {
    primaryStart: IDatePickerInputValidationObject;
    primaryEnd: IDatePickerInputValidationObject;
    compareStart: IDatePickerInputValidationObject;
    compareEnd: IDatePickerInputValidationObject;
}

export type TDatePickerInputValidationAction =
    | {
          type: "SET_INVALID";
          field: keyof IDatePickerInputValidationInitialState;
          value: { invalid: boolean; message: string };
      }
    | { type: "RESET_ALL" };

export type TDatePickerInputFocusState = "primary" | "compare" | null;

export type TCustomDatePickerInputState = {
    [key in CUSTOM_DATE_PICKER_INPUT_FIELDS]: string;
};

export type TCustomDatePickerInputAction = {
    type: "SET_INPUT";
    field: CUSTOM_DATE_PICKER_INPUT_FIELDS;
    value: string;
};

export type TSelectedDateRange = {
    dateRange: { start: string; end: string };
    compareDateRange: { start: string; end: string };
    selectedRange: DATE_PICKER_PRESET_PERIODS;
};

export enum CubeQueryType {
    VALUE_DELTA = "valueDelta",
    DIMENSION_VALUE_DELTA = "dimensionValueDelta",
    DIMENSION_TIME_SERIES = "dimensionTimeSeries",
    DIMENSION_BAR_CHART = "dimensionBarChart",
    DIMENSION_PIVOT_CONSOLIDATION = "dimensionPivotConsolidation",
    SERIES = "series",
    DIMENSION_PIE_CHART = "dimensionPieChart",
    MULTI_MEASURE_VALUE_DELTA = "multiMeasureValueDelta",
}

//TODO: This to be replace with a generic name
export enum CA_DD_MODULES {
    OVERVIEW = "overview",
    SUB_CATEGORIES = "sub-categories",
    BRAND_ANALYTICS = "brand-analysis",
    SKU_ANALYTICS = "sku-analysis",
    CROSS_REGION = "cross-region",
    CROSS_CHANNEL = "cross-channel",
    AI_INSIGHTS = "ai-insights",
    KEYWORD_ANALYSIS = "keyword-analysis",
    COMPETITOR_ANALYSIS = "competitor-analysis",
}

export enum CatAnalyticsSubCatDrillDownContentViews {
    OVERVIEW_VIEW = "overview",
    TABLE_VIEW = "table",
    TRENDS_VIEW = "trends",
}

export enum FilterPosition {
    START = "start",
    END = "end",
}

export type TCatAnalyticsSubCatDrillDownTabKey = CA_DD_MODULES;

export type TSubCatDrillDownConfig = {
    [key in TCatAnalyticsSubCatDrillDownTabKey]: {
        entity: string;
        filters:
            | {
                  activeSubTab: CatAnalyticsSubCatDrillDownContentViews;
                  search: string;
                  hideCompetition: boolean;
                  chartType: CatAnalyticsSubCatDrillDownContentViews;
                  selectedChannels?: string[];
                  selectedYAxisMetrics?: string;
                  selectedYAxisOppositeMetrics?: string;
              }
            | object;
    };
};

export interface IRevstackChannels {
    id: number;
    name: string;
    logo_url: string | null;
    domain: string;
}

export enum WidgetType {
    W0 = "w0",
    W1 = "w1",
    W2 = "w2",
    W3 = "w3",
    W4 = "w4",
    W5 = "w5",
    W6 = "w6",
    W7 = "w7",
    W8 = "w8",
    W9 = "w9",
}

export enum WidgetDimensions {
    VALUE = "value",
    CHANGE = "change",
    SERIES = "series",
    NAME = "name",
    TIME_DIMENSION_LABELS = "timeDimensionLabels",
}

export interface IRevstackChannelState {
    revstackChannel: {
        value: number[];
    };
}

export enum RevstackFilterTypes {
    BEACON = "beacon",
    TRENDS = "trends",
    RADIO = "radio",
    SELECT = "select",
    MULTI_SELECT = "multiSelect",
    CHART_TYPE = "chartType",
    SHOW_TRENDS = "showTrends",
    DRILL_DOWN = "drillDown",
    DEEP_DIVE = "deepDive",
    DROPDOWN_RADIO = "dropdownRadio",
}

export enum RevstackCubeIds {
    PO_REGIONAL = "sku_details",
    KA_NATIONAL = "keyword_analytics_national",
    DD_OVERVIEW_TRENDS = "drill_down_overview_trends",
}

export enum RevstackDashboardModules {
    CATEGORY_ANALYSIS = "category-analysis",
    PRODUCT_ANALYSIS = "product-analysis",
    NAVIGATOR = "navigator",
}

export enum RevstackDashboardSubModules {
    OVERVIEW = "overview",
    TRAFFIC = "traffic",
    CONVERSION = "conversion",
    OPERATIONS = "operations",
    PRODUCT_SELECTION_LIST = "product-selection-list",
    PRODUCT_CHANGE_LIST = "product-change-list",
    AI_INSIGHTS = "ai-insights",
}

export enum RevstackCASectionIds {
    BANNER = "banner",
    PO = "performance-overview",
    TO = "traffic-overview",
    CO = "conversion-overview",
    COMV = "comparison-overview",
    OO = "operation-overview",
    CP = "category-performance",
    CPT = "category-performance-table",
    CA = "category-analysis",
    SA = "subcategory-analysis",
    PA = "product-analysis",
    RA = "regional-analysis",
    PS = "performance-summary",
    PP = "product-performance",
    RP = "regional-performance",
    BD = "brandwise-distribution",
    KD = "keyword-distribution",
    SOVT = "sov-trends",
    KTT = "keyword-type-trends",
    CRT = "category-rpi-trends",
    COMPT = "competition-trends",
    SOVD = "sov-distribution",
    SV = "search-volume",
    MBVC = "my-brand-vs-competition",
    SP = "subcategory-performance",
    SCP = "subcategory-performance",
    RWP = "regional-level-performance",
    CWP = "channel-wise-performance",
    DD = "drill-down",
    DDS = "drill-down-subcategory",
    DDSO = "drill-down-subcategory-overview",
    DDST = "drill-down-subcategory-trends",
    DDSCTA = "drill-down-subcategory-table",
    DDSBTA = "drill-down-subcategory-brand-table",
    DDSBTR = "drill-down-subcategory-brand-trends",
    DDSSTA = "drill-down-subcategory-sku-table",
    DDSKATA = "drill-down-subcategory-keyword-analysis-table",
    DDSSTR = "drill-down-subcategory-sku-trends",
    DDSCRTA = "drill-down-subcategory-cross-region-table",
    DDSCRTR = "drill-down-subcategory-cross-region-trends",
    DDSCCTA = "drill-down-subcategory-cross-channel-table",
    DDSCCTR = "drill-down-subcategory-cross-channel-trends",
    DDSCOMTA = "drill-down-subcategory-competition-table",
    DDCSO = "drill-down-conv-subcategory-overview",
    DDCST = "drill-down-conv-subcategory-trends",
    DDCSBTA = "drill-down-conv-subcategory-brand-table",
    DDCSBTR = "drill-down-conv-subcategory-brand-trends",
    DDCSSTA = "drill-down-conv-subcategory-sku-table",
    DDCSKATA = "drill-down-conv-subcategory-keyword-analysis-table",
    DDCSSTR = "drill-down-conv-subcategory-sku-trends",
    DDCSCRTA = "drill-down-conv-subcategory-cross-region-table",
    DDCSCRTR = "drill-down-conv-subcategory-cross-region-trends",
    DDCSCCTA = "drill-down-conv-subcategory-cross-channel-table",
    DDCSCCTR = "drill-down-conv-subcategory-cross-channel-trends",
    DDOSO = "drill-down-opr-subcategory-overview",
    DDOST = "drill-down-opr-subcategory-trends",
    DDOSBTA = "drill-down-opr-subcategory-brand-table",
    DDOSBTR = "drill-down-opr-subcategory-brand-trends",
    DDOSSTA = "drill-down-opr-subcategory-sku-table",
    DDOSKATA = "drill-down-opr-subcategory-keyword-analysis-table",
    DDOSSTR = "drill-down-opr-subcategory-sku-trends",
    DDOSCRTA = "drill-down-opr-subcategory-cross-region-table",
    DDOSCRTR = "drill-down-opr-subcategory-cross-region-trends",
    DDOSCCTA = "drill-down-opr-subcategory-cross-channel-table",
    DDOSCCTR = "drill-down-opr-subcategory-cross-channel-trends",
    KP = "keyword-performance",
    DDTSO = "drill-down-trends-subcategory-overview",
    DDTSBTA = "drill-down-trends-subcategory-brand-table",
    DDTSSTA = "drill-down-trends-subcategory-sku-table",
    DDTSCRTA = "drill-down-trends-subcategory-cross-region-table",
    DDTSCCTA = "drill-down-trends-subcategory-cross-channel-table",
    // Category Analysis Trends Modal Section IDs
    DDTSOA = "drill-down-trends-subcategory-overview-analysis",
    DDTSBA = "drill-down-trends-subcategory-brand-analysis",
    DDTSSA = "drill-down-trends-subcategory-sku-analysis",
    DDTSCRA = "drill-down-trends-subcategory-cross-region-analysis",
    DDTSCCA = "drill-down-trends-subcategory-cross-channel-analysis",
    // Navigator Trends Modal Section IDs
    TPOA = "trends-performance-overview-analysis",
    TCOMVA = "trends-comparison-overview-analysis",
    CD = "channel-distribution",
    PT = "pricing-trends",
    DT = "discount-trends",
    AT = "availability-trends",
    LS = "Listing Status",
    // Navigator DrillDown Trends Section IDs
    NDDTOA = "navigator-drill-down-trends-overview-analysis",
    NDDTSSA = "navigator-drill-down-trends-subcategory-analysis",
    NDDTBA = "navigator-drill-down-trends-brand-analysis",
    NDDTSA = "navigator-drill-down-trends-sku-analysis",
    NDDTCA = "navigator-drill-down-trends-cross-region-analysis",
    PSL = "product-selection-list",
    PSCL = "product-change-list",
    MSVC = "my-sku-vs-competition",
    // Product Analysis DrillDown Section IDs
    DDPOA = "drill-down-product-overview-analysis",
    DDPCTA = "drill-down-product-competition-table-analysis",
    DDPOCA = "drill-down-product-cross-region-analysis",
    DDPOCCA = "drill-down-product-cross-channel-analysis",

    INOTE = "info-note",
}

export interface WidgetConfig {
    type: WidgetType;
    title: string;
    subTitle?: string;
    infoMessage?: string;
    icon?: string | React.ReactElement;
    filterOptions?: any;
    drilldown?: any;
    formatter?: any;
    widgetComponent: React.ComponentType<any>;
    isTopCardRequired?: boolean;
    widgetType?: string;
    config?: {
        dataKeyMap?: {
            mktShare?: string;
            catCont?: string;
        };
        card?: {
            topCard?: {
                name?: string;
                value?: string;
                change?: string;
                color?: string;
            };
        };
        table?: {
            type?: string;
            dataKeyMap?: Record<string, string>;
            defaultFilter?: string;
            filterOptions?: Array<{
                label: string;
                value: string;
                sortOrder?: "asc" | "desc";
                filterConfig?: {
                    condition?: {
                        operater: string;
                        value: number;
                    };
                    field?: string;
                };
            }>;
            drilldown?: {
                dataKeyMap?: Record<string, any[]>;
            };
        };
        relatedComponentDataKeys?: string[];
    };
    queryMetaInfo?: {
        resultKey?: string;
    };
    tabKey?: string;
    identifier?: string;
    measures?: any[];
    queryDimensions?: string[];
    excludeFilter?: string[];
}

export interface WidgetData {
    [key: string]: any;
}

export type PlacementKey =
    | "placementTop"
    | "placementProductPage"
    | "placementRest";

export interface IConfirmModalState {
    value: boolean;
    title?: string;
    message?: string;
    onConfirm?: () => void;
}

export interface IConfirmModalProps {
    modalData: IConfirmModalState;
}

export interface ICampaignsSelectionData {
    campaignsSelectionData: {
        value: Campaign[];
    };
}

export enum TagColFields {
    CAMPAIGN_TAGS = "campaign_tags",
    PRODUCT_TAGS = "sku_tags",
    TAG_TYPES = "tag_types",
}

export enum CUBE_OPERATORS {
    EQUALS = "equals",
    NOT_EQUALS = "not_equals",
    CONTAINS = "contains",
    NOT_CONTAINS = "not_contains",
    STARTS_WITH = "starts_with",
    ENDS_WITH = "ends_with",
    COMPARE = "compare",
}

export enum CUBE_FILTER_TYPES {
    DIMENSION = "dimension",
    TIME_DIMENSION = "timeDimension",
}

export enum DASHBOARD_TYPES {
    DRILL_DOWN = "drilldown",
    DRILL_DOWN_TRENDS_MODAL = "drilldown_trends_modal",
    BASE = "base",
}

export interface NewWidgetConfig {
    identifier: string;
    title: string;
    subTitle?: string;
    infoMessage?: string;
    isMeasureAppendRequired?: boolean;
    measures?: string[];
    type: WidgetType;
    queryDimensions?: string[];
    laggardField?: string;
    filters?: CuberFilterConfig[];
    // filterOptions?: any[];    // can be added in config
    drilldown?: any;
    widgetComponent: React.ComponentType<any>;
    // isTopCardRequired?: boolean; // can be added in config
    config?: any;
    relativePeriodConfig?: {
        granularity: string;
        value: number;
    };
    queryMetaInfo?: {
        filterToSkip?: string;
        resultKey?: string;
        listLimit?: number;
        // sortMeasure?: string; // can be added in sortConfig as sortKey
        recordKey?: string;
        sortConfig?: {
            sortKey: string;
            sortLevels: {
                key: string;
                sortOrder: string;
                limit: number;
            }[];
        };
    };
}

export interface CuberFilterConfig {
    cubeId: RevstackCubeIds;
    type: CUBE_FILTER_TYPES;
    field: string;
    operator: CUBE_OPERATORS;
    values: () => any[];
}

export interface DashboardSectionConfig {
    icon: React.ReactNode;
    title: string;
    sectionId: RevstackCASectionIds;
    cubeId: RevstackCubeIds;
    widget: React.ComponentType<any>;
    widgets: NewWidgetConfig[];
    drilldownId?: RevstackCASectionIds;
    config?: any;
    filters?: CuberFilterConfig[];
}

export interface DashboardConfig {
    dashBoard: RevstackDashboardModules;
    filters: CuberFilterConfig[];
    sections: DashboardSectionConfig[];
}

export interface Measure {
    value: string;
    label: string;
    unit: string;
    changeUnit: string;
    decimal?: number;
    isMeasureDependent: boolean;
    isCategorical?: boolean;
    dependencies?: string[];
    group?: string;
    isActive: boolean;
    description?: string;
    invertChangeSign?: boolean;
}

export enum CatAnlyticsSubCatDrillDownContentViews {
    OVERVIEW_VIEW = "overview_view",
    TABLE_VIEW = "table_view",
    TRENDS_VIEW = "trends_view",
}

export enum GRID_COLUMNS {
    CAMPAIGN_STATUS = "campaign_status",
    STATE = "state",
    IS_ARCHIVED_ENTITY = "is_archived_entity",
}

export enum COMPONENT_CONFIG_KEYS {
    TILL_BUDGET_ENDS = "till_budget_ends",
    END_DATE = "end_date",
}
export type TRevstackFiltersSearchState = {
    categories: string;
    subcategories: string;
    brands: string;
};

export enum RevstackFiltersSearchActions {
    SET_SEARCH = "SET_SEARCH",
    RESET_SEARCH = "RESET_SEARCH",
}

export type TRevstackFiltersSearchAction =
    | {
          type: RevstackFiltersSearchActions.SET_SEARCH;
          tab: keyof TRevstackFiltersSearchState;
          value: string;
      }
    | { type: RevstackFiltersSearchActions.RESET_SEARCH };
export enum TableExportFormats {
    CSV = "csv",
    EXCEL = "excel",
}
export interface IRevstackFilterOption {
    value: number;
    label: string;
    category?: number;
    subcategory?: number; // for subcategory-level entries
    subcategories?: number[]; // for brand-level entries (array!)
    brandType?: string;
}
export interface KeywordTag {
    id: number;
    name: string;
}
export interface KeywordMappingConfig {
    keywords?: number[];
    filterModel?: Record<string, any>;
    category_list?: number[];
    subcategory_list?: number[];
    brand_list?: number[];
    operation: KeywordMappingOperation;
    client_subgroup_id?: number;
    user_name?: string;
}

export enum KeywordMappingOperation {
    UPSERT = "upsert",
    REMOVE = "remove",
}
export enum DDColGroupKeys {
    OVERALL_PERFORMANCE = "Overall Performance",
    TRAFFIC = "Traffic",
    CONVERSION = "Conversion",
    OPERATIONS = "Operations",
}

export interface IW2DrilldownEntry {
    [key: string]: any;
    value: number;
    change: number;
}

export interface IW2DrilldownData {
    [key: string]: IW2DrilldownEntry[];
}

export interface IW2FormattedDrilldownEntry {
    subcategory: string;
    [key: string]:
        | {
              value: string;
              change: string;
              isPositive: boolean;
              valueOnClick: any;
          }
        | string;
}

export enum AdminSettings {
    DAY_PARTING = "day-parting",
    BUYBOX = "buybox",
    REVSTACK = "revstack",
    KEYWORD_RECOMMENDATION = "keyword-recommendation",
    NAVIGATOR = "navigator",
    CATEGORY_ANALYSIS = "category-analysis",
    PRODUCT_ANALYSIS = "product-analysis",
}

export enum RevstackFrequencySlicerOptions {
    Daily = "day",
    Weekly = "week",
    Monthly = "month",
}

export type TablePerfColDefType =
    | "ca_overview_subcategory"
    | "ca_traffic_subcategory"
    | "ca_overview_regional"
    | "ca_traffic_regional"
    | "ca_traffic_keyword"
    | "ca_conversion_subcategory"
    | "ca_conversion_regional"
    | "ca_operations_subcategory"
    | "ca_operations_regional"
    | "pa_overview_regional"
    | "mi_overview_ps_ca"
    | "mi_overview_ps_sa"
    | "mi_overview_ps_pa"
    | "mi_overview_ps_ra"
    | "dd_mi_ov_ps_bd";

export enum RevstackFilterKey {
    CATEGORY = "category_id",
    SUBCATEGORY = "subcategory_id",
    CROSS_CHANNEL_SKU_ID = "cross_channel_sku_id",
    BRAND = "brand_id",
    REGION = "region",
    CHANNEL = "channel_id",
    CLIENT_SUBGROUP = "client_subgroup_id",
    BRAND_FILTER = "brand_filter",
    SKU_FILTER = "sku_filter",
    SKU = "sku",
}

export enum SeriesGranularity {
    DAY = "day",
    WEEK = "week",
    MONTH = "month",
}

export enum SETTINGS_TABLE_DATA_TYPE {
    SELLERS = "Sellers",
    BRANDS = "Brands",
}

export enum COLUMN_VISIBILITY_MODE {
    GROUP = "group",
    COLUMNS = "columns",
}

export const ACTIVITY_TYPES = [
    "USER_MANAGEMENT",
    "CLIENT_MANAGEMENT",
    "ROLE_MANAGEMENT",
    "ROLE_PERMISSION_MANAGEMENT",
    "USER_PRIVILEGE_MANAGEMENT",
    "REPORT_MANAGEMENT",
    "FEATURE_MANAGEMENT",
    "USER_PREFERENCES",
    "BUSINESS_LOGIC",
    "TAG_MANAGEMENT",
];

export enum UserTabs {
    DETAILS = "details",
    ACCESS = "access",
    ACTIVITY_LOGS = "activity-logs",
}

export interface IKeywordCohort {
    id: string;
    keywordTypes: string[];
    keywords: IBCCKeyword[];
    negativeKeywords?: INegativeKeywordTarget[];
    categories?: ICategoryTarget[];
    createdAt?: string;
    updatedAt?: string;
}

export interface ICollection {
    id?: string;
    campaignType: string;
    keywordType: string | string[];
    keywords: string[] | any[];
    negative_keywords?: INegativeKeywordTarget[];
    categories?: ICategoryTarget[];
    keywordCohorts?: IKeywordCohort[];
    city: string | string[];
    timeSlot: string;
    budget: string;
    type: string;
    startDate?: any;
    campaigns?: Array<any>;
}

export interface IClusterFormData {
    id: string;
    name?: string;
    createdAt?: string;
    totalBudget?: string;
    startDate?: any;
    endDate?: any;
    budgetUsed?: number;
    collections: Array<ICollection>;
    products: Array<any>;
}

export interface ICreateCollectionsFormProps {
    clusterId: string;
    cluster: IClusterFormData;
    handleFieldChange: (
        clusterId: string,
        collectionId: string,
        field: string,
        value: any,
        record?: any
    ) => void;
    handleAddKeywords: (
        clusterId: string,
        collectionId: string,
        selectedKwTypes: string[]
    ) => void;
    handleEditCohort: (
        clusterId: string,
        collectionId: string,
        cohortId: string
    ) => void;
    setSelectedKeywords: (keywords: IBCCKeyword[]) => void;
    citiesList: IBCCCityList[];
    currentStep: number;
    handleAddAssets: (clusterId: string, collectionId: string) => void;
}

export interface ICampaignCollectionBreakdownProps {
    cluster: IClusterFormData;
    clusterId?: string;
    collectionId?: string;
    clientCurrencySymbol: string;
    handleCampaignUpdate: (
        collectionId: string,
        campaign: any,
        updates: any
    ) => void;
    showCombinedView?: boolean;
    citiesList?: Array<{
        city_id: number;
        city_name: string;
        city_offtake: number;
    }>;
    onAutoSuggestBudget?: (clusterId: string, collectionId?: string) => void;
}

export interface IBCCCityList {
    city_id: number;
    city_name: string;
    city_offtake: number;
    tier?: string;
    region?: string;
    zone?: string;
}

export interface CascaderOption {
    value: string;
    label: string;
    children?: CascaderOption[];
}

export interface IBCCProductSkuDetail {
    sku?: string;
    unit?: string;
    sku_group_variant_id?: string | number;
    isActive?: boolean;
    isActiveForSession?: boolean;
}

export interface IProductGroup {
    id: string;
    sku: string;
    sku_title: string;
    title: string;
    category: string;
    subcategory: string;
    msPercent: number;
    sovPercent: number;
    wtAvailabilityPercent: number;
    image: string;
    thumbnail: string;
    market_share_value: number;
    market_share_change: number;
    weighted_availability_value: number;
    weighted_availability_change?: number;
    market_share_pct: number;
    market_share_pct_change?: number;
    overall_sov: number;
    overall_sov_change?: number;
    weighted_availability_pct: number;
    weighted_availability_pct_change?: number;
    organic_sov: number;
    organic_sov_change?: number;
    paid_sov: number;
    paid_sov_change?: number;
    priority_value: number;
    variants_value: string[];
    unit: string;
    organicSov: number;
    sponsoredSov: number;
    avgRating: number;
    isComboGroup: boolean;
    isVariantGroup: boolean;
    skuDetails: IBCCProductSkuDetail[];
    expanded: boolean;
    [key: string]: any;
}

export interface IModalParams {
    clusterId?: string;
    collectionId?: string;
    selectedKwTypes?: string[];
    editingCohortId?: string | null;
}

export interface IStepMeta {
    title: string;
    description: string;
    accordionType: CLUSTER_ACCORDION_TYPES;
}

export interface IActiveStep extends IStepMeta {
    accordionType: CLUSTER_ACCORDION_TYPES;
}

export interface IBCCKeyword {
    keyword?: string;
    keyword_type?: string;
    clusterId?: string;
    id?: string;
    exact_match_bid?: number | null;
    smart_match_bid?: number | null;
    bid_booster?: number | null;
    isCustom?: boolean;
    key?: string;
    min_bid_limit?: number;
    max_bid_limit?: number;
    exact_suggested_min?: number;
    exact_suggested_max?: number;
    smart_suggested_min?: number;
    smart_suggested_max?: number;
    overall_rank?: number | null;
    organic_rank?: number | null;
    paid_rank?: number | null;
    search_vol?: number;
    organic_sov?: number;
    sponsored_sov?: number;
    overall_sov?: number;
    previous_overall_rank?: number | null;
    overall_rank_change?: number;
    previous_organic_rank?: number | null;
    organic_rank_change?: number;
    previous_paid_rank?: number | null;
    paid_rank_change?: number;
    previous_organic_sov?: number;
    organic_sov_change?: number;
    previous_sponsored_sov?: number;
    sponsored_sov_change?: number;
    previous_overall_sov?: number;
    overall_sov_change?: number;
    [key: string]: any;
}

export enum CREATE_CAMPAIGN_HANDLER_ACTIONS {
    BACK = "back",
    NEXT = "next",
    PUBLISH = "publish",
}

export interface ICampaignCreationContext {
    clusters: IClusterFormData[];
    activeTab: string;
    setActiveTab: (tab: string) => void;
    searchValue: string;
    setSearchValue: (value: string) => void;
    filteredClusters: IClusterFormData[];
    setFilteredClusters: (clusters: IClusterFormData[]) => void;
    productGroups: IProductGroup[];
    setProductGroups: (products: IProductGroup[]) => void;
    keywords: IBCCKeyword[];
    setKeywords: (keywords: IBCCKeyword[]) => void;
    filteredProducts: IProductGroup[];
    setFilteredProducts: (products: IProductGroup[]) => void;
    expandedClusters: string[];
    setExpandedClusters: (clusters: string[]) => void;
    currentStep: number;
    setCurrentStep: (step: number) => void;
    isLoadingProducts: boolean;
    setIsLoadingProducts: (loading: boolean) => void;
    isSavingCluster: boolean;
    isPublishingCampaigns: boolean;
    selectedProducts: IProductGroup[];
    setSelectedProducts: (products: IProductGroup[]) => void;
    selectedKeywords: IBCCKeyword[];
    setSelectedKeywords: (keywords: IBCCKeyword[]) => void;
    clusterName: string;
    setClusterName: (name: string) => void;
    isModalVisible: boolean;
    setIsModalVisible: (visible: boolean) => void;
    selectedClusters: string[];
    setSelectedClusters: (clusters: string[]) => void;
    selectedProductIds: string[];
    setSelectedProductIds: (ids: string[]) => void;
    stepMeta: IStepMeta[];
    activeStep: IActiveStep | null;
    handleModalCancel: () => void;
    handleProductSelection: (selectedRows: IProductGroup[]) => void;
    handleKeywordSelection: (selectedRows: IBCCKeyword[]) => void;
    handleCategorySelection: (selectedRows: ICategoryTarget[]) => void;
    handleNegativeKeywordSelection: (
        negativeKeywords: INegativeKeywordTarget[]
    ) => void;
    handleClusterSelection: (clusterId: string) => void;
    handleClusterDeselection: (clusterId: string) => void;
    handleProductSelectionInLeftPanel: (productId: string) => void;
    handleProductDeselection: (productId: string) => void;
    handleSaveCluster: (shouldAddCluster?: boolean) => Promise<void>;
    handleFieldChange: (
        clusterId: string,
        collectionId: string,
        field: string,
        value: any,
        record?: any
    ) => void;
    handleCampaignNameChange: (
        clusterId: string,
        collectionId: string,
        campaignId: string,
        name: string
    ) => void;
    handleAddKeywords: (
        clusterId: string,
        collectionId: string,
        selectedKwTypes: string[]
    ) => void;
    handleEditCohort: (
        clusterId: string,
        collectionId: string,
        cohortId: string
    ) => void;
    handleAddCustomKeyword: (keyword: IBCCKeyword) => void;
    handleSaveKeywords: () => void;
    handleCampaignCollectionBreakdown: (
        clusterId: string,
        collectionId: string,
        setIsRowExpanded: (expanded: boolean) => void,
        isRowExpanded: boolean
    ) => void;
    handleCreateNew: () => void;
    createCampaignsHandler: (action: CREATE_CAMPAIGN_HANDLER_ACTIONS) => void;
    handleToggleExpand: (clusterId: string) => void;
    modalParams: IModalParams;
    setModalParams: (
        params: IModalParams | ((prev: IModalParams) => IModalParams)
    ) => void;
    handleSearch: (value: string) => void;
    citiesList: IBCCCityList[];
    setCitiesList: (cities: IBCCCityList[]) => void;
    isNextStepDisabled: (config?: {
        clusterCount?: number;
        productCount?: number;
    }) => boolean;
    isStepAccessible: (targetStep: number) => boolean;
    handleStepNavigation: (targetStep: number) => void;
    targetCategories: ICategoryTarget[];
    selectedTargetCategories: ICategoryTarget[];
    fetchClusterTargets: (clusterId: string) => Promise<void>;
    loadingClustersTargets: Record<string, boolean>;
    handleAddAssets: (clusterId: string, collectionId: string) => void;
    modalType: BCC_MODAL_TYPES;
    handleBatchKeywordFieldUpdates: (
        updates: Array<{
            clusterId: string;
            collectionId: string;
            field: string;
            value: any;
            record: any;
        }>
    ) => void;
    handleBatchCategoryFieldUpdates: (
        updates: Array<{
            clusterId: string;
            collectionId: string;
            field: string;
            value: any;
            record: any;
        }>
    ) => void;
    setModalType: (type: BCC_MODAL_TYPES) => void;
    assets: IBCCAsset[];
}
export interface INegativeKeywordTarget {
    keyword: string;
    keyword_type: string;
}
export interface ICategoryTarget {
    category_id: number;
    category_name: string;
    min_bid_limit: number;
    max_bid_limit: number;
    exact_suggested_min: number;
    exact_suggested_max: number;
    search_vol: number;
    cpm_bid?: number;
    clusterId?: string;
    id?: string;
    key?: string;
}

export enum BCC_CAMPAIGN_TYPES {
    MULTIPLE = "multiple",
    SINGLE = "single",
}

export enum BCC_MODULE_TYPES {
    CLUSTER = "cluster",
    KEYWORDS = "keywords",
}

export enum KWD_BID_TYPES {
    EXACT_MATCH_BID = "exact_match_bid",
    CPM_BID = "cpm_bid",
    SMART_MATCH_BID = "smart_match_bid",
    BID_BOOSTER = "bid_booster",
}

export enum CLUSTER_ACCORDION_TYPES {
    CLUSTER = "cluster",
    PRODUCT = "product",
}

export enum BCC_KEYWORD_TYPES {
    NEGATIVE = "negative",
    CUSTOM = "custom",
}

export enum BCC_TARGET_TYPES {
    KEYWORDS = "keywords",
    NEGATIVE_KEYWORDS = "negative_keywords",
    CATEGORIES = "categories",
    KEYWORD_COHORTS = "keywordCohorts",
}

export interface IBCCAddTargetsModalTableConfig {
    colDefs: Array<Record<string, any>>;
    isGroupedTable?: boolean;
    isSearchReq?: boolean;
    isDrilldownTable?: boolean;
    isCompetitionButtonReq?: boolean;
    isLayoutButtonReq?: boolean;
    isDownloadButtonReq?: boolean;
    isExpandable?: boolean;
    isCheckboxSelectionRequired?: boolean;
    style?: {
        height?: number | string;
        expandedHeight?: string;
    };
    additionalFilters?: Array<{
        section?: string;
        key: string;
        type: string;
        props: Record<string, any>;
    }>;
}
export enum BCC_BID_SUGGESTION_TYPES {
    EXACT_SUGGESTED_MIN = "exact_suggested_min",
    EXACT_SUGGESTED_MAX = "exact_suggested_max",
    MIN_BID_LIMIT = "min_bid_limit",
}

export interface VariantsCellRendererRecord {
    id?: string;
    clusterId?: string;
    skuDetails?: IBCCProductSkuDetail[];
    disabled?: boolean;
    [key: string]: unknown;
}

export interface VariantsCellRendererProps {
    value?: string[];
    record?: VariantsCellRendererRecord;
}

export interface IRevstackLayoutFilters {
    priceType: {
        selected: string;
    };
    metricType: {
        selected: string;
        options?: Array<TOptionsString & { options?: TOptionsString[] }>;
    };
}

export interface IRevstackPopupData {
    drilldown: {
        title: string;
        show: boolean;
        activeSectionId: RevstackCASectionIds | null;
        module: RevstackDashboardModules | null;
    };
    trends: {
        show: boolean;
        activeSectionId: RevstackCASectionIds | null;
        module: RevstackDashboardModules | null;
        defaultTab: CA_DD_MODULES | null;
        hideTrendsTabSection: boolean;
        titlePills: string[];
    };
    changeSku: {
        show: boolean;
        selectedChangeSku?: number | null;
    };
}

export interface IBCCAsset {
    name: string;
    desc: string;
    cpm_bid?: number | null;
    exact_suggested_min?: number;
    exact_suggested_max?: number;
}

export interface IRevstackWidgetData {
    dashboard: Record<string, any>;
    drilldown: Record<string, any>;
    drilldownTrends: Record<string, any>;
    changeSku: Record<string, any>;
}

export interface IRevstackActiveTab {
    value: RevstackDashboardSubModules | CA_DD_MODULES;
}

export enum REVSTACK_FILTER_MARKERS {
    ALL_UNIVERSAL = "ALL_UNIVERSAL",
    ALL_COMPETITION = "ALL_COMPETITION",
    ALL_CATEGORY_PREFIX = "ALL_CATEGORY_",
}

export enum REVSTACK_BRAND_TYPES {
    MY_BRAND = "mybrand",
    COMPETITION = "competition",
}

export interface IRevstackRelativeFilters {
    skus: string[];
    regional: string[];
}

export interface IRelativeFiltersState {
    relativeFilters: {
        value: IRevstackRelativeFilters;
    };
}

export interface IRevstackDashboardTab {
    key: RevstackDashboardSubModules | CA_DD_MODULES;
    label: string | React.ReactNode;
}

export enum REVSTACK_INFO_NOTES {
    SKU_SELECTION_LIST = "Metrics shown are calculated across your selected Category X Subcategory filters. Individual SKU pages display data within the SKU's native Subcategory",
}
export enum CAMPAIGN_SYNC_STATE {
    PENDING = "PENDING",
    SYNCED = "SYNCED",
    FAILED = "FAILED",
}

export enum CAMPAIGN_ORIGIN {
    REVINITI = "reviniti",
    EXTERNAL = "external",
}

// BCC City Selection Enums
export enum BCCSelectionType {
    CITY = "city",
    REGION = "region",
    TIER = "tier",
    ZONE = "zone",
    COHORT = "cohort",
}

export enum BCCSelectionLevel {
    CITY = "city",
    REGION = "region",
    TIER = "tier",
    CUSTOM = "custom",
}

export enum BCCSelectionMode {
    INDIVIDUAL = "individual",
    COHORT = "cohort",
}

export interface BCCCitySelection {
    id: number;
    type: BCCSelectionType;
    cohortLevel?: BCCSelectionLevel;
    items: string[];
    originalItems?: string[];
    label: string;
}

export interface IFilterHashPayload {
    brands: number[];
    categories: number[];
    subcategories: number[];
    dateRange?: {
        current_period: string[];
        previous_period: string[];
        selectedRange?: string;
    };
    client?: number;
    channel?: number[];
    skus?: string[];
    advancedFilters?: Record<string, any>;
    priceType?: string;
    metricType?: string;
}

export interface IFilterHashRequest {
    hash: string;
    platform: string;
    module: string;
    filters: IFilterHashPayload;
}

export interface IFilterHashResponse {
    status: boolean;
    hash?: string;
    filters?: IFilterHashPayload;
    message?: string;
}

export interface IFiltersFromUrlState {
    filtersFromUrl: {
        value: IFilterHashPayload | null;
        isLoading: boolean;
        error: string | null;
    };
}

export type TValidationResult<T> = {
    valid: T[];
    invalid: T[];
};

export interface IMainFiltersValidationResult {
    categories: TValidationResult<number>;
    subcategories: TValidationResult<number>;
    brands: TValidationResult<number>;
}

export interface IChannelValidationResult {
    channels: TValidationResult<number>;
}

export interface ILayoutFiltersValidationResult {
    priceType?: string | null;
    metricType?: string | null;
}

export interface IMainFilters {
    categories: number[];
    subcategories: number[];
    brands: number[];
    skus: number[];
    regional: string[];
    channels: number[];
    rerender?: boolean;
}

export interface IMainFiltersData {
    categories: IRevstackFilterOption[];
    subcategories: IRevstackFilterOption[];
    brands: IRevstackFilterOption[];
    selected?: {
        categories: number[];
        subcategories: number[];
        brands: number[];
        skus: string[];
        regional: string[];
    };
}

export interface IAdditionalFilterData {
    dateRange?: TSelectedDateRange;
    client?: number;
    channel?: number[];
    skus?: string[];
    advancedFilters?: Record<string, number[]>;
    priceType?: string;
    metricType?: string;
}

export interface IOverrideFilterData {
    mainFilters?: IMainFilters;
    dateRange?: TSelectedDateRange;
    channel?: number[];
    client?: number;
    skus?: string[];
    advancedFilters?: Record<string, unknown>;
    priceType?: string;
    metricType?: string;
    resetFilters?: boolean;
    [key: string]: unknown;
}

export interface IQueryParams {
    dateRange?: TSelectedDateRange;
    channel?: number[] | number;
    client?: number;
    sku?: string;
    revstackLayoutFilters?: {
        priceType?: {
            selected?: string;
        };
        metricType?: {
            selected?: string;
        };
    };
    [key: string]: string | string[] | number | number[] | object | undefined;
}

export interface IOpenPageInNewTabParams {
    platform: string;
    module: string;
    type?: string;
    queryParams: IQueryParams;
    mainFilters: Record<string, string[]>;
}

export interface IGetFilterValueOverrideData {
    resetFilters?: boolean;
    [key: string]: unknown;
}

export interface IValidateLayoutFiltersState {
    revstackLayoutFilters: IRevstackLayoutFilters;
}

export interface IApplyHashFiltersToStateCurrentState {
    revstackLayoutFilters: IRevstackLayoutFilters;
    selectedDateRange: TSelectedDateRange;
}

export interface IMainFiltersValue {
    categories: number[];
    subcategories: number[];
    brands: number[];
    skus?: number[];
    regional?: number[];
    channels?: number[];
    rerender: boolean;
}

export interface IRevstackLayoutFiltersValue {
    priceType: {
        selected: string;
    };
    metricType: {
        selected: string;
        options: Array<{
            value: string;
            label: string;
            options: Array<{ value: string; label: string }>;
        }>;
    };
}

export interface IRevstackChannel {
    channel_id: number;
    channel_name: string;
}

export interface ITagTypeOption {
    label: string;
    value: number;
    options: Array<{ label: string; value: string }>;
}

export interface IAdditionalFiltersValue {
    tagTypes: ITagTypeOption[];
    state: IAdditionalFiltersState;
}

export interface IAdditionalFiltersState {
    isOpen: boolean;
    appliedFilters: Record<number, string[]>;
    draftFilters: Record<number, string[]>;
    activeIndex: number;
    searchText: string;
}
export interface ICubeQuery {
    measures?: string[];
    dimensions?: string[];
    filters?: Record<string, unknown>[];
    [key: string]: unknown;
}

export interface IDashboardWidgetConfig {
    cubeQueries: ICubeQuery[];
    [key: string]: unknown;
}

export interface IRevstackFiltersLayoutProps extends LayoutProps {
    showCategoryFilters?: boolean;
}

export interface IFiltersLayoutRootState {
    activeClientId: { value: number };
    revstackChannel: { value: number[]; updateId?: number };
    mainFilters: { value: IMainFiltersValue };
    revstackLayoutFilters: { value: IRevstackLayoutFiltersValue };
    selectedDateRange: { value: TSelectedDateRange };
    revstackChannelsList: { value: IRevstackChannel[] };
    tagTypeListLoading: { value: boolean };
    additionalFilters?: { value: IAdditionalFiltersValue };
}

export interface IAdvancedFiltersParams {
    categoryIds: number[];
    subcategoryIds: number[];
    brandIds: number[];
}

export interface IDrilldownFiltersValue {
    options: {
        category: IRevstackFilterOption[];
        subcategory: IRevstackFilterOption[];
        regional: IRevstackFilterOption[];
    };
    selected: {
        category: string[];
        subcategory?: string[];
        secondary_region: string[];
    };
}

export interface ITrendsFiltersValue {
    options: {
        category: IRevstackFilterOption[];
        subcategory: IRevstackFilterOption[];
        secondary_region: IRevstackFilterOption[];
        channel: IRevstackFilterOption[];
    };
    selected: {
        category: string[];
        subcategory: string[];
        secondary_region: string[];
        channel: string[];
    };
}

export interface ISkuMainFiltersValue {
    selected: {
        category: number[] | null;
        subcategory: number[] | null;
        brand: number[];
        cross_channel_sku_id: number[] | null;
    };
}

export interface IDashboardConfigRootState {
    revstackLayoutFilters: { value: IRevstackLayoutFilters };
    selectedDateRange: { value: TSelectedDateRange };
    revstackChannel: { value: number[] };
    revstackChannelsList: { value: IRevstackChannel[] };
    mainFilters: { value: IMainFiltersValue };
    drilldownFilters: { value: IDrilldownFiltersValue };
    trendsFilters: { value: ITrendsFiltersValue };
    skuMainFilters: { value: ISkuMainFiltersValue };
    additionalFilters: { value: IAdditionalFiltersValue };
    clientSubGroupId: { value: number | null };
    revstackActiveTab: { value: RevstackDashboardSubModules };
    channelCurrencySymbol: { value: string };
    relativeFilters: { value: IRevstackRelativeFilters };
}
export interface ICitySelectionDropdownProps {
    value: string[];
    record: ICollection;
    citiesList: IBCCCityList[];
    handleFieldChange: (
        collectionId: string,
        field: string,
        value: any,
        record: ICollection
    ) => void;
}

export interface IBCCSelectionTagProps {
    selection: BCCCitySelection;
    closable?: boolean;
    onRemove: (id: number) => void;
    citiesList: IBCCCityList[];
}

export interface IBCCSelectableItemProps {
    id: string;
    label: string;
    selected: boolean;
    disabled: boolean;
    disabledReason?: string;
    onToggle: () => void;
}

export interface IBCCSelectAllItemProps {
    count: number;
    allSelected: boolean;
    someSelected: boolean;
    onToggle: () => void;
    className?: string;
}

export interface IBCCZoneItemProps {
    zoneName: string;
    selected: boolean;
    disabled: boolean;
    onToggle: (e: React.MouseEvent) => void;
    onExpand: () => void;
}

export interface IBCCBreadcrumbProps {
    currentZone: string;
    onBack: () => void;
}

export interface IBCCSearchBarProps {
    placeholder: string;
    value: string;
    onChange: (value: string) => void;
}

export interface IBCCLevelSelectorProps {
    currentLevel: BCCSelectionLevel;
    onLevelChange: (level: BCCSelectionLevel) => void;
}

export interface IBCCModeToggleProps {
    currentMode: BCCSelectionMode;
    onModeChange: (mode: BCCSelectionMode) => void;
}

export interface IBCCCohortLevelSelectorProps {
    selectedLevel: BCCSelectionLevel;
    onLevelChange: (level: BCCSelectionLevel) => void;
}

export interface IBCCCohortPreviewProps {
    items: string[];
    selectedLevel: BCCSelectionLevel;
    citiesList: IBCCCityList[];
    onRemove: (item: string) => void;
    getCohortItemLabel: (itemId: string) => string;
}

export interface IBCCFooterProps {
    selections: BCCCitySelection[];
    onClearAll: () => void;
    onApply: () => void;
    renderTag: (
        selection: BCCCitySelection,
        closable?: boolean
    ) => React.ReactNode;
}

export interface IBCCModalUIConfig {
    showHeader: boolean;
    showHeaderInput: boolean;
    showTabBar: boolean;
    showCustomKeywordButton: boolean;
    showFooterSelectedCount: boolean;
    showCategoriesCount?: boolean;
    modalWidth?: number;
    modalHeight?: number;
    contentHeight?: number;
    tableHeaderJustifyContent?: "justify-content-start" | "justify-content-end";
}

export interface IBCCModalHeaderConfig {
    title: string | React.ReactNode;
    icon?: React.ReactNode;
    iconStyles?: {
        containerWidth?: string;
        containerHeight?: string;
        containerBgColor?: string;
        iconSize?: string;
        iconColor?: string;
    };
    showCheckboxFilters?: boolean;
    checkboxOptions?: Array<{
        value: string;
        label: string;
    }>;
}

export interface IBCCModalTabConfig {
    key: string;
    label: string;
    icon?: React.ReactNode;
    content: {
        type: "table" | "custom";
        tableConfig?: any;
        dataSource?: any;
        selectedRows?: any[];
        isLoading?: boolean;
        customComponent?: React.ReactNode;
    };
    extraContent?: React.ReactNode;
    isVisible?: boolean | (() => boolean);
    handlers?: {
        onTableSelection?: (...args: any[]) => void;
        onFieldChange?: (...args: any[]) => void;
        onCustomAction?: (...args: any[]) => void;
    };
}

export interface IBCCModalTableConfig {
    primary: IBCCAddTargetsModalTableConfig;
    secondary?: IBCCAddTargetsModalTableConfig;
    dataSource: {
        key: string;
        transformer?: (data: any) => any;
    };
    filters?: {
        enabled: boolean;
        categoryFilter?: boolean;
        subcategoryFilter?: boolean;
        customFilters?: Array<any>;
    };
    sorter?: {
        enabled: boolean;
        customSorter?: (data: any, originalData: any, sortConfig: any) => any;
    };
}

export interface IBCCModalHandlers {
    onTableSelection?: (selectedRows: any[]) => void;
    onRowSelect?: (
        record: any,
        selected: boolean,
        allSelectedRows: any[]
    ) => void;
    onNegativeKeywordSelection?: (selectedRows: any[]) => void;
    onCategorySelection?: (selectedRows: any[]) => void;
    onSave: () => void | Promise<void>;
    onCancel: () => void;
    onFieldChange?: (
        clusterId: string,
        collectionId: string,
        field: string,
        value: any,
        record: any
    ) => void;
    onAddCustomKeyword?: (keyword: any) => void;
    onModalClose?: () => void;
}

export interface IBCCModalValidation {
    saveDisabled: boolean | (() => boolean);
    customValidation?: (state: any) => boolean;
}

export interface IBCCModalFooterConfig {
    showSelectedCount: boolean;
    selectedCountLabel?: string | ((count: number) => string);
    saveButtonText: string;
    saveButtonLoading?: boolean;
    customFooterContent?: React.ReactNode;
}

export interface IBCCModalLoading {
    isLoading: boolean;
    isSaving: boolean;
    loadingText?: string;
}

export interface IBCCModalMetadata {
    modalParams?: any;
    selectedRows?: any[];
    contextData?: any;
}

export interface IBCCModalConfig {
    modalId: string;
    moduleType: BCC_MODULE_TYPES;
    ui: IBCCModalUIConfig;
    header?: IBCCModalHeaderConfig;
    tabs?: IBCCModalTabConfig[];
    tableConfig: IBCCModalTableConfig;
    handlers: IBCCModalHandlers;
    validation?: IBCCModalValidation;
    footer: IBCCModalFooterConfig;
    loading?: IBCCModalLoading;
    metadata?: IBCCModalMetadata;
}

export enum KEYWORD_MODAL_ACTIVE_TABS {
    SUGGESTED_KEYWORDS = "suggested-keywords",
    NEGATIVE_KEYWORDS = "negative-keywords",
    CATEGORIES = "categories",
}

export enum BCC_CAMPAIGN_TYPES {
    PRODUCT_BOOSTER = "Product Booster",
    RECOMMENDATION_ADS = "Recommendation Ads",
}

export enum BCC_MODAL_TYPES {
    CLUSTER = "cluster",
    KEYWORDS = "keywords",
    ASSETS = "assets",
}

export enum BCC_CSV_UPLOAD_COLUMNS {
    KEYWORD = "keyword",
    TYPE = "type",
    BID = "bid",
}

export enum BCC_CITY_DISABLED_REASONS {
    IN_COHORT = "In cohort",
    IN_ZONE = "In zone",
}

export interface IBCCCollectionState {
    overridesByCollection: Record<string, Record<string, Record<string, any>>>;
    selectedKeywordIdsByCollection: Record<string, Record<string, string[]>>;
    negativeKeywordsByCollection: Record<
        string,
        Record<string, INegativeKeywordTarget[]>
    >;
    categoriesOverridesByCollection: Record<
        string,
        Record<string, Record<string, Partial<ICategoryTarget>>>
    >;
    selectedCategoryIdsByCollection: Record<string, Record<string, string[]>>;
}

export type IBCCCollectionAction =
    | {
          type: "CLEAR_COLLECTION";
          payload: { clusterId: string; collectionId: string };
      }
    | {
          type: "SET_OVERRIDES";
          payload: {
              clusterId: string;
              collectionId: string;
              overrides: Record<string, any>;
          };
      }
    | {
          type: "SET_SELECTED_KEYWORD_IDS";
          payload: { clusterId: string; collectionId: string; ids: string[] };
      }
    | {
          type: "SET_NEGATIVE_KEYWORDS";
          payload: {
              clusterId: string;
              collectionId: string;
              keywords: INegativeKeywordTarget[];
          };
      }
    | {
          type: "SET_CATEGORIES_OVERRIDES";
          payload: {
              clusterId: string;
              collectionId: string;
              overrides: Record<string, Partial<ICategoryTarget>>;
          };
      }
    | {
          type: "SET_SELECTED_CATEGORY_IDS";
          payload: { clusterId: string; collectionId: string; ids: string[] };
      };

// Mapping refresh widget
export enum MappingSyncStatus {
    Success = "success",
    Syncing = "syncing",
    Error = "error",
}

export interface MappingRefreshWidgetProps {
    status?: MappingSyncStatus;
    statusText?: string;
    lastSyncText?: string;
    buttonText?: string;
    refreshing?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

export interface ICompetitionDataSourceItem {
    brand_id: number | string;
    [key: string]: any;
}

export type TSetHideCompetition = React.Dispatch<React.SetStateAction<boolean>>;
